// ============================================================================
// SS1 BIOLOGY — COMPLETE LEARNING MODULE (24 TOPICS)
// NERDC Scheme of Work — First Term to Third Term
// ============================================================================

export const WAEC_BIOLOGY_LEARN_SS1 = [
  {
    "topic": "Recognizing Living Things",
    "topicCode": "SS1-BIO-01",
    "module": "Nature of Biology",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Biology</span> is the scientific study of life. To study life, we must first understand what it means to be <span class=\"learn-keyword\">alive</span>. Living things — also called <span class=\"learn-keyword\">organisms</span> — share a set of characteristics that distinguish them from non-living things. Recognizing these characteristics is the foundation of all biological study.\n</div>\n\n<h3 class=\"learn-subheading\">1. Characteristics of Living Things (MRGREN)</h3>\n<p class=\"learn-p\">The acronym <strong>MRGREN</strong> summarizes the key life processes:</p>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Letter</th><th>Process</th><th>Meaning</th></tr></thead>\n    <tbody>\n      <tr><td>M</td><td>Movement</td><td>Ability to move whole body or parts</td></tr>\n      <tr><td>R</td><td>Respiration</td><td>Release of energy from food</td></tr>\n      <tr><td>G</td><td>Growth</td><td>Permanent increase in size/mass</td></tr>\n      <tr><td>R</td><td>Reproduction</td><td>Producing offspring of same kind</td></tr>\n      <tr><td>E</td><td>Excretion</td><td>Removal of metabolic waste products</td></tr>\n      <tr><td>N</td><td>Nutrition</td><td>Obtaining and using food/energy</td></tr>\n      <tr><td>+ S</td><td>Sensitivity</td><td>Detecting and responding to stimuli</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Non-Living Things</h3>\n<p class=\"learn-p\">Non-living things do not carry out life processes. They may show one or two features that resemble life (e.g., fire grows and uses oxygen, a car moves) but they do <strong>not</strong> possess all the characteristics simultaneously. The key distinction is that living organisms carry out <strong>all</strong> life processes and are made of cells.</p>\n\n<h3 class=\"learn-subheading\">3. Differences Between Living and Non-Living Things</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Feature</th><th>Living Things</th><th>Non-Living Things</th></tr></thead>\n    <tbody>\n      <tr><td>Cellular structure</td><td>Made of one or more cells</td><td>Not made of cells</td></tr>\n      <tr><td>Metabolism</td><td>Chemical reactions occur inside</td><td>No metabolic reactions</td></tr>\n      <tr><td>Growth</td><td>Grow from within (intrinsic)</td><td>May increase by accumulation</td></tr>\n      <tr><td>Reproduction</td><td>Can produce own kind</td><td>Cannot reproduce</td></tr>\n      <tr><td>Response</td><td>Respond to stimuli</td><td>Do not respond to stimuli</td></tr>\n      <tr><td>Nutrition</td><td>Need and process food</td><td>Do not need food</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">4. Special Cases: Viruses</h3>\n<p class=\"learn-p\">Viruses are a special category — they show some features of living things (reproduction inside host cells, genetic material) but lack others (no cellular structure, no independent metabolism). They are therefore considered on the <strong>borderline between living and non-living</strong>.</p>\n\n<div class=\"learn-svg-wrap\">\n  <svg class=\"learn-svg\" viewBox=\"0 0 480 200\" xmlns=\"http://www.w3.org/2000/svg\">\n    <rect width=\"480\" height=\"200\" fill=\"#0f0020\" rx=\"10\"/>\n    <text x=\"240\" y=\"22\" text-anchor=\"middle\" fill=\"#FFC53D\" font-size=\"12\" font-weight=\"bold\">Characteristics of Living Things</text>\n    <circle cx=\"240\" cy=\"110\" r=\"75\" fill=\"none\" stroke=\"#28c840\" stroke-width=\"1.5\"/>\n    <text x=\"240\" y=\"55\" text-anchor=\"middle\" fill=\"#28c840\" font-size=\"10\" font-weight=\"bold\">LIVING ORGANISM</text>\n    <text x=\"240\" y=\"75\" text-anchor=\"middle\" fill=\"#c8c8c8\" font-size=\"9\">Movement · Respiration</text>\n    <text x=\"240\" y=\"90\" text-anchor=\"middle\" fill=\"#c8c8c8\" font-size=\"9\">Growth · Reproduction</text>\n    <text x=\"240\" y=\"105\" text-anchor=\"middle\" fill=\"#c8c8c8\" font-size=\"9\">Excretion · Nutrition</text>\n    <text x=\"240\" y=\"120\" text-anchor=\"middle\" fill=\"#c8b4f0\" font-size=\"9\">Sensitivity · Made of cells</text>\n    <rect x=\"10\" y=\"150\" width=\"130\" height=\"35\" rx=\"6\" fill=\"#1B0A47\" stroke=\"#FFC53D\" stroke-width=\"1\"/>\n    <text x=\"75\" y=\"165\" text-anchor=\"middle\" fill=\"#FFC53D\" font-size=\"9\">Non-Living: fire</text>\n    <text x=\"75\" y=\"178\" text-anchor=\"middle\" fill=\"#c8c8c8\" font-size=\"8\">moves but not alive</text>\n    <rect x=\"340\" y=\"150\" width=\"130\" height=\"35\" rx=\"6\" fill=\"#1B0A47\" stroke=\"#c8b4f0\" stroke-width=\"1\"/>\n    <text x=\"405\" y=\"165\" text-anchor=\"middle\" fill=\"#c8b4f0\" font-size=\"9\">Virus: borderline</text>\n    <text x=\"405\" y=\"178\" text-anchor=\"middle\" fill=\"#c8c8c8\" font-size=\"8\">not truly living/non-living</text>\n  </svg>\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Examiners often ask about viruses or fire as tricky cases. Remember: viruses are not cells, cannot grow or metabolize independently, so they are NOT classified as living. Fire uses oxygen and grows but does not reproduce its own kind or have cells.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Living things are distinguished by seven characteristics: movement, respiration, growth, reproduction, excretion, nutrition, and sensitivity (MR GRENS). They are all made of cells and carry out metabolism. Non-living things may show one or two of these features but never all. Viruses are borderline cases.\n</div>\n  ",
    "questions": [
      {
        "q": "Which of the following is NOT a characteristic of living things?",
        "o": ["Respiration", "Growth", "Combustion", "Excretion"],
        "a": 2,
        "e": "Combustion (burning) is a chemical process that occurs in non-living things like fire. All living things carry out respiration, growth, and excretion as life processes.",
        "h": "Living things carry out MR GRENS — combustion is not one of them.",
        "yr": "SS1"
      },
      {
        "q": "The acronym MRGREN stands for life processes. What does 'E' represent?",
        "o": ["Evolution", "Excretion", "Elongation", "Effusion"],
        "a": 1,
        "e": "In MRGREN, E stands for Excretion — the removal of metabolic waste products from the body of a living organism.",
        "h": "MRGREN: Movement, Respiration, Growth, Reproduction, Excretion, Nutrition.",
        "yr": "SS1"
      },
      {
        "q": "Why are viruses described as being on the borderline between living and non-living things?",
        "o": [
          "They have a cell wall",
          "They can photosynthesize",
          "They lack cellular structure but can reproduce inside host cells",
          "They perform all seven life processes"
        ],
        "a": 2,
        "e": "Viruses have genetic material and can replicate inside host cells (living feature) but lack a cellular structure and cannot carry out independent metabolism (non-living features). This places them on the borderline.",
        "h": "Viruses lack cells and independent metabolism but can reproduce in hosts.",
        "yr": "SS1"
      },
      {
        "q": "A stone increases in size when more stones are piled on it. This is an example of:",
        "o": ["Biological growth", "Accretion (addition from outside)", "Respiration", "Reproduction"],
        "a": 1,
        "e": "The increase in size of a stone by adding more material is accretion — growth from outside. Living organisms grow intrinsically (from within) through cell division and enlargement.",
        "h": "Living things grow from within; non-living things grow by accumulation from outside.",
        "yr": "SS1"
      },
      {
        "q": "Which statement correctly distinguishes excretion from egestion?",
        "o": [
          "Excretion removes undigested food; egestion removes metabolic waste",
          "Excretion removes metabolic waste; egestion removes undigested food",
          "Both terms mean the same thing",
          "Excretion only occurs in animals"
        ],
        "a": 1,
        "e": "Excretion is the removal of waste products of metabolism (e.g., CO₂, urea). Egestion is the removal of undigested food (faeces). Both are different life processes.",
        "h": "Excretion = metabolic waste removal; egestion = removal of undigested food.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following correctly shows a feature shared by ALL living things?",
        "o": ["Presence of chlorophyll", "Made of cells", "Ability to move from place to place", "Presence of a backbone"],
        "a": 1,
        "e": "All living organisms are made of one or more cells — this is the fundamental unit of life. Not all organisms have chlorophyll (e.g., animals), move from place to place (e.g., plants), or have a backbone (most animals don't).",
        "h": "The cell theory: all living things are made of cells.",
        "yr": "SS1"
      },
      {
        "q": "Sensitivity in living organisms refers to:",
        "o": [
          "The ability to feel pain only",
          "Detection and response to changes in the environment (stimuli)",
          "The process of releasing energy from food",
          "The removal of waste products"
        ],
        "a": 1,
        "e": "Sensitivity (also called irritability) is the ability to detect changes in the environment (stimuli) and respond to them. This applies to all living things, not just animals.",
        "h": "Sensitivity = detecting stimuli and responding appropriately.",
        "yr": "SS1"
      },
      {
        "q": "Fire burns wood and grows in size, uses oxygen, and produces CO₂. Why is fire NOT classified as living?",
        "o": [
          "It does not use oxygen",
          "It cannot reproduce its own kind, lacks cells, and has no metabolism",
          "It does not produce CO₂",
          "It cannot move"
        ],
        "a": 1,
        "e": "Although fire consumes oxygen and grows, it cannot reproduce its own kind, is not made of cells, and does not carry out biological metabolism. It lacks the full suite of life characteristics.",
        "h": "Fire lacks cells, reproduction of its own kind, and metabolism.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is an example of movement in plants?",
        "o": ["Locomotion", "Phototropism (bending toward light)", "Running", "Swimming"],
        "a": 1,
        "e": "Plants cannot move from place to place (no locomotion) but they do show movement — e.g., phototropism where shoots bend toward a light source. This is movement of parts, not the whole organism.",
        "h": "Plants show movement of parts, not locomotion of the whole organism.",
        "yr": "SS1"
      },
      {
        "q": "Which life process involves the chemical breakdown of food to release energy?",
        "o": ["Nutrition", "Growth", "Respiration", "Reproduction"],
        "a": 2,
        "e": "Respiration is the chemical process by which food (glucose) is broken down to release energy (ATP) inside cells. It occurs in all living organisms and involves chemical reactions in the cytoplasm and mitochondria.",
        "h": "Respiration = chemical release of energy from food in cells.",
        "yr": "SS1"
      },
      {
        "q": "A seed germinates and grows into a plant. This is an example of which life process?",
        "o": ["Excretion", "Growth", "Respiration", "Sensitivity"],
        "a": 1,
        "e": "Germination and development of a seed into a mature plant is growth — a permanent increase in size and complexity due to cell division and enlargement.",
        "h": "Growth is a permanent irreversible increase in size and complexity.",
        "yr": "SS1"
      },
      {
        "q": "Which characteristic do all living organisms share with viruses?",
        "o": ["Cellular structure", "Carrying out metabolism", "Possessing genetic material (DNA or RNA)", "Carrying out respiration"],
        "a": 2,
        "e": "All living organisms and viruses possess genetic material (DNA or RNA). However, viruses differ in that they lack cellular structure and independent metabolism, which is why they are not classified as fully living.",
        "h": "Viruses and living things share genetic material but viruses lack cells and metabolism.",
        "yr": "SS1"
      },
      {
        "q": "The production of urea by the liver and its removal in urine is an example of:",
        "o": ["Nutrition", "Growth", "Excretion", "Egestion"],
        "a": 2,
        "e": "Urea is a metabolic waste product produced from the breakdown of amino acids (deamination) in the liver. Its removal from the body in urine is excretion — elimination of metabolic wastes.",
        "h": "Urea is a metabolic waste → its removal is excretion, not egestion.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following correctly pairs a non-living thing with a life characteristic it appears to show?",
        "o": [
          "Rock — respiration",
          "River — movement",
          "Sand — reproduction",
          "Soil — nutrition"
        ],
        "a": 1,
        "e": "A river appears to show movement (water flowing), which resembles locomotion. However, it lacks all other life processes. A rock, sand, and soil do not clearly exhibit movement, respiration, reproduction, or nutrition.",
        "h": "A river moves — but movement alone does not make it alive.",
        "yr": "SS1"
      },
      {
        "q": "Reproduction ensures:",
        "o": [
          "Energy release in organisms",
          "Removal of waste products",
          "Continuity of a species",
          "Growth of individual organisms"
        ],
        "a": 2,
        "e": "Reproduction is the production of new individuals of the same species, ensuring the continuation of that species. Without reproduction, species would become extinct.",
        "h": "Reproduction → continuity and survival of the species.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is an autotrophic (self-feeding) organism?",
        "o": ["Mushroom", "Tapeworm", "Green plant", "Human being"],
        "a": 2,
        "e": "Green plants are autotrophs — they manufacture their own food through photosynthesis using sunlight, CO₂, and water. Mushrooms are saprophytes, tapeworms are parasites, and humans are heterotrophs.",
        "h": "Autotrophs make their own food; green plants photosynthesize.",
        "yr": "SS1"
      },
      {
        "q": "The permanent increase in the dry mass of an organism is the best definition of:",
        "o": ["Nutrition", "Growth", "Respiration", "Sensitivity"],
        "a": 1,
        "e": "Growth is best defined as a permanent, irreversible increase in the dry mass (or size and complexity) of an organism. Using dry mass (not wet mass) avoids confusion with temporary water uptake.",
        "h": "Growth = permanent increase in dry mass through cell division and enlargement.",
        "yr": "SS1"
      },
      {
        "q": "Carbon dioxide exhaled during breathing is a product of:",
        "o": [
          "Excretion only",
          "Respiration (and is also excreted)",
          "Nutrition",
          "Growth"
        ],
        "a": 1,
        "e": "CO₂ is a waste product of cellular respiration (aerobic). It is produced in cells, transported in the blood, and expelled through the lungs. Because it is a metabolic waste, its removal is also classified as excretion.",
        "h": "CO₂ is a by-product of respiration and a metabolic waste excreted via the lungs.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following activities by a plant is an example of sensitivity?",
        "o": [
          "Absorbing water through roots",
          "A shoot bending toward a light source (phototropism)",
          "Producing seeds",
          "Releasing oxygen during photosynthesis"
        ],
        "a": 1,
        "e": "Phototropism — the bending of a plant shoot toward light — is a response to an environmental stimulus (light). This is sensitivity (irritability) in plants.",
        "h": "Phototropism is the plant's response to light stimulus — sensitivity.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following best explains why a car is NOT a living thing?",
        "o": [
          "It moves and uses fuel",
          "It cannot grow from within, reproduce, or carry out metabolism",
          "It is too large to be a living thing",
          "It does not need water"
        ],
        "a": 1,
        "e": "A car moves and uses fuel (resembling nutrition/respiration) but it cannot grow intrinsically, reproduce its own kind, or carry out cellular metabolism. It lacks cells and all the life characteristics together.",
        "h": "A car lacks intrinsic growth, reproduction, cells, and metabolism.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Classification of Living Things",
    "topicCode": "SS1-BIO-02",
    "module": "Nature of Biology",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Classification</span> (taxonomy) is the systematic grouping of organisms based on their similarities and differences. It helps biologists organize the enormous diversity of life — estimated at over 8 million species — into a logical, manageable system. The science of classification is called <span class=\"learn-keyword\">taxonomy</span>, and its pioneer was <span class=\"learn-keyword\">Carl Linnaeus</span> (1707–1778).\n</div>\n\n<h3 class=\"learn-subheading\">1. Importance of Classification</h3>\n<ul class=\"learn-list\">\n  <li>Makes the study of the enormous diversity of life manageable and systematic</li>\n  <li>Enables scientists worldwide to communicate using the same names for organisms</li>\n  <li>Reveals evolutionary relationships between organisms</li>\n  <li>Helps in identifying and naming new species</li>\n  <li>Assists in agriculture, medicine, and conservation by grouping organisms with similar properties</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Levels (Hierarchy) of Classification</h3>\n<p class=\"learn-p\">Organisms are arranged in a hierarchy from the most general to the most specific. The seven main levels are:</p>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Level (Taxon)</th><th>Description</th><th>Example (Human)</th></tr></thead>\n    <tbody>\n      <tr><td>Kingdom</td><td>Broadest grouping</td><td>Animalia</td></tr>\n      <tr><td>Phylum</td><td>Major body plan grouping</td><td>Chordata</td></tr>\n      <tr><td>Class</td><td>More specific grouping</td><td>Mammalia</td></tr>\n      <tr><td>Order</td><td>Related families</td><td>Primates</td></tr>\n      <tr><td>Family</td><td>Closely related genera</td><td>Hominidae</td></tr>\n      <tr><td>Genus</td><td>Very closely related species</td><td>Homo</td></tr>\n      <tr><td>Species</td><td>Most specific; organisms can interbreed</td><td>sapiens</td></tr>\n    </tbody>\n  </table>\n</div>\n<p class=\"learn-p\"><strong>Mnemonic:</strong> <em>King Philip Came Over For Good Soup</em> (Kingdom → Phylum → Class → Order → Family → Genus → Species)</p>\n\n<h3 class=\"learn-subheading\">3. Binomial Nomenclature</h3>\n<p class=\"learn-p\">Linnaeus introduced the <span class=\"learn-keyword\">binomial nomenclature</span> system — giving each organism a two-part scientific name consisting of <strong>Genus</strong> and <strong>species</strong>. Rules:</p>\n<ul class=\"learn-list\">\n  <li>Written in Latin or Latinized form</li>\n  <li>Genus name starts with a capital letter; species name is lowercase</li>\n  <li>Both are italicized when printed (or underlined when handwritten)</li>\n  <li>Example: <em>Homo sapiens</em> (human), <em>Zea mays</em> (maize), <em>Canis lupus familiaris</em> (dog)</li>\n</ul>\n\n<h3 class=\"learn-subheading\">4. Basis of Classification</h3>\n<p class=\"learn-p\">Organisms are classified based on:</p>\n<ul class=\"learn-list\">\n  <li><strong>Morphology:</strong> Physical appearance and body structure</li>\n  <li><strong>Anatomy:</strong> Internal structure</li>\n  <li><strong>Biochemistry:</strong> Chemical composition and reactions</li>\n  <li><strong>Genetics:</strong> DNA sequences and chromosomes</li>\n  <li><strong>Evolutionary history:</strong> Common ancestry (phylogeny)</li>\n</ul>\n\n<div class=\"learn-svg-wrap\">\n  <svg class=\"learn-svg\" viewBox=\"0 0 480 200\" xmlns=\"http://www.w3.org/2000/svg\">\n    <rect width=\"480\" height=\"200\" fill=\"#0f0020\" rx=\"10\"/>\n    <text x=\"240\" y=\"20\" text-anchor=\"middle\" fill=\"#FFC53D\" font-size=\"12\" font-weight=\"bold\">Hierarchy of Classification</text>\n    <rect x=\"185\" y=\"28\" width=\"110\" height=\"18\" rx=\"4\" fill=\"#7C3AED\"/>\n    <text x=\"240\" y=\"41\" text-anchor=\"middle\" fill=\"#fff\" font-size=\"9\">KINGDOM (broadest)</text>\n    <rect x=\"170\" y=\"50\" width=\"140\" height=\"16\" rx=\"4\" fill=\"#28c840\" fill-opacity=\"0.7\"/>\n    <text x=\"240\" y=\"62\" text-anchor=\"middle\" fill=\"#fff\" font-size=\"9\">PHYLUM</text>\n    <rect x=\"160\" y=\"70\" width=\"160\" height=\"16\" rx=\"4\" fill=\"#FFC53D\" fill-opacity=\"0.7\"/>\n    <text x=\"240\" y=\"82\" text-anchor=\"middle\" fill=\"#0f0020\" font-size=\"9\">CLASS</text>\n    <rect x=\"150\" y=\"90\" width=\"180\" height=\"16\" rx=\"4\" fill=\"#c8b4f0\" fill-opacity=\"0.7\"/>\n    <text x=\"240\" y=\"102\" text-anchor=\"middle\" fill=\"#0f0020\" font-size=\"9\">ORDER</text>\n    <rect x=\"140\" y=\"110\" width=\"200\" height=\"16\" rx=\"4\" fill=\"#28c840\" fill-opacity=\"0.5\"/>\n    <text x=\"240\" y=\"122\" text-anchor=\"middle\" fill=\"#fff\" font-size=\"9\">FAMILY</text>\n    <rect x=\"130\" y=\"130\" width=\"220\" height=\"16\" rx=\"4\" fill=\"#FFC53D\" fill-opacity=\"0.5\"/>\n    <text x=\"240\" y=\"142\" text-anchor=\"middle\" fill=\"#0f0020\" font-size=\"9\">GENUS</text>\n    <rect x=\"120\" y=\"150\" width=\"240\" height=\"18\" rx=\"4\" fill=\"#7C3AED\"/>\n    <text x=\"240\" y=\"163\" text-anchor=\"middle\" fill=\"#fff\" font-size=\"9\">SPECIES (most specific)</text>\n    <text x=\"240\" y=\"188\" text-anchor=\"middle\" fill=\"#c8c8c8\" font-size=\"8\">King Philip Came Over For Good Soup</text>\n  </svg>\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Remember that a species is defined as a group of organisms that can interbreed and produce fertile offspring. The scientific name of an organism always has the genus first (capital letter) and species second (lowercase), both in italics.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Classification organizes life into a hierarchy of seven levels: Kingdom, Phylum, Class, Order, Family, Genus, Species. Linnaeus developed binomial nomenclature — each organism has a two-part Latin name (genus + species). Classification is based on morphology, anatomy, biochemistry, genetics, and evolutionary history.\n</div>\n  ",
    "questions": [
      {
        "q": "The science of classifying organisms is called:",
        "o": ["Ecology", "Taxonomy", "Physiology", "Genetics"],
        "a": 1,
        "e": "Taxonomy is the branch of biology concerned with identifying, naming, and classifying organisms into groups based on shared characteristics.",
        "h": "Taxonomy = science of classification of living things.",
        "yr": "SS1"
      },
      {
        "q": "Who is credited with developing the modern system of biological classification?",
        "o": ["Charles Darwin", "Gregor Mendel", "Carl Linnaeus", "Louis Pasteur"],
        "a": 2,
        "e": "Carl Linnaeus (1707–1778) is the father of taxonomy. He developed the binomial nomenclature system and the hierarchical classification of organisms.",
        "h": "Carl Linnaeus = father of taxonomy and binomial nomenclature.",
        "yr": "SS1"
      },
      {
        "q": "In the correct order from broadest to most specific, the levels of classification are:",
        "o": [
          "Kingdom → Species → Genus → Family → Order → Class → Phylum",
          "Kingdom → Phylum → Class → Order → Family → Genus → Species",
          "Species → Genus → Family → Order → Class → Phylum → Kingdom",
          "Phylum → Kingdom → Class → Order → Genus → Family → Species"
        ],
        "a": 1,
        "e": "The correct hierarchy from broadest to most specific is: Kingdom → Phylum → Class → Order → Family → Genus → Species. Remember: King Philip Came Over For Good Soup.",
        "h": "Mnemonic: King Philip Came Over For Good Soup.",
        "yr": "SS1"
      },
      {
        "q": "The scientific name of maize is Zea mays. 'Zea' refers to the:",
        "o": ["Species", "Family", "Genus", "Kingdom"],
        "a": 2,
        "e": "In binomial nomenclature, the first word of the scientific name is the Genus (Zea) and the second is the species (mays). The genus always starts with a capital letter.",
        "h": "First word of binomial name = Genus (capital letter).",
        "yr": "SS1"
      },
      {
        "q": "Which rule applies to writing scientific names in binomial nomenclature?",
        "o": [
          "Both words are written in uppercase",
          "Genus starts with lowercase; species starts with uppercase",
          "Genus starts with uppercase; species is lowercase; both are italicized",
          "The names are written in the local language"
        ],
        "a": 2,
        "e": "In binomial nomenclature: the genus starts with a capital letter, the species is all lowercase, and both are italicized (or underlined when handwritten). They are written in Latin or Latinized form.",
        "h": "Genus = Capital; species = lowercase; both italicized.",
        "yr": "SS1"
      },
      {
        "q": "A species is best defined as a group of organisms that:",
        "o": [
          "Look exactly alike",
          "Live in the same habitat",
          "Can interbreed and produce fertile offspring",
          "Have the same diet"
        ],
        "a": 2,
        "e": "A species is defined as a group of organisms that can naturally interbreed with each other and produce fertile (viable) offspring. Members of different species generally cannot interbreed or produce fertile offspring.",
        "h": "Species = organisms that interbreed naturally and produce fertile offspring.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is the most specific level of classification?",
        "o": ["Kingdom", "Phylum", "Genus", "Species"],
        "a": 3,
        "e": "Species is the most specific (narrowest) level of classification. Kingdom is the broadest. As you move down the hierarchy (K→P→C→O→F→G→S), the groupings become more specific and organisms become more similar.",
        "h": "Species is the most specific taxon; Kingdom is the broadest.",
        "yr": "SS1"
      },
      {
        "q": "Two organisms belong to the same genus but different species. What does this imply?",
        "o": [
          "They are identical in all features",
          "They are closely related but not the same species",
          "They can always interbreed",
          "They belong to different kingdoms"
        ],
        "a": 1,
        "e": "Organisms in the same genus share a close evolutionary relationship and many features, but belong to different species. They generally cannot interbreed to produce fertile offspring.",
        "h": "Same genus = closely related; different species = cannot freely interbreed.",
        "yr": "SS1"
      },
      {
        "q": "The scientific name of humans is Homo sapiens. Their family is Hominidae. Which level comes directly ABOVE family in the hierarchy?",
        "o": ["Species", "Genus", "Order", "Class"],
        "a": 2,
        "e": "The hierarchy is K → P → C → Order → Family → Genus → Species. The level directly above Family is Order (Primates for humans).",
        "h": "Order comes above Family in the hierarchy.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is NOT a basis for classifying organisms?",
        "o": ["Morphology", "Biochemistry", "Geographical location", "Genetic makeup"],
        "a": 2,
        "e": "Classification is based on morphology, anatomy, biochemistry, genetics, and evolutionary history. Geographical location is not a basis — organisms from different continents may belong to the same species.",
        "h": "Geography is NOT a basis for classification; morphology, genetics, and biochemistry are.",
        "yr": "SS1"
      },
      {
        "q": "What is the main advantage of using scientific (Latin) names for organisms?",
        "o": [
          "They are easier to remember",
          "They ensure all scientists worldwide use the same name for each organism",
          "They describe the colour of the organism",
          "They indicate the size of the organism"
        ],
        "a": 1,
        "e": "Scientific names in Latin are universal — a biologist in Nigeria, Japan, or Brazil uses the same name for the same organism. This eliminates confusion caused by different common names in different languages.",
        "h": "Scientific names are universal — same name used globally by all scientists.",
        "yr": "SS1"
      },
      {
        "q": "Which kingdom contains organisms that are multicellular, lack chlorophyll, and obtain food by absorption?",
        "o": ["Plantae", "Animalia", "Fungi", "Monera"],
        "a": 2,
        "e": "Fungi are multicellular (mostly), lack chlorophyll, and are saprophytic — they secrete enzymes onto organic material and absorb the digested nutrients. Examples include mushrooms and moulds.",
        "h": "Fungi = multicellular, no chlorophyll, absorb food (saprophytic).",
        "yr": "SS1"
      },
      {
        "q": "How many levels are in the standard taxonomic hierarchy?",
        "o": ["5", "6", "7", "8"],
        "a": 2,
        "e": "The standard taxonomic hierarchy has 7 levels: Kingdom, Phylum, Class, Order, Family, Genus, Species. (Domain is sometimes added above Kingdom, making 8, but the traditional system has 7.)",
        "h": "7 levels: Kingdom, Phylum, Class, Order, Family, Genus, Species.",
        "yr": "SS1"
      },
      {
        "q": "The lion's scientific name is Panthera leo and the tiger's is Panthera tigris. What does this tell us?",
        "o": [
          "They are the same species",
          "They belong to the same genus (Panthera) but are different species",
          "They belong to different families",
          "They cannot be compared"
        ],
        "a": 1,
        "e": "Both share the genus Panthera, meaning they are closely related. However, their species names (leo and tigris) differ, so they are different species. They can occasionally interbreed (ligers) but normally do not in the wild.",
        "h": "Same genus (Panthera) = closely related; different species name = different species.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is correctly written according to binomial nomenclature rules?",
        "o": ["homo sapiens", "Homo Sapiens", "Homo sapiens", "HOMO SAPIENS"],
        "a": 2,
        "e": "The correct format is Homo sapiens — genus starts with a capital letter (Homo), species is all lowercase (sapiens), and both are italicized. 'Homo Sapiens' incorrectly capitalizes the species name.",
        "h": "Genus = Capital, species = lowercase, both italicized.",
        "yr": "SS1"
      },
      {
        "q": "Classification helps in medicine primarily because:",
        "o": [
          "It allows doctors to name patients",
          "Organisms in the same group often share biochemical properties useful in drug development",
          "It determines a patient's blood group",
          "It tells doctors how large an organism is"
        ],
        "a": 1,
        "e": "Organisms in the same taxonomic group tend to share similar biochemical properties. This helps scientists identify medicinal compounds — for example, plants in the same family often contain related active ingredients.",
        "h": "Related organisms share biochemical properties → useful for medicine and drug discovery.",
        "yr": "SS1"
      },
      {
        "q": "Which level of classification contains the largest number of different organisms?",
        "o": ["Species", "Genus", "Family", "Kingdom"],
        "a": 3,
        "e": "Kingdom is the broadest level and therefore contains the most diverse and largest number of organisms. As you move toward species, the groups become smaller and more specific.",
        "h": "Kingdom = broadest = contains the most organisms.",
        "yr": "SS1"
      },
      {
        "q": "In the classification hierarchy, which level comes directly BELOW genus?",
        "o": ["Family", "Order", "Species", "Class"],
        "a": 2,
        "e": "The hierarchy is: ...Family → Genus → Species. Species comes directly below genus — it is the most specific level.",
        "h": "Species is directly below Genus in the hierarchy.",
        "yr": "SS1"
      },
      {
        "q": "Two organisms that belong to the same species must also belong to the same:",
        "o": [
          "Habitat",
          "Genus, family, order, class, phylum, and kingdom",
          "Only the same family",
          "Only the same phylum"
        ],
        "a": 1,
        "e": "If two organisms are in the same species, they are automatically in the same genus, family, order, class, phylum, and kingdom — because each level contains all lower levels. The hierarchy is inclusive from top to bottom.",
        "h": "Same species → automatically same genus, family, order, class, phylum, kingdom.",
        "yr": "SS1"
      },
      {
        "q": "What mnemonic helps remember the order Kingdom → Phylum → Class → Order → Family → Genus → Species?",
        "o": [
          "King Philip Came Over For Good Soup",
          "King Peter Can Only Find Green Snakes",
          "Both A and B work",
          "No standard mnemonic exists"
        ],
        "a": 2,
        "e": "Both mnemonics work: 'King Philip Came Over For Good Soup' and 'King Peter Can Only Find Green Snakes' both map to K-P-C-O-F-G-S. Both are acceptable and widely used.",
        "h": "Any mnemonic mapping K-P-C-O-F-G-S is valid for remembering the taxonomy hierarchy.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Kingdoms of Living Things",
    "topicCode": "SS1-BIO-03",
    "module": "Nature of Biology",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  Modern biology organizes all life into <span class=\"learn-keyword\">five kingdoms</span>: Monera, Protista, Fungi, Plantae, and Animalia. Each kingdom is defined by fundamental differences in cell structure, nutrition, body organization, and mode of reproduction. Understanding the five kingdoms is essential to understanding the diversity of life on Earth.\n</div>\n\n<h3 class=\"learn-subheading\">1. Kingdom Monera (Prokaryotes)</h3>\n<p class=\"learn-p\">Monerans are the simplest and most ancient organisms. They are <strong>prokaryotes</strong> — their cells lack a true membrane-bound nucleus.</p>\n<ul class=\"learn-list\">\n  <li><strong>Examples:</strong> Bacteria (e.g., <em>Escherichia coli</em>) and Cyanobacteria (blue-green algae)</li>\n  <li><strong>Cell type:</strong> Prokaryotic (no nuclear membrane, no membrane-bound organelles)</li>\n  <li><strong>Body organization:</strong> Unicellular</li>\n  <li><strong>Nutrition:</strong> Autotrophic (photosynthetic or chemosynthetic) or heterotrophic (saprophytic or parasitic)</li>\n  <li><strong>Reproduction:</strong> Binary fission (asexual)</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Kingdom Protista</h3>\n<p class=\"learn-p\">Protists are mostly <strong>unicellular eukaryotes</strong> — they have a true nucleus but are generally single-celled.</p>\n<ul class=\"learn-list\">\n  <li><strong>Examples:</strong> Amoeba, Paramecium, Euglena, Plasmodium (malaria parasite), algae</li>\n  <li><strong>Cell type:</strong> Eukaryotic (true nucleus present)</li>\n  <li><strong>Body organization:</strong> Mostly unicellular; some colonial or multicellular</li>\n  <li><strong>Nutrition:</strong> Autotrophic (e.g., algae) or heterotrophic (e.g., Amoeba)</li>\n  <li><strong>Euglena</strong> is a special case — it is both autotrophic (has chlorophyll) and heterotrophic (can ingest food in darkness)</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Kingdom Fungi</h3>\n<p class=\"learn-p\">Fungi are mostly multicellular, eukaryotic organisms that cannot photosynthesize — they obtain nutrients by absorption.</p>\n<ul class=\"learn-list\">\n  <li><strong>Examples:</strong> Mushrooms, moulds, yeasts, <em>Rhizopus</em> (bread mould), <em>Aspergillus</em></li>\n  <li><strong>Cell type:</strong> Eukaryotic; cell walls made of chitin (not cellulose)</li>\n  <li><strong>Body structure:</strong> Made of thread-like hyphae forming a mycelium</li>\n  <li><strong>Nutrition:</strong> Saprophytic (absorb from dead matter) or parasitic</li>\n  <li><strong>Reproduction:</strong> Spores (asexual and sexual)</li>\n</ul>\n\n<h3 class=\"learn-subheading\">4. Kingdom Plantae</h3>\n<p class=\"learn-p\">Plants are multicellular eukaryotes with cell walls made of cellulose. They are autotrophic — they make food by photosynthesis.</p>\n<ul class=\"learn-list\">\n  <li><strong>Examples:</strong> Mosses, ferns, conifers, flowering plants</li>\n  <li><strong>Cell type:</strong> Eukaryotic; cell wall of cellulose; chloroplasts present</li>\n  <li><strong>Nutrition:</strong> Autotrophic (photosynthesis)</li>\n  <li><strong>Body organization:</strong> Multicellular; organized into tissues and organs</li>\n</ul>\n\n<h3 class=\"learn-subheading\">5. Kingdom Animalia</h3>\n<p class=\"learn-p\">Animals are multicellular eukaryotes without cell walls. They are heterotrophic — they consume other organisms for food.</p>\n<ul class=\"learn-list\">\n  <li><strong>Examples:</strong> Insects, fish, amphibians, reptiles, birds, mammals</li>\n  <li><strong>Cell type:</strong> Eukaryotic; no cell wall; no chloroplasts</li>\n  <li><strong>Nutrition:</strong> Heterotrophic (ingestive feeding)</li>\n  <li><strong>Movement:</strong> Most can move from place to place (locomotion)</li>\n</ul>\n\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Kingdom</th><th>Cell Type</th><th>Cell Wall</th><th>Nutrition</th><th>Example</th></tr></thead>\n    <tbody>\n      <tr><td>Monera</td><td>Prokaryotic</td><td>Peptidoglycan</td><td>Auto/Hetero</td><td>Bacteria</td></tr>\n      <tr><td>Protista</td><td>Eukaryotic</td><td>Some have</td><td>Auto/Hetero</td><td>Amoeba, Euglena</td></tr>\n      <tr><td>Fungi</td><td>Eukaryotic</td><td>Chitin</td><td>Saprophytic</td><td>Mushroom</td></tr>\n      <tr><td>Plantae</td><td>Eukaryotic</td><td>Cellulose</td><td>Autotrophic</td><td>Mango tree</td></tr>\n      <tr><td>Animalia</td><td>Eukaryotic</td><td>None</td><td>Heterotrophic</td><td>Human</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> The key difference between Monera and the other four kingdoms is that Monera are PROKARYOTES (no true nucleus). All others are eukaryotes. Remember that Fungi cell walls contain CHITIN (not cellulose like plants).\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> The five kingdoms are Monera (prokaryotes), Protista (mostly unicellular eukaryotes), Fungi (absorptive heterotrophs, chitin cell walls), Plantae (photosynthetic, cellulose cell walls), and Animalia (ingestive heterotrophs, no cell walls). Euglena is a protist showing both plant-like and animal-like features.\n</div>\n  ",
    "questions": [
      {
        "q": "Which kingdom contains organisms that are prokaryotic?",
        "o": ["Protista", "Fungi", "Monera", "Plantae"],
        "a": 2,
        "e": "Kingdom Monera contains prokaryotes — organisms whose cells lack a true membrane-bound nucleus. Bacteria and cyanobacteria are examples. All other kingdoms (Protista, Fungi, Plantae, Animalia) are eukaryotic.",
        "h": "Monera = prokaryotes (no true nucleus).",
        "yr": "SS1"
      },
      {
        "q": "What substance makes up the cell wall of Fungi?",
        "o": ["Cellulose", "Peptidoglycan", "Chitin", "Starch"],
        "a": 2,
        "e": "The cell wall of fungi is made of chitin — a nitrogen-containing polysaccharide. Plant cell walls are made of cellulose, and bacterial cell walls are made of peptidoglycan.",
        "h": "Fungi cell wall = chitin (not cellulose like plants).",
        "yr": "SS1"
      },
      {
        "q": "Euglena is classified in which kingdom?",
        "o": ["Monera", "Plantae", "Protista", "Fungi"],
        "a": 2,
        "e": "Euglena belongs to Kingdom Protista. It is a unicellular eukaryote that shows both plant-like features (has chlorophyll and can photosynthesize) and animal-like features (can move with a flagellum and feed heterotrophically in darkness).",
        "h": "Euglena = Protista (unicellular eukaryote with plant and animal features).",
        "yr": "SS1"
      },
      {
        "q": "Kingdom Animalia organisms are best characterized by:",
        "o": [
          "Having cellulose cell walls",
          "Being autotrophic",
          "Being multicellular eukaryotes without cell walls that feed heterotrophically",
          "Reproducing only by binary fission"
        ],
        "a": 2,
        "e": "Animals are multicellular eukaryotes that lack cell walls, have no chloroplasts, and obtain nutrition by ingesting other organisms (heterotrophic). They generally show locomotion and complex tissue organization.",
        "h": "Animals = multicellular, no cell walls, heterotrophic (ingestive).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a feature of Kingdom Plantae?",
        "o": [
          "Chitin cell walls and saprophytic nutrition",
          "No cell walls and ingestive feeding",
          "Cellulose cell walls and autotrophic (photosynthetic) nutrition",
          "Prokaryotic cells and binary fission"
        ],
        "a": 2,
        "e": "Plants have eukaryotic cells with cellulose cell walls and chloroplasts. They make their own food via photosynthesis (autotrophic). These features distinguish them from fungi (chitin, saprophytic) and animals (no cell walls, ingestive).",
        "h": "Plantae = cellulose cell walls + photosynthesis (autotrophic).",
        "yr": "SS1"
      },
      {
        "q": "Bacteria reproduce primarily by:",
        "o": ["Budding", "Binary fission", "Spore formation", "Sexual reproduction"],
        "a": 1,
        "e": "Bacteria (Kingdom Monera) reproduce mainly by binary fission — the cell divides into two equal daughter cells. This is a form of asexual reproduction that can occur very rapidly under favorable conditions.",
        "h": "Bacteria = binary fission (asexual reproduction).",
        "yr": "SS1"
      },
      {
        "q": "Which kingdom includes the malaria-causing parasite Plasmodium?",
        "o": ["Monera", "Animalia", "Protista", "Fungi"],
        "a": 2,
        "e": "Plasmodium (the malaria parasite) belongs to Kingdom Protista. It is a unicellular eukaryote that lives parasitically in human red blood cells and is transmitted by the female Anopheles mosquito.",
        "h": "Plasmodium = Protista (unicellular eukaryotic parasite causing malaria).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is NOT a member of Kingdom Fungi?",
        "o": ["Mushroom", "Yeast", "Rhizopus (bread mould)", "Amoeba"],
        "a": 3,
        "e": "Amoeba is a member of Kingdom Protista, not Fungi. Mushrooms, yeasts, and Rhizopus (bread mould) are all fungi — they have chitin cell walls and obtain nutrition by absorption.",
        "h": "Amoeba = Protista; mushroom, yeast, Rhizopus = Fungi.",
        "yr": "SS1"
      },
      {
        "q": "What is the key difference between Monera and Protista?",
        "o": [
          "Monera are autotrophic; Protista are heterotrophic",
          "Monera are prokaryotic (no true nucleus); Protista are eukaryotic (true nucleus present)",
          "Monera are multicellular; Protista are unicellular",
          "Monera have chitin cell walls; Protista have cellulose cell walls"
        ],
        "a": 1,
        "e": "The fundamental difference is cell structure: Monera are prokaryotes (no membrane-bound nucleus, no membrane-bound organelles), while Protista are eukaryotes (true nucleus enclosed in a nuclear membrane).",
        "h": "Monera = prokaryotic; Protista = eukaryotic. This is the key distinction.",
        "yr": "SS1"
      },
      {
        "q": "The body of a fungus is made up of thread-like structures called:",
        "o": ["Cilia", "Hyphae", "Flagella", "Villi"],
        "a": 1,
        "e": "The body of most fungi is composed of thread-like filaments called hyphae (singular: hypha). A mass of hyphae forms the mycelium, which is the main body of the fungus.",
        "h": "Fungal body = hyphae → collectively called mycelium.",
        "yr": "SS1"
      },
      {
        "q": "Which kingdom would a photosynthetic, multicellular organism with cellulose cell walls belong to?",
        "o": ["Fungi", "Protista", "Monera", "Plantae"],
        "a": 3,
        "e": "Photosynthetic + multicellular + cellulose cell walls = Kingdom Plantae. These three features together are the hallmarks of the plant kingdom.",
        "h": "Photosynthetic + multicellular + cellulose cell wall = Plantae.",
        "yr": "SS1"
      },
      {
        "q": "Cyanobacteria (blue-green algae) belong to which kingdom?",
        "o": ["Protista", "Plantae", "Monera", "Animalia"],
        "a": 2,
        "e": "Cyanobacteria belong to Kingdom Monera. They are prokaryotes — their cells lack a true nucleus. Although they photosynthesize like plants, their prokaryotic cell structure places them firmly in Monera.",
        "h": "Cyanobacteria = prokaryotic → Kingdom Monera (not Plantae).",
        "yr": "SS1"
      },
      {
        "q": "How does Fungi differ from Plantae in terms of nutrition?",
        "o": [
          "Fungi photosynthesize; plants absorb food",
          "Fungi absorb food saprophytically; plants photosynthesize",
          "Both photosynthesize",
          "Both absorb food"
        ],
        "a": 1,
        "e": "Fungi are saprophytic — they secrete digestive enzymes into organic matter and absorb the products. They cannot photosynthesize because they lack chlorophyll. Plants are autotrophic and produce food through photosynthesis.",
        "h": "Fungi = absorptive saprophytes (no chlorophyll); Plants = photosynthetic autotrophs.",
        "yr": "SS1"
      },
      {
        "q": "Which kingdom contains organisms that are unicellular, eukaryotic, and may be either autotrophic or heterotrophic?",
        "o": ["Monera", "Fungi", "Protista", "Animalia"],
        "a": 2,
        "e": "Kingdom Protista contains mostly unicellular eukaryotes that can be autotrophic (e.g., algae with chlorophyll) or heterotrophic (e.g., Amoeba that engulfs food). Euglena can be both.",
        "h": "Protista = unicellular eukaryotes, can be auto or heterotrophic.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following organisms is correctly matched with its kingdom?",
        "o": [
          "Mushroom → Plantae",
          "Amoeba → Animalia",
          "E. coli (bacteria) → Monera",
          "Fern → Fungi"
        ],
        "a": 2,
        "e": "E. coli is a bacterium — prokaryotic → Kingdom Monera. Mushroom belongs to Fungi (not Plantae), Amoeba belongs to Protista (not Animalia), and ferns belong to Plantae (not Fungi).",
        "h": "E. coli = bacterium = Monera (prokaryotic).",
        "yr": "SS1"
      },
      {
        "q": "What does 'eukaryotic' mean?",
        "o": [
          "Cells without a nucleus",
          "Cells with a true membrane-bound nucleus",
          "Cells with no organelles",
          "Cells with chitin walls"
        ],
        "a": 1,
        "e": "Eukaryotic cells have a true nucleus enclosed within a nuclear membrane, along with other membrane-bound organelles (e.g., mitochondria, endoplasmic reticulum). This contrasts with prokaryotic cells, which lack a membrane-bound nucleus.",
        "h": "Eukaryotic = cells with a true membrane-bound nucleus.",
        "yr": "SS1"
      },
      {
        "q": "Which kingdom do mushrooms belong to, and why?",
        "o": [
          "Plantae — because they grow in soil",
          "Fungi — because they lack chlorophyll, have chitin cell walls, and absorb food",
          "Protista — because they are small",
          "Animalia — because they cannot photosynthesize"
        ],
        "a": 1,
        "e": "Mushrooms belong to Kingdom Fungi because they have eukaryotic cells with chitin cell walls, lack chlorophyll, and obtain nutrition by secreting enzymes and absorbing digested organic matter from the substrate.",
        "h": "Mushrooms = Fungi: chitin walls, no chlorophyll, absorptive nutrition.",
        "yr": "SS1"
      },
      {
        "q": "Which of the five kingdoms does NOT have a cell wall in any of its members?",
        "o": ["Monera", "Fungi", "Plantae", "Animalia"],
        "a": 3,
        "e": "Animal cells (Kingdom Animalia) never have cell walls. Bacteria (Monera) have peptidoglycan walls, Fungi have chitin walls, and Plants have cellulose walls. The absence of a cell wall allows animal cells greater flexibility.",
        "h": "Animalia = no cell walls in any member.",
        "yr": "SS1"
      },
      {
        "q": "Rhizopus stolonifer is the bread mould that grows on stale bread. Its mode of nutrition is:",
        "o": ["Photosynthesis", "Chemosynthesis", "Saprophytic absorption", "Parasitism"],
        "a": 2,
        "e": "Rhizopus is a fungus and a saprophyte — it secretes digestive enzymes onto the bread, breaking down complex molecules, and then absorbs the soluble products. This is saprophytic (absorptive heterotrophic) nutrition.",
        "h": "Rhizopus = fungus = saprophytic nutrition (secretion + absorption).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following statements about Kingdom Protista is CORRECT?",
        "o": [
          "All protists are prokaryotic",
          "All protists are heterotrophic",
          "Protists include unicellular eukaryotes that may be autotrophic or heterotrophic",
          "Protists all have chitin cell walls"
        ],
        "a": 2,
        "e": "Protists are eukaryotes (true nucleus). They include autotrophic forms (algae with chlorophyll), heterotrophic forms (Amoeba, Paramecium), and mixed forms (Euglena). They do not have chitin walls and are not prokaryotic.",
        "h": "Protista = eukaryotic unicellular organisms; can be auto or heterotrophic.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "The Cell",
    "topicCode": "SS1-BIO-04",
    "module": "Cell Biology",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  The <span class=\"learn-keyword\">cell</span> is the basic structural and functional unit of all living organisms. This is the <span class=\"learn-keyword\">cell theory</span>, formulated by Schleiden (1838), Schwann (1839), and Virchow (1855). Every living organism is made of one or more cells, and all cells arise from pre-existing cells.\n</div>\n\n<h3 class=\"learn-subheading\">1. Types of Cells</h3>\n<p class=\"learn-p\">There are two fundamental cell types:</p>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Feature</th><th>Prokaryotic Cell</th><th>Eukaryotic Cell</th></tr></thead>\n    <tbody>\n      <tr><td>Nucleus</td><td>No membrane-bound nucleus; nucleoid region</td><td>True nucleus with nuclear membrane</td></tr>\n      <tr><td>Size</td><td>Smaller (1–10 µm)</td><td>Larger (10–100 µm)</td></tr>\n      <tr><td>Organelles</td><td>No membrane-bound organelles</td><td>Membrane-bound organelles present</td></tr>\n      <tr><td>DNA</td><td>Circular, in cytoplasm</td><td>Linear, enclosed in nucleus</td></tr>\n      <tr><td>Examples</td><td>Bacteria, cyanobacteria</td><td>Plant, animal, fungal cells</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Cell Organelles and Their Functions</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Organelle</th><th>Function</th><th>Present In</th></tr></thead>\n    <tbody>\n      <tr><td>Cell membrane</td><td>Controls entry/exit of substances (selective permeability)</td><td>All cells</td></tr>\n      <tr><td>Cell wall</td><td>Provides structural support and shape</td><td>Plants, fungi, bacteria</td></tr>\n      <tr><td>Nucleus</td><td>Controls cell activities; contains DNA (genetic information)</td><td>Eukaryotes</td></tr>\n      <tr><td>Nucleolus</td><td>Produces ribosomal RNA (rRNA)</td><td>Eukaryotes</td></tr>\n      <tr><td>Cytoplasm</td><td>Medium where chemical reactions occur; suspends organelles</td><td>All cells</td></tr>\n      <tr><td>Mitochondria</td><td>Site of aerobic respiration; produces ATP (\"powerhouse of cell\")</td><td>Eukaryotes</td></tr>\n      <tr><td>Chloroplast</td><td>Site of photosynthesis; contains chlorophyll</td><td>Plant cells only</td></tr>\n      <tr><td>Ribosomes</td><td>Site of protein synthesis</td><td>All cells</td></tr>\n      <tr><td>Endoplasmic reticulum (ER)</td><td>Transport network; rough ER has ribosomes (protein); smooth ER (lipids)</td><td>Eukaryotes</td></tr>\n      <tr><td>Golgi apparatus</td><td>Packages and secretes proteins and lipids</td><td>Eukaryotes</td></tr>\n      <tr><td>Lysosomes</td><td>Digestion of waste materials and foreign substances</td><td>Animal cells mainly</td></tr>\n      <tr><td>Vacuole</td><td>Storage (water, food, waste); large central vacuole in plants</td><td>All cells (large in plants)</td></tr>\n      <tr><td>Centrosome/Centrioles</td><td>Cell division (forms spindle fibres)</td><td>Animal cells mainly</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">3. Plant vs Animal Cells</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Feature</th><th>Plant Cell</th><th>Animal Cell</th></tr></thead>\n    <tbody>\n      <tr><td>Cell wall</td><td>Present (cellulose)</td><td>Absent</td></tr>\n      <tr><td>Chloroplasts</td><td>Present</td><td>Absent</td></tr>\n      <tr><td>Vacuole</td><td>Large, permanent, central</td><td>Small or absent</td></tr>\n      <tr><td>Centrioles</td><td>Absent (in most)</td><td>Present</td></tr>\n      <tr><td>Shape</td><td>Regular, fixed</td><td>Irregular, flexible</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class=\"learn-svg-wrap\">\n  <svg class=\"learn-svg\" viewBox=\"0 0 480 200\" xmlns=\"http://www.w3.org/2000/svg\">\n    <rect width=\"480\" height=\"200\" fill=\"#0f0020\" rx=\"10\"/>\n    <text x=\"240\" y=\"18\" text-anchor=\"middle\" fill=\"#FFC53D\" font-size=\"11\" font-weight=\"bold\">Plant Cell vs Animal Cell</text>\n    <!-- Plant Cell -->\n    <rect x=\"15\" y=\"25\" width=\"215\" height=\"160\" rx=\"8\" fill=\"#1B0A47\" stroke=\"#28c840\" stroke-width=\"2\"/>\n    <text x=\"122\" y=\"42\" text-anchor=\"middle\" fill=\"#28c840\" font-size=\"10\" font-weight=\"bold\">PLANT CELL</text>\n    <text x=\"122\" y=\"58\" text-anchor=\"middle\" fill=\"#c8c8c8\" font-size=\"8\">✔ Cell wall (cellulose)</text>\n    <text x=\"122\" y=\"72\" text-anchor=\"middle\" fill=\"#c8c8c8\" font-size=\"8\">✔ Chloroplasts</text>\n    <text x=\"122\" y=\"86\" text-anchor=\"middle\" fill=\"#c8c8c8\" font-size=\"8\">✔ Large central vacuole</text>\n    <text x=\"122\" y=\"100\" text-anchor=\"middle\" fill=\"#c8c8c8\" font-size=\"8\">✔ Nucleus, mitochondria</text>\n    <text x=\"122\" y=\"114\" text-anchor=\"middle\" fill=\"#c8c8c8\" font-size=\"8\">✔ Fixed rectangular shape</text>\n    <text x=\"122\" y=\"128\" text-anchor=\"middle\" fill=\"#FFC53D\" font-size=\"8\">✘ No centrioles (usually)</text>\n    <!-- Animal Cell -->\n    <rect x=\"250\" y=\"25\" width=\"215\" height=\"160\" rx=\"8\" fill=\"#1B0A47\" stroke=\"#c8b4f0\" stroke-width=\"2\"/>\n    <text x=\"357\" y=\"42\" text-anchor=\"middle\" fill=\"#c8b4f0\" font-size=\"10\" font-weight=\"bold\">ANIMAL CELL</text>\n    <text x=\"357\" y=\"58\" text-anchor=\"middle\" fill=\"#c8c8c8\" font-size=\"8\">✔ Centrioles</text>\n    <text x=\"357\" y=\"72\" text-anchor=\"middle\" fill=\"#c8c8c8\" font-size=\"8\">✔ Lysosomes</text>\n    <text x=\"357\" y=\"86\" text-anchor=\"middle\" fill=\"#c8c8c8\" font-size=\"8\">✔ Nucleus, mitochondria</text>\n    <text x=\"357\" y=\"100\" text-anchor=\"middle\" fill=\"#c8c8c8\" font-size=\"8\">✔ Irregular shape (flexible)</text>\n    <text x=\"357\" y=\"114\" text-anchor=\"middle\" fill=\"#FFC53D\" font-size=\"8\">✘ No cell wall</text>\n    <text x=\"357\" y=\"128\" text-anchor=\"middle\" fill=\"#FFC53D\" font-size=\"8\">✘ No chloroplasts</text>\n    <text x=\"357\" y=\"142\" text-anchor=\"middle\" fill=\"#FFC53D\" font-size=\"8\">✘ No large vacuole</text>\n  </svg>\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> The three organelles found in plant cells but NOT in animal cells are: cell wall, chloroplasts, and large permanent central vacuole. Centrioles are found in animal cells but not (usually) in plant cells.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Cells are the basic units of life (cell theory). Prokaryotic cells (bacteria) lack a true nucleus; eukaryotic cells have a true membrane-bound nucleus. Organelles have specific functions: mitochondria = energy (ATP), chloroplasts = photosynthesis, ribosomes = protein synthesis, nucleus = control center (DNA). Plant cells have cell walls (cellulose), chloroplasts, and large vacuoles; animal cells have centrioles and lysosomes.\n</div>\n  ",
    "questions": [
      {
        "q": "Which organelle is known as the 'powerhouse of the cell'?",
        "o": ["Nucleus", "Ribosome", "Mitochondrion", "Golgi apparatus"],
        "a": 2,
        "e": "The mitochondrion is called the powerhouse of the cell because it is the site of aerobic respiration, where ATP (adenosine triphosphate) — the cell's energy currency — is produced from glucose and oxygen.",
        "h": "Mitochondrion = powerhouse = site of aerobic respiration and ATP production.",
        "yr": "SS1"
      },
      {
        "q": "Which organelle is the site of protein synthesis?",
        "o": ["Mitochondrion", "Ribosome", "Lysosome", "Vacuole"],
        "a": 1,
        "e": "Ribosomes are the sites of protein synthesis — they translate messenger RNA (mRNA) into polypeptide chains. Ribosomes are found in all cells (prokaryotic and eukaryotic), either free in the cytoplasm or attached to the rough endoplasmic reticulum.",
        "h": "Ribosome = protein synthesis site (found in ALL cells).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following structures is found in plant cells but NOT in animal cells?",
        "o": ["Mitochondrion", "Ribosome", "Chloroplast", "Nucleus"],
        "a": 2,
        "e": "Chloroplasts are found only in plant cells (and some protists). They contain chlorophyll and are the site of photosynthesis. Animal cells lack chloroplasts because animals cannot photosynthesize.",
        "h": "Chloroplast = plant cells only (site of photosynthesis).",
        "yr": "SS1"
      },
      {
        "q": "The cell membrane is described as 'selectively permeable' because:",
        "o": [
          "It allows all substances to pass through equally",
          "It allows only selected substances to pass in or out",
          "It prevents all substances from entering",
          "It only allows water to pass through"
        ],
        "a": 1,
        "e": "The cell membrane is selectively (semi) permeable — it controls what enters and leaves the cell, allowing some substances (like water and small molecules) to pass freely while restricting or regulating others.",
        "h": "Selectively permeable = allows certain substances through but not others.",
        "yr": "SS1"
      },
      {
        "q": "The nucleus controls the cell mainly because it:",
        "o": [
          "Is the largest organelle",
          "Produces energy",
          "Contains DNA, which directs cell activities and stores genetic information",
          "Produces chlorophyll"
        ],
        "a": 2,
        "e": "The nucleus contains DNA (deoxyribonucleic acid), the genetic blueprint of the cell. DNA carries instructions for making proteins, which control all cellular processes. Without the nucleus, the cell cannot function or reproduce properly.",
        "h": "Nucleus = control center because it contains DNA (genetic information).",
        "yr": "SS1"
      },
      {
        "q": "Which structure is present in animal cells but typically ABSENT in plant cells?",
        "o": ["Cell wall", "Vacuole", "Centrioles", "Mitochondria"],
        "a": 2,
        "e": "Centrioles are present in animal cells (and lower plants) where they form the spindle apparatus during cell division. Most higher plant cells lack centrioles but still undergo cell division using other mechanisms.",
        "h": "Centrioles = found in animal cells, typically absent in higher plant cells.",
        "yr": "SS1"
      },
      {
        "q": "The Golgi apparatus in a cell is responsible for:",
        "o": [
          "Producing ATP",
          "Synthesizing DNA",
          "Packaging and secreting proteins and lipids",
          "Photosynthesis"
        ],
        "a": 2,
        "e": "The Golgi apparatus (Golgi body) receives proteins from the endoplasmic reticulum, modifies, packages, and labels them for secretion outside the cell or delivery to other organelles (like lysosomes).",
        "h": "Golgi apparatus = packaging and secretion of proteins and lipids.",
        "yr": "SS1"
      },
      {
        "q": "Lysosomes are found mainly in animal cells. Their function is:",
        "o": [
          "Photosynthesis",
          "Protein synthesis",
          "Digestion of waste materials and foreign substances using enzymes",
          "Storage of water"
        ],
        "a": 2,
        "e": "Lysosomes contain hydrolytic enzymes that digest old cell organelles, engulfed bacteria, and cellular debris. This process is called autophagy (self-eating). Lysosomes are sometimes called the 'suicide bags' of the cell.",
        "h": "Lysosomes = digestive organelles ('suicide bags') containing hydrolytic enzymes.",
        "yr": "SS1"
      },
      {
        "q": "The cell wall of a plant cell is made of:",
        "o": ["Chitin", "Cellulose", "Peptidoglycan", "Glycogen"],
        "a": 1,
        "e": "The cell wall of plant cells is composed of cellulose — a polysaccharide made of glucose units. It provides structural rigidity and prevents the cell from bursting when turgid. Fungal cell walls contain chitin; bacterial walls contain peptidoglycan.",
        "h": "Plant cell wall = cellulose (unlike fungi = chitin, bacteria = peptidoglycan).",
        "yr": "SS1"
      },
      {
        "q": "The rough endoplasmic reticulum (RER) differs from the smooth ER in that it:",
        "o": [
          "Has ribosomes attached to its surface and is involved in protein synthesis",
          "Produces lipids",
          "Is involved in detoxification",
          "Contains chlorophyll"
        ],
        "a": 0,
        "e": "The rough ER has ribosomes on its surface, giving it a 'rough' appearance. It is involved in the synthesis and transport of proteins. The smooth ER lacks ribosomes and is involved in lipid synthesis and detoxification.",
        "h": "Rough ER = ribosomes on surface = protein synthesis/transport.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following correctly states the cell theory?",
        "o": [
          "Cells only exist in multicellular organisms",
          "All living organisms are made of cells, and all cells come from pre-existing cells",
          "Cells are only found in animals",
          "Cells were first discovered in the 20th century"
        ],
        "a": 1,
        "e": "The cell theory states: (1) all living organisms are made of one or more cells, (2) the cell is the basic unit of life, and (3) all cells arise from pre-existing cells (Virchow, 1855). This applies universally to all living things.",
        "h": "Cell theory: all living things are made of cells; all cells come from pre-existing cells.",
        "yr": "SS1"
      },
      {
        "q": "The large permanent central vacuole in plant cells serves to:",
        "o": [
          "Produce chlorophyll",
          "Maintain cell turgidity by storing water and maintain cell shape",
          "Synthesize proteins",
          "Carry out photosynthesis"
        ],
        "a": 1,
        "e": "The large central vacuole in plant cells stores water, dissolved salts, sugars, and waste products. It exerts turgor pressure against the cell wall, keeping the plant cell firm (turgid) and helping maintain the plant's structure.",
        "h": "Plant central vacuole = stores water → turgidity → structural support.",
        "yr": "SS1"
      },
      {
        "q": "Which organelle is found in ALL types of cells (both prokaryotic and eukaryotic)?",
        "o": ["Mitochondrion", "Chloroplast", "Ribosome", "Golgi apparatus"],
        "a": 2,
        "e": "Ribosomes are found in all cells — prokaryotic and eukaryotic. They are the universal sites of protein synthesis. However, prokaryotic ribosomes are smaller (70S) than eukaryotic ribosomes (80S).",
        "h": "Ribosomes = universal; found in ALL cells (prokaryotic and eukaryotic).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is NOT a feature of prokaryotic cells?",
        "o": [
          "Circular DNA",
          "Ribosomes",
          "Membrane-bound nucleus",
          "Cell membrane"
        ],
        "a": 2,
        "e": "Prokaryotic cells lack a membrane-bound nucleus — their DNA floats freely in the cytoplasm in a region called the nucleoid. They do have ribosomes, a cell membrane, and circular DNA.",
        "h": "Prokaryotes lack a membrane-bound nucleus (no nuclear membrane).",
        "yr": "SS1"
      },
      {
        "q": "The nucleolus within the nucleus is responsible for:",
        "o": [
          "Storing genetic information (DNA)",
          "Producing ribosomal RNA (rRNA)",
          "Controlling what enters the nucleus",
          "Generating ATP"
        ],
        "a": 1,
        "e": "The nucleolus is a dense region within the nucleus that synthesizes ribosomal RNA (rRNA) — a key component of ribosomes. It assembles ribosomal subunits that are later exported to the cytoplasm.",
        "h": "Nucleolus = produces rRNA (used to build ribosomes).",
        "yr": "SS1"
      },
      {
        "q": "Chloroplasts contain a green pigment called:",
        "o": ["Haemoglobin", "Melanin", "Chlorophyll", "Carotene"],
        "a": 2,
        "e": "Chloroplasts contain chlorophyll — the green pigment that absorbs light energy (mainly red and blue wavelengths) used in photosynthesis to convert CO₂ and water into glucose and oxygen.",
        "h": "Chloroplast = contains chlorophyll (green pigment for photosynthesis).",
        "yr": "SS1"
      },
      {
        "q": "Which scientist proposed that all cells come from pre-existing cells?",
        "o": ["Schleiden", "Schwann", "Virchow", "Hooke"],
        "a": 2,
        "e": "Rudolf Virchow (1855) proposed 'Omnis cellula e cellula' — every cell from a cell. He added the third tenet of cell theory: all cells arise from pre-existing cells. Schleiden and Schwann proposed the earlier parts of cell theory.",
        "h": "Virchow (1855) = 'all cells from pre-existing cells'.",
        "yr": "SS1"
      },
      {
        "q": "The cytoplasm of a cell is best described as:",
        "o": [
          "The outer boundary of the cell",
          "The jelly-like fluid filling the cell that suspends organelles and is the site of many chemical reactions",
          "The membrane surrounding the nucleus",
          "The site of ATP production"
        ],
        "a": 1,
        "e": "The cytoplasm is the jelly-like fluid (cytosol) that fills the cell between the cell membrane and the nucleus. It suspends all organelles and is the site of many metabolic reactions, including glycolysis.",
        "h": "Cytoplasm = jelly-like fluid, suspends organelles, site of many reactions.",
        "yr": "SS1"
      },
      {
        "q": "Which three features distinguish a plant cell from an animal cell?",
        "o": [
          "Nucleus, mitochondria, ribosomes",
          "Cell wall, chloroplasts, large central vacuole",
          "Centrioles, lysosomes, small vacuoles",
          "Golgi apparatus, ER, ribosomes"
        ],
        "a": 1,
        "e": "The three features unique to plant cells (absent in animal cells) are: cellulose cell wall, chloroplasts, and a large permanent central vacuole. Animal cells have centrioles and prominent lysosomes instead.",
        "h": "Plant cell: cell wall + chloroplasts + large vacuole (absent in animal cells).",
        "yr": "SS1"
      },
      {
        "q": "The endoplasmic reticulum (ER) functions primarily as:",
        "o": [
          "The site of photosynthesis",
          "An intracellular transport system connecting the nucleus to the cell membrane",
          "The site of DNA replication",
          "The energy-producing organelle"
        ],
        "a": 1,
        "e": "The endoplasmic reticulum is a network of membranous channels and sacs that forms an intracellular transport system. Rough ER transports proteins synthesized by ribosomes; smooth ER synthesizes lipids and detoxifies substances.",
        "h": "ER = intracellular transport network (rough ER: proteins; smooth ER: lipids).",
        "yr": "SS1"
      }
    ]
  }
,
  {
    "topic": "Cell and Its Environment",
    "topicCode": "SS1-BIO-05",
    "module": "Cell Biology",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  Cells constantly interact with their surroundings. Three key processes govern how substances move into and out of cells: <span class=\"learn-keyword\">diffusion</span>, <span class=\"learn-keyword\">osmosis</span>, and <span class=\"learn-keyword\">active transport</span>. Understanding these processes explains how cells obtain nutrients, expel waste, and maintain their internal balance (homeostasis).\n</div>\n\n<h3 class=\"learn-subheading\">1. Diffusion</h3>\n<p class=\"learn-p\">Diffusion is the movement of molecules or ions from a region of <strong>higher concentration</strong> to a region of <strong>lower concentration</strong> down the concentration gradient. It requires <strong>no energy</strong> (passive process).</p>\n<ul class=\"learn-list\">\n  <li>Occurs in gases, liquids, and across cell membranes</li>\n  <li>Rate depends on: concentration gradient, temperature, surface area, and molecular size</li>\n  <li><strong>Examples:</strong> O₂ entering cells; CO₂ leaving cells; perfume spreading in a room</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Osmosis</h3>\n<p class=\"learn-p\">Osmosis is the diffusion of <strong>water molecules</strong> through a <span class=\"learn-keyword\">selectively permeable membrane</span> from a region of <strong>lower solute concentration</strong> (higher water potential) to a region of <strong>higher solute concentration</strong> (lower water potential).</p>\n<ul class=\"learn-list\">\n  <li>Only water molecules move (not solutes)</li>\n  <li>Requires no energy (passive process)</li>\n  <li><strong>Hypotonic solution:</strong> Less concentrated than cell → water enters cell → cell swells (may burst in animal cells = lysis; plant cells become turgid)</li>\n  <li><strong>Hypertonic solution:</strong> More concentrated than cell → water leaves cell → cell shrinks (plasmolysis in plants; crenation in animal cells)</li>\n  <li><strong>Isotonic solution:</strong> Same concentration as cell → no net movement of water</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Active Transport</h3>\n<p class=\"learn-p\">Active transport is the movement of molecules or ions from a region of <strong>lower concentration</strong> to a region of <strong>higher concentration</strong> — <em>against</em> the concentration gradient. It <strong>requires energy (ATP)</strong> and involves <strong>carrier proteins</strong>.</p>\n<ul class=\"learn-list\">\n  <li>Examples: absorption of glucose and amino acids from the gut; uptake of mineral ions by root hair cells; sodium-potassium pump in nerve cells</li>\n  <li>Requires metabolic energy (ATP) and specific carrier proteins in the membrane</li>\n</ul>\n\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Feature</th><th>Diffusion</th><th>Osmosis</th><th>Active Transport</th></tr></thead>\n    <tbody>\n      <tr><td>Substance</td><td>Any molecule/ion</td><td>Water only</td><td>Any molecule/ion</td></tr>\n      <tr><td>Direction</td><td>High → Low conc.</td><td>High water → Low water potential</td><td>Low → High conc. (against gradient)</td></tr>\n      <tr><td>Energy needed</td><td>No</td><td>No</td><td>Yes (ATP)</td></tr>\n      <tr><td>Membrane needed</td><td>Not always</td><td>Yes (selectively permeable)</td><td>Yes</td></tr>\n      <tr><td>Carrier proteins</td><td>No (simple diffusion)</td><td>No (aquaporins)</td><td>Yes</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Osmosis is a special type of diffusion involving ONLY water through a selectively permeable membrane. Remember: water moves from LESS concentrated solution (more water) to MORE concentrated solution (less water). Plants become turgid in hypotonic solutions (beneficial — maintains shape) and plasmolyse in hypertonic solutions.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Three ways substances cross cell membranes: (1) Diffusion — passive, any molecule, high to low concentration. (2) Osmosis — passive, water only, through selectively permeable membrane, from high to low water potential. (3) Active transport — requires ATP, against concentration gradient, uses carrier proteins. Examples include root mineral uptake (active) and gas exchange in lungs (diffusion).\n</div>\n  ",
    "questions": [
      {
        "q": "Diffusion is defined as the movement of molecules from:",
        "o": [
          "Low to high concentration",
          "High to low concentration",
          "Equal to equal concentration",
          "Only across membranes"
        ],
        "a": 1,
        "e": "Diffusion is the passive movement of molecules from a region of higher concentration to a region of lower concentration (down the concentration gradient) until equilibrium is reached.",
        "h": "Diffusion: high → low concentration; passive (no energy needed).",
        "yr": "SS1"
      },
      {
        "q": "Osmosis specifically involves the movement of:",
        "o": ["Any solute", "Glucose molecules", "Water molecules through a selectively permeable membrane", "Ions against a gradient"],
        "a": 2,
        "e": "Osmosis is the diffusion of water molecules specifically through a selectively permeable membrane from a region of high water potential (low solute) to a region of low water potential (high solute).",
        "h": "Osmosis = water only, through selectively permeable membrane, passive.",
        "yr": "SS1"
      },
      {
        "q": "What happens to a red blood cell placed in a hypotonic solution (very dilute)?",
        "o": [
          "It shrinks (crenation)",
          "It swells and may burst (lysis)",
          "It remains unchanged",
          "It divides"
        ],
        "a": 1,
        "e": "A hypotonic solution has a lower solute concentration than the cell. Water moves into the cell by osmosis (from high water potential outside to lower water potential inside), causing the cell to swell and potentially burst — this is called lysis or haemolysis in red blood cells.",
        "h": "Hypotonic → water enters cell → swells → lysis (bursting).",
        "yr": "SS1"
      },
      {
        "q": "Active transport differs from diffusion in that active transport:",
        "o": [
          "Moves substances down the concentration gradient",
          "Requires no energy",
          "Moves substances against the concentration gradient using ATP and carrier proteins",
          "Only moves water molecules"
        ],
        "a": 2,
        "e": "Active transport moves molecules from low to high concentration (against the gradient) and requires metabolic energy (ATP) and specific carrier proteins in the membrane. Diffusion is passive and moves substances down the gradient.",
        "h": "Active transport: against gradient, requires ATP and carrier proteins.",
        "yr": "SS1"
      },
      {
        "q": "A plant cell placed in a concentrated salt solution (hypertonic) will:",
        "o": [
          "Become turgid",
          "Burst",
          "Plasmolyse (cell membrane pulls away from cell wall)",
          "Remain unchanged"
        ],
        "a": 2,
        "e": "In a hypertonic solution, water leaves the plant cell by osmosis. The cytoplasm shrinks and the cell membrane pulls away from the cell wall — this is called plasmolysis. The cell wall prevents bursting.",
        "h": "Hypertonic → water leaves plant cell → plasmolysis (membrane shrinks from wall).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is an example of diffusion in biology?",
        "o": [
          "Root hair cells absorbing mineral ions against a concentration gradient",
          "Oxygen entering the blood from alveoli in the lungs",
          "Sodium-potassium pump in nerve cells",
          "Glucose absorbed from the gut against the gradient"
        ],
        "a": 1,
        "e": "Oxygen entering the blood from alveoli is diffusion — O₂ moves from high concentration in the alveoli (just breathed in) to lower concentration in the blood. This is passive and requires no energy.",
        "h": "O₂ into blood from alveoli = diffusion (high → low O₂ concentration).",
        "yr": "SS1"
      },
      {
        "q": "Which solution would cause no net movement of water in or out of a cell?",
        "o": ["Hypotonic", "Hypertonic", "Isotonic", "Distilled water"],
        "a": 2,
        "e": "An isotonic solution has the same solute concentration as the cell. There is no net movement of water by osmosis because the water potential is equal on both sides of the membrane.",
        "h": "Isotonic = same concentration as cell = no net water movement.",
        "yr": "SS1"
      },
      {
        "q": "The absorption of mineral salts by root hair cells against their concentration gradient is an example of:",
        "o": ["Diffusion", "Osmosis", "Active transport", "Passive transport"],
        "a": 2,
        "e": "Root hair cells absorb mineral ions from the soil even when the soil concentration is lower than inside the root cell — this is against the gradient, requiring ATP (energy) and carrier proteins. This is active transport.",
        "h": "Mineral absorption by roots against gradient = active transport (requires ATP).",
        "yr": "SS1"
      },
      {
        "q": "Turgor pressure in plant cells is caused by:",
        "o": [
          "Excess glucose in the vacuole",
          "Water entering the cell by osmosis, pushing the cell membrane against the cell wall",
          "Active transport of ions out of the cell",
          "Carbon dioxide accumulation in the cytoplasm"
        ],
        "a": 1,
        "e": "Turgor pressure results from water entering the plant cell by osmosis in a dilute environment. The vacuole fills with water and pushes the cell membrane outward against the rigid cell wall. This makes the cell firm (turgid) and helps support the plant.",
        "h": "Turgor = water enters cell by osmosis → pushes against cell wall → cell becomes firm.",
        "yr": "SS1"
      },
      {
        "q": "Which factor INCREASES the rate of diffusion?",
        "o": [
          "Decreased concentration gradient",
          "Lower temperature",
          "Increased concentration gradient",
          "Thicker membrane"
        ],
        "a": 2,
        "e": "Diffusion rate increases with: greater concentration gradient, higher temperature (more kinetic energy), larger surface area, thinner membrane, and smaller molecular size. A steeper gradient means more molecules are available to move.",
        "h": "Diffusion rate ↑ with steeper concentration gradient, higher temperature, larger surface area.",
        "yr": "SS1"
      },
      {
        "q": "A selectively permeable membrane allows:",
        "o": [
          "Only large molecules through",
          "No substances through",
          "Some substances through but not others based on size, charge, and solubility",
          "All substances through equally"
        ],
        "a": 2,
        "e": "A selectively (semi) permeable membrane allows certain molecules to pass while restricting others, based on factors like molecular size, electrical charge, and lipid solubility. Water and small non-polar molecules pass easily; large or charged molecules require transport proteins.",
        "h": "Selectively permeable = allows some substances through, restricts others.",
        "yr": "SS1"
      },
      {
        "q": "What provides the energy for active transport?",
        "o": ["Glucose directly", "ATP (adenosine triphosphate)", "Sunlight", "ADP"],
        "a": 1,
        "e": "Active transport requires ATP (adenosine triphosphate) — the cell's energy currency produced by respiration. ATP powers the carrier proteins (pumps) that move molecules against their concentration gradient.",
        "h": "Active transport energy source = ATP (from cellular respiration).",
        "yr": "SS1"
      },
      {
        "q": "The sodium-potassium pump in nerve cells is an example of:",
        "o": ["Diffusion", "Osmosis", "Active transport", "Facilitated diffusion"],
        "a": 2,
        "e": "The sodium-potassium pump moves Na⁺ out and K⁺ into the cell against their concentration gradients, using ATP. This is active transport — energy-requiring movement against the gradient — and is vital for nerve impulse transmission.",
        "h": "Sodium-potassium pump = active transport (uses ATP, against gradient).",
        "yr": "SS1"
      },
      {
        "q": "When a piece of dried raisin is placed in water, it swells. This demonstrates:",
        "o": ["Diffusion of sugar", "Active transport", "Osmosis (water entering the raisin)", "Plasmolysis"],
        "a": 2,
        "e": "The raisin has a higher solute concentration than the water around it. Water moves into the raisin by osmosis (from high water potential outside to low water potential inside) through the raisin's membranes, causing it to swell.",
        "h": "Raisin swells in water = osmosis (water enters from dilute surrounding into concentrated raisin).",
        "yr": "SS1"
      },
      {
        "q": "Carbon dioxide produced in body cells moves into the blood because:",
        "o": [
          "Active transport pumps it out",
          "The concentration of CO₂ is higher in cells than in blood, so it diffuses out",
          "Osmosis moves it out",
          "Carrier proteins actively pump it"
        ],
        "a": 1,
        "e": "CO₂ produced in respiring cells is at a higher concentration inside cells than in the blood. It diffuses (passive, no energy needed) from the high concentration in cells to the lower concentration in the blood.",
        "h": "CO₂ leaves cells by diffusion (high concentration in cells → lower in blood).",
        "yr": "SS1"
      },
      {
        "q": "Plasmolysis in plant cells refers to:",
        "o": [
          "The cell swelling due to water uptake",
          "The cell wall dissolving",
          "The cell membrane shrinking away from the cell wall due to water loss",
          "Cell division"
        ],
        "a": 2,
        "e": "Plasmolysis occurs when a plant cell loses water by osmosis in a hypertonic solution. The cytoplasm shrinks and the cell membrane pulls away from the cell wall. The cell becomes flaccid.",
        "h": "Plasmolysis = water loss from plant cell → membrane pulls away from wall.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following substances primarily enters cells by osmosis?",
        "o": ["Oxygen", "Glucose", "Water", "Sodium ions"],
        "a": 2,
        "e": "Osmosis specifically refers to the movement of water molecules through a selectively permeable membrane. Oxygen enters by diffusion, glucose by facilitated diffusion or active transport, and sodium ions by active transport or ion channels.",
        "h": "Osmosis = water molecules only.",
        "yr": "SS1"
      },
      {
        "q": "Which process requires carrier proteins AND energy?",
        "o": ["Simple diffusion", "Osmosis", "Facilitated diffusion", "Active transport"],
        "a": 3,
        "e": "Active transport requires both carrier proteins (to move molecules across the membrane) and ATP energy (because movement is against the concentration gradient). Facilitated diffusion uses carrier proteins but no energy; simple diffusion and osmosis need neither.",
        "h": "Active transport = carrier proteins + ATP energy.",
        "yr": "SS1"
      },
      {
        "q": "If a plant wilts, which process has occurred at the cellular level?",
        "o": [
          "Turgor pressure increased",
          "Water entered cells by osmosis",
          "Water left cells by osmosis, reducing turgor pressure",
          "Active transport increased ion concentration"
        ],
        "a": 2,
        "e": "Wilting occurs when plant cells lose water by osmosis (e.g., in drought conditions or in a hypertonic environment). As turgor pressure decreases, cells become flaccid and the plant droops.",
        "h": "Wilting = water loss by osmosis → reduced turgor → flaccid cells.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following best explains why glucose is absorbed from the small intestine even when its concentration inside the intestinal cell is already high?",
        "o": [
          "Diffusion",
          "Osmosis",
          "Active transport against the concentration gradient using ATP",
          "The glucose moves by gravity"
        ],
        "a": 2,
        "e": "Glucose absorption from the gut into intestinal cells occurs by active transport — the cells use ATP to pump glucose against the concentration gradient (from lower concentration in the gut lumen to higher concentration inside the cells).",
        "h": "Glucose absorbed against gradient in gut = active transport (ATP-powered).",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Properties and Functions of the Cell",
    "topicCode": "SS1-BIO-06",
    "module": "Cell Biology",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  Every living cell carries out a set of fundamental life processes: <span class=\"learn-keyword\">nutrition</span> (obtaining energy-containing substances), <span class=\"learn-keyword\">respiration</span> (releasing energy from food), <span class=\"learn-keyword\">growth</span> (increasing in size), and <span class=\"learn-keyword\">irritability</span> (responding to stimuli). These properties define the cell as a living unit.\n</div>\n\n<h3 class=\"learn-subheading\">1. Nutrition</h3>\n<p class=\"learn-p\">Nutrition is the process by which cells obtain energy-containing substances (food) needed for metabolic activities.</p>\n<ul class=\"learn-list\">\n  <li><strong>Autotrophic nutrition:</strong> Cells make their own food using light (photosynthesis) or chemical energy (chemosynthesis) — e.g., plant cells with chloroplasts</li>\n  <li><strong>Heterotrophic nutrition:</strong> Cells obtain energy by consuming other organisms — includes holozoic (ingestion), saprophytic (absorption), and parasitic nutrition</li>\n  <li>Nutrients obtained include: carbohydrates, proteins, lipids, minerals, vitamins, and water</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Respiration at the Cellular Level</h3>\n<p class=\"learn-p\">Cellular respiration releases energy from glucose. It occurs in the cytoplasm (glycolysis) and mitochondria (Krebs cycle and oxidative phosphorylation).</p>\n<ul class=\"learn-list\">\n  <li><strong>Aerobic:</strong> C₆H₁₂O₆ + 6O₂ → 6CO₂ + 6H₂O + ATP (large amount of ATP)</li>\n  <li><strong>Anaerobic (in animals/yeast):</strong> Glucose → Lactic acid + small ATP (animals) or Ethanol + CO₂ + small ATP (yeast)</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Growth</h3>\n<p class=\"learn-p\">Cells grow by:</p>\n<ul class=\"learn-list\">\n  <li><strong>Cell division (mitosis):</strong> One cell divides into two identical daughter cells — increases cell number</li>\n  <li><strong>Cell enlargement:</strong> Cells take in more water and synthesize more cytoplasm — increases cell size</li>\n  <li><strong>Cell differentiation:</strong> Cells become specialized for specific functions</li>\n</ul>\n\n<h3 class=\"learn-subheading\">4. Irritability (Sensitivity)</h3>\n<p class=\"learn-p\">Irritability is the ability of a cell to detect and respond to changes (stimuli) in its environment.</p>\n<ul class=\"learn-list\">\n  <li>Nerve cells detect electrical and chemical signals</li>\n  <li>Muscle cells respond to nerve impulses by contracting</li>\n  <li>Plant cells respond to light (phototropism), gravity (geotropism), and water (hydrotropism)</li>\n  <li>Unicellular organisms like Amoeba respond to chemicals (chemotaxis) and light (phototaxis)</li>\n</ul>\n\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Property</th><th>Definition</th><th>Cellular Example</th></tr></thead>\n    <tbody>\n      <tr><td>Nutrition</td><td>Obtaining food/energy</td><td>Chloroplast photosynthesis; glucose absorption</td></tr>\n      <tr><td>Respiration</td><td>Releasing energy from food</td><td>Mitochondria producing ATP</td></tr>\n      <tr><td>Growth</td><td>Increase in size/number</td><td>Cell division (mitosis); cell enlargement</td></tr>\n      <tr><td>Irritability</td><td>Response to stimuli</td><td>Nerve impulse; phototropism</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Distinguish between nutrition (obtaining food) and respiration (releasing energy from food). Nutrition provides the raw materials; respiration extracts the energy. Both are essential cellular properties.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> The four key properties of a living cell are nutrition, respiration, growth, and irritability. Autotrophic cells make their own food; heterotrophic cells consume others. Respiration releases ATP from glucose in mitochondria. Growth involves cell division (mitosis) and enlargement. Irritability enables cells to respond to environmental stimuli.\n</div>\n  ",
    "questions": [
      {
        "q": "Which cellular organelle is directly responsible for autotrophic nutrition in plant cells?",
        "o": ["Mitochondrion", "Ribosome", "Chloroplast", "Vacuole"],
        "a": 2,
        "e": "The chloroplast is the organelle responsible for photosynthesis — the autotrophic process by which plant cells use light energy, CO₂, and water to produce glucose. It contains the green pigment chlorophyll.",
        "h": "Chloroplast = site of photosynthesis = autotrophic nutrition in plants.",
        "yr": "SS1"
      },
      {
        "q": "Which organelle is the main site of cellular respiration (ATP production)?",
        "o": ["Chloroplast", "Nucleus", "Mitochondrion", "Ribosome"],
        "a": 2,
        "e": "The mitochondrion is the main site of aerobic cellular respiration, where glucose is fully oxidized and large amounts of ATP are produced through the Krebs cycle and oxidative phosphorylation.",
        "h": "Mitochondrion = ATP production (cellular respiration).",
        "yr": "SS1"
      },
      {
        "q": "Cell growth occurs mainly through:",
        "o": [
          "Respiration only",
          "Cell division (mitosis) and cell enlargement",
          "Osmosis only",
          "Active transport"
        ],
        "a": 1,
        "e": "Cell growth occurs by: (1) cell division — mitosis produces two identical daughter cells increasing cell number, and (2) cell enlargement — cells increase in size by taking in more materials and synthesizing more cytoplasm.",
        "h": "Growth = cell division (mitosis) + cell enlargement.",
        "yr": "SS1"
      },
      {
        "q": "Which type of nutrition involves obtaining food by absorbing nutrients from dead organic matter?",
        "o": ["Holozoic", "Autotrophic", "Parasitic", "Saprophytic"],
        "a": 3,
        "e": "Saprophytic nutrition involves secreting enzymes onto dead organic matter, breaking it down, and absorbing the soluble products. Fungi are the classic example. This is different from parasitism (living off a living host) and holozoic nutrition (ingesting food).",
        "h": "Saprophytic = absorbing nutrients from dead organic matter (e.g., fungi).",
        "yr": "SS1"
      },
      {
        "q": "Irritability at the cellular level means:",
        "o": [
          "The cell is angry",
          "The cell can detect and respond to stimuli in its environment",
          "The cell is producing too much CO₂",
          "The cell wall is breaking down"
        ],
        "a": 1,
        "e": "Irritability (sensitivity) is the ability of a cell to detect changes (stimuli) in its environment and produce an appropriate response. Nerve cells, muscle cells, and even plant cells all show irritability.",
        "h": "Irritability = detecting stimuli + producing appropriate response.",
        "yr": "SS1"
      },
      {
        "q": "The overall equation for aerobic respiration is:",
        "o": [
          "CO₂ + H₂O → C₆H₁₂O₆ + O₂",
          "C₆H₁₂O₆ + 6O₂ → 6CO₂ + 6H₂O + ATP",
          "C₆H₁₂O₆ → lactic acid + CO₂",
          "6CO₂ + 6H₂O + light → C₆H₁₂O₆ + 6O₂"
        ],
        "a": 1,
        "e": "Aerobic respiration: C₆H₁₂O₆ + 6O₂ → 6CO₂ + 6H₂O + ATP. Glucose is fully oxidized using oxygen, releasing maximum ATP (about 36–38 molecules per glucose). The last option is photosynthesis, not respiration.",
        "h": "Aerobic respiration: glucose + O₂ → CO₂ + H₂O + ATP.",
        "yr": "SS1"
      },
      {
        "q": "The process by which a cell takes in its own damaged organelles and digests them is called:",
        "o": ["Respiration", "Autophagy (self-digestion by lysosomes)", "Osmosis", "Differentiation"],
        "a": 1,
        "e": "Autophagy is the cellular process where lysosomes engulf and digest old, damaged, or excess organelles. This is part of cellular housekeeping and is essential for cell survival and renewal.",
        "h": "Autophagy = lysosomes digest old organelles (cell self-cleaning).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is an example of irritability in an animal cell?",
        "o": [
          "A leaf turning green in sunlight",
          "A nerve cell transmitting an impulse in response to a pin prick",
          "A root growing downward",
          "Yeast cells fermenting sugar"
        ],
        "a": 1,
        "e": "A nerve cell transmitting an electrical impulse in response to a pin prick is irritability — the cell detects a stimulus (pain/pressure) and generates a response (action potential/impulse). This is a clear example of cellular sensitivity.",
        "h": "Nerve cell responding to stimulus = irritability (sensitivity) at cell level.",
        "yr": "SS1"
      },
      {
        "q": "Mitosis results in:",
        "o": [
          "Two cells with half the chromosome number",
          "Two genetically identical daughter cells with the same chromosome number as the parent",
          "Four cells with half the chromosomes",
          "One cell with double the chromosomes"
        ],
        "a": 1,
        "e": "Mitosis produces two daughter cells that are genetically identical to the parent cell and have the same chromosome number (diploid). It is involved in growth, repair, and asexual reproduction. Meiosis produces four cells with half the chromosomes.",
        "h": "Mitosis → 2 identical daughter cells, same chromosome number as parent.",
        "yr": "SS1"
      },
      {
        "q": "Heterotrophic nutrition differs from autotrophic nutrition in that heterotrophs:",
        "o": [
          "Make their own food from inorganic substances",
          "Obtain energy by consuming pre-formed organic substances from other organisms",
          "Only feed on dead matter",
          "Use sunlight directly for energy"
        ],
        "a": 1,
        "e": "Heterotrophs cannot make their own food — they must obtain pre-formed organic matter from other organisms. This includes animals (holozoic), fungi (saprophytic), and parasites. Autotrophs (plants) make their own food from inorganic substances.",
        "h": "Heterotrophs = cannot make own food, must consume others.",
        "yr": "SS1"
      },
      {
        "q": "Anaerobic respiration in yeast produces:",
        "o": [
          "Lactic acid and CO₂",
          "Ethanol (alcohol) and CO₂",
          "Only ATP",
          "Water and CO₂"
        ],
        "a": 1,
        "e": "In yeast (and other fungi), anaerobic respiration produces ethanol (alcohol) and CO₂ as by-products, along with a small amount of ATP. This is used in brewing, wine-making, and bread-making.",
        "h": "Yeast anaerobic respiration → ethanol + CO₂ (fermentation).",
        "yr": "SS1"
      },
      {
        "q": "Cell differentiation refers to:",
        "o": [
          "Cells dividing rapidly",
          "Cells becoming specialized for specific functions",
          "Cells taking in more water",
          "Cells dying"
        ],
        "a": 1,
        "e": "Cell differentiation is the process by which cells develop specific structures and functions to perform specialized roles. For example, a stem cell can differentiate into a nerve cell, muscle cell, or red blood cell.",
        "h": "Differentiation = cells becoming specialized for specific functions.",
        "yr": "SS1"
      },
      {
        "q": "During strenuous exercise, muscle cells may respire anaerobically. The product formed in human muscle cells is:",
        "o": ["Ethanol", "CO₂ only", "Lactic acid", "Glycogen"],
        "a": 2,
        "e": "In human muscle cells under oxygen-deficient conditions (anaerobic), glucose is partially broken down to lactic acid. The buildup of lactic acid causes muscle fatigue and cramps. Unlike yeast, human cells do not produce ethanol.",
        "h": "Anaerobic respiration in human muscles → lactic acid (causes cramps).",
        "yr": "SS1"
      },
      {
        "q": "The first stage of cellular respiration (glycolysis) occurs in the:",
        "o": ["Mitochondria", "Nucleus", "Cytoplasm", "Chloroplast"],
        "a": 2,
        "e": "Glycolysis — the first stage of cellular respiration where glucose (6C) is broken down to pyruvate (3C) — occurs in the cytoplasm. It produces a small amount of ATP and NADH. It can occur with or without oxygen.",
        "h": "Glycolysis = first stage of respiration, occurs in the cytoplasm.",
        "yr": "SS1"
      },
      {
        "q": "Holozoic nutrition involves:",
        "o": [
          "Absorbing nutrients from dead matter",
          "Making food by photosynthesis",
          "Ingesting, digesting, absorbing, and assimilating solid food",
          "Feeding on a living host"
        ],
        "a": 2,
        "e": "Holozoic nutrition involves: ingestion (taking in food), digestion (breaking it down), absorption (taking up nutrients), assimilation (using nutrients in metabolism), and egestion (expelling undigested matter). This is typical of most animals.",
        "h": "Holozoic = ingestion → digestion → absorption → assimilation (animals).",
        "yr": "SS1"
      },
      {
        "q": "Which property of cells ensures that the organism can repair damaged or worn-out tissues?",
        "o": ["Irritability", "Nutrition", "Growth (through cell division)", "Excretion"],
        "a": 2,
        "e": "Repair of tissues depends on cell growth — specifically mitosis (cell division) that produces new cells to replace damaged or dead ones. Skin cells and red blood cells, for example, are constantly replaced by mitosis.",
        "h": "Tissue repair = cell division (mitosis) producing new cells.",
        "yr": "SS1"
      },
      {
        "q": "Photosynthesis in plant cells is the basis of which cellular property?",
        "o": ["Irritability", "Autotrophic nutrition", "Excretion", "Heterotrophic nutrition"],
        "a": 1,
        "e": "Photosynthesis is the mechanism of autotrophic nutrition in plant cells — plants use sunlight, CO₂, and water to manufacture their own food (glucose). This is the cellular basis of autotrophy.",
        "h": "Photosynthesis = autotrophic nutrition in plant cells.",
        "yr": "SS1"
      },
      {
        "q": "Which statement is TRUE about ATP?",
        "o": [
          "ATP is only produced by plant cells",
          "ATP is the universal energy currency of cells, produced mainly by respiration",
          "ATP is only produced during anaerobic respiration",
          "ATP stores genetic information"
        ],
        "a": 1,
        "e": "ATP (adenosine triphosphate) is the universal energy currency of all cells. It is produced mainly through aerobic respiration in mitochondria. All life processes requiring energy (active transport, muscle contraction, protein synthesis) use ATP.",
        "h": "ATP = universal energy currency, produced by respiration, used by all cells.",
        "yr": "SS1"
      },
      {
        "q": "When a single-celled organism like Amoeba moves toward a food particle (chemotaxis), this demonstrates:",
        "o": ["Growth", "Nutrition", "Irritability (sensitivity)", "Reproduction"],
        "a": 2,
        "e": "Chemotaxis — Amoeba moving toward food chemicals — is a demonstration of irritability (sensitivity) at the cellular level. The Amoeba detects chemical stimuli and responds by moving toward the food source.",
        "h": "Amoeba moving toward food = chemotaxis = irritability.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following best explains why cells need nutrition?",
        "o": [
          "To increase the temperature of the body only",
          "To provide raw materials and energy for all life processes including growth, repair, and reproduction",
          "Only to produce carbon dioxide",
          "To enable the cell to excrete waste"
        ],
        "a": 1,
        "e": "Nutrition provides cells with (1) energy for all life processes and (2) raw materials (building blocks) for growth, repair, enzyme synthesis, and reproduction. Without nutrition, cells cannot carry out any of their functions.",
        "h": "Nutrition provides energy AND raw materials for all cellular functions.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Respiration",
    "topicCode": "SS1-BIO-07",
    "module": "Cell Biology",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Respiration</span> is the chemical process by which living cells break down organic molecules (mainly glucose) to release energy in the form of <span class=\"learn-keyword\">ATP</span>. It occurs in every living cell and must not be confused with breathing (ventilation), which is the mechanical process of moving air in and out of the lungs.\n</div>\n\n<h3 class=\"learn-subheading\">1. Aerobic Respiration</h3>\n<p class=\"learn-p\">Aerobic respiration requires oxygen and releases a large amount of energy (ATP). It occurs in two main stages:</p>\n<p class=\"learn-p\"><strong>Stage 1 — Glycolysis</strong> (in cytoplasm): Glucose (6C) → 2 pyruvate (3C) + small ATP + NADH</p>\n<p class=\"learn-p\"><strong>Stage 2 — Krebs Cycle + Oxidative Phosphorylation</strong> (in mitochondria): Pyruvate is fully oxidized to CO₂ + H₂O, releasing large amounts of ATP</p>\n<p class=\"learn-p\"><strong>Overall equation:</strong></p>\n<p class=\"learn-p\" style=\"text-align:center;\"><strong>C₆H₁₂O₆ + 6O₂ → 6CO₂ + 6H₂O + ~36–38 ATP</strong></p>\n<p class=\"learn-p\"><strong>Sites:</strong> Cytoplasm (glycolysis) and Mitochondria (Krebs cycle and ETC)</p>\n\n<h3 class=\"learn-subheading\">2. Anaerobic Respiration</h3>\n<p class=\"learn-p\">Anaerobic respiration occurs <strong>without oxygen</strong> and releases much less energy. Two types:</p>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Organism</th><th>Products</th><th>Equation</th></tr></thead>\n    <tbody>\n      <tr><td>Animals (muscle cells)</td><td>Lactic acid + small ATP</td><td>Glucose → 2 lactic acid + 2 ATP</td></tr>\n      <tr><td>Yeast/microorganisms</td><td>Ethanol + CO₂ + small ATP</td><td>Glucose → 2 ethanol + 2CO₂ + 2 ATP</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">3. Comparison: Aerobic vs Anaerobic Respiration</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Feature</th><th>Aerobic</th><th>Anaerobic</th></tr></thead>\n    <tbody>\n      <tr><td>Oxygen required</td><td>Yes</td><td>No</td></tr>\n      <tr><td>ATP yield</td><td>36–38 ATP per glucose</td><td>2 ATP per glucose</td></tr>\n      <tr><td>Products</td><td>CO₂ + H₂O</td><td>Lactic acid (animals) or ethanol + CO₂ (yeast)</td></tr>\n      <tr><td>Site</td><td>Cytoplasm + Mitochondria</td><td>Cytoplasm only</td></tr>\n      <tr><td>Duration</td><td>Sustained</td><td>Short-term only</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">4. Practical Importance of Respiration</h3>\n<ul class=\"learn-list\">\n  <li><strong>Fermentation (yeast anaerobic):</strong> Bread-making (CO₂ makes dough rise); brewing beer and wine (ethanol)</li>\n  <li><strong>Muscle fatigue:</strong> Lactic acid accumulation during intense exercise → oxygen debt</li>\n  <li><strong>Germination:</strong> Seeds respire rapidly to provide energy for growth</li>\n  <li><strong>Body temperature:</strong> Respiration releases heat, helping warm-blooded animals maintain body temperature</li>\n</ul>\n\n<div class=\"learn-svg-wrap\">\n  <svg class=\"learn-svg\" viewBox=\"0 0 480 190\" xmlns=\"http://www.w3.org/2000/svg\">\n    <rect width=\"480\" height=\"190\" fill=\"#0f0020\" rx=\"10\"/>\n    <text x=\"240\" y=\"18\" text-anchor=\"middle\" fill=\"#FFC53D\" font-size=\"11\" font-weight=\"bold\">Respiration Summary</text>\n    <rect x=\"15\" y=\"28\" width=\"215\" height=\"145\" rx=\"8\" fill=\"#1B0A47\" stroke=\"#28c840\" stroke-width=\"1.5\"/>\n    <text x=\"122\" y=\"46\" text-anchor=\"middle\" fill=\"#28c840\" font-size=\"10\" font-weight=\"bold\">AEROBIC</text>\n    <text x=\"122\" y=\"62\" text-anchor=\"middle\" fill=\"#c8c8c8\" font-size=\"8\">Glucose + O₂ → CO₂ + H₂O + ATP</text>\n    <text x=\"122\" y=\"76\" text-anchor=\"middle\" fill=\"#FFC53D\" font-size=\"8\">Yield: ~36–38 ATP per glucose</text>\n    <text x=\"122\" y=\"90\" text-anchor=\"middle\" fill=\"#c8c8c8\" font-size=\"8\">Stage 1: Glycolysis (cytoplasm)</text>\n    <text x=\"122\" y=\"104\" text-anchor=\"middle\" fill=\"#c8c8c8\" font-size=\"8\">Stage 2: Krebs (mitochondria)</text>\n    <text x=\"122\" y=\"118\" text-anchor=\"middle\" fill=\"#c8c8c8\" font-size=\"8\">Stage 3: ETC (mitochondria)</text>\n    <text x=\"122\" y=\"135\" text-anchor=\"middle\" fill=\"#28c840\" font-size=\"8\">Complete oxidation of glucose</text>\n    <rect x=\"250\" y=\"28\" width=\"215\" height=\"145\" rx=\"8\" fill=\"#1B0A47\" stroke=\"#c8b4f0\" stroke-width=\"1.5\"/>\n    <text x=\"357\" y=\"46\" text-anchor=\"middle\" fill=\"#c8b4f0\" font-size=\"10\" font-weight=\"bold\">ANAEROBIC</text>\n    <text x=\"357\" y=\"62\" text-anchor=\"middle\" fill=\"#c8c8c8\" font-size=\"8\">No oxygen required</text>\n    <text x=\"357\" y=\"76\" text-anchor=\"middle\" fill=\"#FFC53D\" font-size=\"8\">Yield: ~2 ATP per glucose (low)</text>\n    <text x=\"357\" y=\"90\" text-anchor=\"middle\" fill=\"#c8c8c8\" font-size=\"8\">Animals: glucose → lactic acid</text>\n    <text x=\"357\" y=\"104\" text-anchor=\"middle\" fill=\"#c8c8c8\" font-size=\"8\">Yeast: glucose → ethanol + CO₂</text>\n    <text x=\"357\" y=\"118\" text-anchor=\"middle\" fill=\"#c8c8c8\" font-size=\"8\">Only cytoplasm involved</text>\n    <text x=\"357\" y=\"135\" text-anchor=\"middle\" fill=\"#c8b4f0\" font-size=\"8\">Used in brewing, bread-making</text>\n  </svg>\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Remember: respiration is NOT the same as breathing. Respiration = chemical release of energy from food in EVERY cell. Breathing = mechanical movement of air into/out of lungs. Aerobic respiration produces FAR more ATP (36–38) than anaerobic (2 ATP).\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Aerobic respiration: C₆H₁₂O₆ + 6O₂ → 6CO₂ + 6H₂O + 36–38 ATP (cytoplasm + mitochondria). Anaerobic respiration: glucose → lactic acid (animals) or ethanol + CO₂ (yeast) + 2 ATP (cytoplasm only). Aerobic yields far more ATP. Fermentation (yeast anaerobic) is used in brewing and bread-making.\n</div>\n  ",
    "questions": [
      {
        "q": "Respiration is best defined as:",
        "o": [
          "The process of breathing air in and out of the lungs",
          "The chemical breakdown of organic molecules in cells to release energy (ATP)",
          "The exchange of gases through the skin",
          "The process of photosynthesis in reverse"
        ],
        "a": 1,
        "e": "Respiration is the chemical process occurring in all living cells where organic molecules (mainly glucose) are oxidized to release energy stored as ATP. It is NOT the same as breathing (ventilation), which is mechanical.",
        "h": "Respiration = chemical release of energy from food in cells (NOT breathing).",
        "yr": "SS1"
      },
      {
        "q": "The overall equation for aerobic respiration is:",
        "o": [
          "6CO₂ + 6H₂O + light → C₆H₁₂O₆ + 6O₂",
          "C₆H₁₂O₆ → 2 lactic acid + 2 ATP",
          "C₆H₁₂O₆ + 6O₂ → 6CO₂ + 6H₂O + ATP",
          "C₆H₁₂O₆ → 2 ethanol + 2CO₂ + ATP"
        ],
        "a": 2,
        "e": "Aerobic respiration: C₆H₁₂O₆ + 6O₂ → 6CO₂ + 6H₂O + 36–38 ATP. Option A is photosynthesis. Options B and D are anaerobic respiration.",
        "h": "Aerobic: glucose + O₂ → CO₂ + H₂O + lots of ATP.",
        "yr": "SS1"
      },
      {
        "q": "Anaerobic respiration in yeast is used in bread-making because:",
        "o": [
          "Yeast produces oxygen which makes the bread rise",
          "Yeast produces CO₂ which causes the dough to rise, and ethanol which evaporates during baking",
          "Yeast produces lactic acid which flavors the bread",
          "Yeast breaks down gluten in the flour"
        ],
        "a": 1,
        "e": "During bread-making, yeast carries out anaerobic fermentation: glucose → ethanol + CO₂ + ATP. The CO₂ bubbles get trapped in the dough, causing it to rise. During baking, the ethanol evaporates and the CO₂ gives the bread its airy texture.",
        "h": "Yeast fermentation → CO₂ (dough rises) + ethanol (evaporates in baking).",
        "yr": "SS1"
      },
      {
        "q": "The first stage of cellular respiration, glycolysis, occurs in the:",
        "o": ["Mitochondria", "Nucleus", "Cytoplasm", "Chloroplast"],
        "a": 2,
        "e": "Glycolysis (splitting of glucose) is the first stage of both aerobic and anaerobic respiration. It occurs in the cytoplasm and converts glucose (6C) to two molecules of pyruvate (3C), producing a net gain of 2 ATP.",
        "h": "Glycolysis = first stage of respiration = occurs in the CYTOPLASM.",
        "yr": "SS1"
      },
      {
        "q": "How much ATP is produced per glucose molecule during aerobic respiration compared to anaerobic?",
        "o": [
          "Same amount (2 ATP each)",
          "Aerobic: 36–38 ATP; Anaerobic: 2 ATP",
          "Aerobic: 2 ATP; Anaerobic: 36–38 ATP",
          "Aerobic: 18 ATP; Anaerobic: 9 ATP"
        ],
        "a": 1,
        "e": "Aerobic respiration produces ~36–38 ATP per glucose (complete oxidation). Anaerobic respiration produces only 2 ATP per glucose (incomplete oxidation). Aerobic respiration is much more efficient.",
        "h": "Aerobic = 36–38 ATP; Anaerobic = 2 ATP per glucose.",
        "yr": "SS1"
      },
      {
        "q": "Muscle fatigue during intense exercise is caused by the accumulation of:",
        "o": ["CO₂", "Oxygen", "Lactic acid", "Ethanol"],
        "a": 2,
        "e": "During intense exercise, muscles may not receive enough oxygen and resort to anaerobic respiration. The lactic acid produced accumulates in muscles, lowering pH and causing the sensation of fatigue and muscle cramps.",
        "h": "Muscle fatigue = lactic acid buildup from anaerobic respiration in muscles.",
        "yr": "SS1"
      },
      {
        "q": "Which organelle carries out the Krebs cycle and electron transport chain?",
        "o": ["Ribosome", "Chloroplast", "Mitochondrion", "Golgi apparatus"],
        "a": 2,
        "e": "The Krebs cycle and electron transport chain (oxidative phosphorylation) — the main ATP-producing stages of aerobic respiration — occur in the mitochondrion (matrix and inner membrane respectively).",
        "h": "Mitochondrion = site of Krebs cycle + ETC (ATP production).",
        "yr": "SS1"
      },
      {
        "q": "Oxygen debt after vigorous exercise refers to:",
        "o": [
          "The oxygen used during exercise",
          "The extra oxygen needed after exercise to break down accumulated lactic acid",
          "The oxygen stored in muscles",
          "The CO₂ produced during exercise"
        ],
        "a": 1,
        "e": "Oxygen debt is the extra oxygen needed after intense exercise to oxidize the lactic acid that accumulated during anaerobic respiration. The liver converts lactic acid back to glucose or oxidizes it completely, requiring the 'extra' oxygen.",
        "h": "Oxygen debt = extra O₂ needed after exercise to remove accumulated lactic acid.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a product of BOTH aerobic and anaerobic respiration?",
        "o": ["Water", "CO₂", "ATP", "Lactic acid"],
        "a": 2,
        "e": "ATP is produced in both aerobic (36–38 ATP) and anaerobic (2 ATP) respiration. Water is only produced aerobically; CO₂ is produced aerobically and in yeast fermentation (not in animal anaerobic); lactic acid is only in animal anaerobic.",
        "h": "ATP is produced in BOTH aerobic and anaerobic respiration.",
        "yr": "SS1"
      },
      {
        "q": "Fermentation is the term used to describe:",
        "o": [
          "Aerobic respiration in animals",
          "Anaerobic respiration in microorganisms like yeast",
          "Photosynthesis in algae",
          "Decomposition of proteins by bacteria"
        ],
        "a": 1,
        "e": "Fermentation specifically refers to anaerobic respiration in microorganisms (like yeast and bacteria), where glucose is incompletely broken down to produce ethanol + CO₂ (yeast) or lactic acid (bacteria). It is widely used in food and beverage production.",
        "h": "Fermentation = anaerobic respiration in microorganisms (yeast/bacteria).",
        "yr": "SS1"
      },
      {
        "q": "Which gas is consumed in aerobic respiration and must be continuously supplied to cells?",
        "o": ["Carbon dioxide", "Nitrogen", "Oxygen", "Hydrogen"],
        "a": 2,
        "e": "Aerobic respiration requires oxygen as the final electron acceptor. Oxygen is combined with hydrogen at the end of the electron transport chain to form water. Without oxygen, aerobic respiration cannot proceed.",
        "h": "Aerobic respiration consumes O₂ (final electron acceptor).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following occurs in the CYTOPLASM during respiration?",
        "o": ["Krebs cycle", "Electron transport chain", "Glycolysis", "Oxidative phosphorylation"],
        "a": 2,
        "e": "Glycolysis (splitting of glucose to pyruvate) is the only stage of respiration that occurs in the cytoplasm. The Krebs cycle, electron transport chain, and oxidative phosphorylation all occur in the mitochondria.",
        "h": "Cytoplasm = glycolysis only; mitochondria = Krebs cycle + ETC.",
        "yr": "SS1"
      },
      {
        "q": "CO₂ is a by-product of aerobic respiration. In the body, CO₂ is:",
        "o": [
          "Stored in fat cells",
          "Used to make glucose",
          "Transported in blood to the lungs and exhaled",
          "Converted to oxygen in the liver"
        ],
        "a": 2,
        "e": "CO₂ produced in respiring cells is a metabolic waste product. It dissolves in the blood (carried mainly as bicarbonate ions), is transported to the lungs, and is expelled during exhalation. Exhaling CO₂ is also a form of excretion.",
        "h": "CO₂ from respiration → blood → lungs → exhaled.",
        "yr": "SS1"
      },
      {
        "q": "Seeds germinating in a sealed container would eventually stop growing because:",
        "o": [
          "They run out of water",
          "The oxygen is used up by aerobic respiration and CO₂ accumulates",
          "Light is absent",
          "The temperature drops"
        ],
        "a": 1,
        "e": "Germinating seeds respire aerobically at a high rate. In a sealed container, they would deplete the available oxygen and accumulate CO₂. Without oxygen, aerobic respiration cannot continue and the seeds would die.",
        "h": "Germinating seeds deplete O₂ by aerobic respiration in a sealed container.",
        "yr": "SS1"
      },
      {
        "q": "Which statement about anaerobic respiration is FALSE?",
        "o": [
          "It does not require oxygen",
          "It produces less ATP than aerobic respiration",
          "In animals, it produces lactic acid",
          "It occurs in the mitochondria"
        ],
        "a": 3,
        "e": "Anaerobic respiration occurs in the cytoplasm, not the mitochondria. It only involves glycolysis (cytoplasm). The mitochondria are the site of aerobic stages (Krebs cycle, ETC). The other statements about anaerobic respiration are true.",
        "h": "Anaerobic respiration occurs in the CYTOPLASM (not mitochondria).",
        "yr": "SS1"
      },
      {
        "q": "Aerobic respiration is the preferred metabolic pathway in most cells because:",
        "o": [
          "It is faster than anaerobic",
          "It completely oxidizes glucose and produces far more ATP (36–38) than anaerobic (2 ATP)",
          "It produces useful by-products like ethanol",
          "It does not require enzymes"
        ],
        "a": 1,
        "e": "Aerobic respiration is preferred because it completely oxidizes glucose, extracting maximum energy as ATP (~36–38 per glucose). Anaerobic produces only 2 ATP and leaves toxic by-products (lactic acid). Most cells use aerobic respiration whenever oxygen is available.",
        "h": "Aerobic preferred: produces 36–38 ATP vs 2 ATP in anaerobic, complete oxidation.",
        "yr": "SS1"
      },
      {
        "q": "The process of brewing palm wine involves:",
        "o": [
          "Aerobic respiration of bacteria producing water",
          "Anaerobic fermentation of sugars by yeast producing ethanol and CO₂",
          "Photosynthesis by algae",
          "Aerobic decomposition of palm fruit"
        ],
        "a": 1,
        "e": "The production of palm wine, beer, and other alcoholic beverages involves anaerobic fermentation: yeast cells break down sugars (from palm sap, grains, etc.) to ethanol and CO₂ in the absence of oxygen.",
        "h": "Brewing/palm wine = yeast anaerobic fermentation → ethanol + CO₂.",
        "yr": "SS1"
      },
      {
        "q": "Respiration releases energy in the form of:",
        "o": ["Glucose", "ADP", "ATP (adenosine triphosphate)", "NADH only"],
        "a": 2,
        "e": "Respiration releases energy that is immediately captured and stored in ATP (adenosine triphosphate) molecules. ATP is then used to power all energy-requiring processes in the cell — it is the universal energy currency.",
        "h": "Respiration → energy stored as ATP (the cell's energy currency).",
        "yr": "SS1"
      },
      {
        "q": "Which product of anaerobic respiration in yeast causes bread dough to rise?",
        "o": ["Ethanol", "Lactic acid", "Carbon dioxide", "Water"],
        "a": 2,
        "e": "Carbon dioxide gas produced by yeast fermentation forms bubbles in the dough. These bubbles expand when heated during baking, causing the dough to rise and giving bread its light, airy texture.",
        "h": "CO₂ from yeast fermentation causes bread dough to rise.",
        "yr": "SS1"
      },
      {
        "q": "Which statement correctly distinguishes breathing from respiration?",
        "o": [
          "Breathing releases ATP; respiration moves air",
          "Breathing is the mechanical movement of air in/out of lungs; respiration is the cellular chemical release of energy from food",
          "Both terms mean the same thing",
          "Breathing occurs only in animals; respiration occurs in plants"
        ],
        "a": 1,
        "e": "Breathing (ventilation) is the mechanical process of inhaling and exhaling — moving air in and out of lungs. Respiration is the cellular chemical process of breaking down glucose to release ATP energy. All living cells respire; only air-breathing organisms breathe.",
        "h": "Breathing = mechanical (lungs); Respiration = chemical (all cells, releases ATP).",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Growth and Irritability",
    "topicCode": "SS1-BIO-08",
    "module": "Cell Biology",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Growth</span> is the permanent, irreversible increase in the size, mass, and complexity of a living organism. <span class=\"learn-keyword\">Irritability</span> (also called sensitivity) is the ability to detect changes (stimuli) in the environment and make appropriate responses. Both are fundamental life processes that enable organisms to survive and adapt.\n</div>\n\n<h3 class=\"learn-subheading\">1. Meaning and Processes of Growth</h3>\n<p class=\"learn-p\">Growth at the cellular level involves three processes:</p>\n<ul class=\"learn-list\">\n  <li><strong>Cell division (mitosis):</strong> Increases the number of cells in the organism</li>\n  <li><strong>Cell enlargement:</strong> Each new cell increases in size by taking in water and synthesizing more cytoplasm</li>\n  <li><strong>Cell differentiation:</strong> Cells develop specialized structures and functions (e.g., nerve cells, muscle cells)</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Importance of Growth</h3>\n<ul class=\"learn-list\">\n  <li>Enables organisms to mature and reproduce</li>\n  <li>Allows repair and replacement of damaged or worn-out tissues</li>\n  <li>Provides increased body mass for survival</li>\n  <li>Leads to differentiation and specialization for efficient functioning</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Measurement of Growth</h3>\n<p class=\"learn-p\">Growth can be measured by changes in:</p>\n<ul class=\"learn-list\">\n  <li><strong>Height or length</strong> — simple but may fluctuate</li>\n  <li><strong>Wet mass</strong> — affected by water content (not always reliable)</li>\n  <li><strong>Dry mass</strong> — most accurate measure (removes water effect); kills the organism</li>\n  <li><strong>Cell number</strong> — counted under microscope</li>\n</ul>\n\n<h3 class=\"learn-subheading\">4. Irritability: Meaning and Processes</h3>\n<p class=\"learn-p\">Irritability involves two steps: <strong>reception</strong> (detecting the stimulus) and <strong>response</strong> (reacting to it).</p>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Organism</th><th>Stimulus</th><th>Response</th><th>Name</th></tr></thead>\n    <tbody>\n      <tr><td>Plant shoot</td><td>Light</td><td>Bends toward light</td><td>Phototropism</td></tr>\n      <tr><td>Plant root</td><td>Gravity</td><td>Grows downward</td><td>Geotropism</td></tr>\n      <tr><td>Plant root</td><td>Water</td><td>Grows toward water</td><td>Hydrotropism</td></tr>\n      <tr><td>Amoeba</td><td>Chemical (food)</td><td>Moves toward it</td><td>Positive chemotaxis</td></tr>\n      <tr><td>Animal</td><td>Pain</td><td>Withdraws from stimulus</td><td>Reflex action</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">5. Importance of Irritability</h3>\n<ul class=\"learn-list\">\n  <li>Enables organisms to detect and avoid danger</li>\n  <li>Helps locate food sources</li>\n  <li>Allows plants to position leaves for maximum photosynthesis</li>\n  <li>Coordinates bodily functions through the nervous and endocrine systems</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Dry mass is the most reliable measure of growth because wet mass varies with water content. A seed losing water during germination might show a decrease in wet mass but an increase in dry mass as new organic molecules are synthesized.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Growth is a permanent increase in size/mass through cell division (mitosis), cell enlargement, and differentiation. Best measured as dry mass. Irritability is the ability to detect stimuli and respond. Plant tropisms (phototropism, geotropism, hydrotropism) and animal reflexes are examples. Both growth and irritability are essential life processes for survival.\n</div>\n  ",
    "questions": [
      {
        "q": "Growth is best defined as:",
        "o": [
          "Any increase in size",
          "A temporary increase in water content",
          "A permanent, irreversible increase in the size and dry mass of an organism",
          "The production of offspring"
        ],
        "a": 2,
        "e": "Growth is a permanent, irreversible increase in the size, mass, and complexity of an organism. It occurs through cell division, enlargement, and differentiation. It is permanent (not reversed when conditions change) unlike water uptake.",
        "h": "Growth = permanent, irreversible increase in size/dry mass.",
        "yr": "SS1"
      },
      {
        "q": "Which measurement of growth is the most accurate and reliable?",
        "o": ["Height", "Wet mass", "Dry mass", "Waist circumference"],
        "a": 2,
        "e": "Dry mass is the most accurate measure of growth because it removes the variable of water content. Wet mass fluctuates with hydration levels and does not truly reflect growth in organic matter.",
        "h": "Dry mass = most accurate measure of growth (removes water variable).",
        "yr": "SS1"
      },
      {
        "q": "Irritability in biology is defined as:",
        "o": [
          "The ability to produce emotions",
          "The ability to detect changes in the environment (stimuli) and respond appropriately",
          "The ability to digest food",
          "The ability to reproduce"
        ],
        "a": 1,
        "e": "Irritability (sensitivity) is the ability to detect stimuli (changes in the internal or external environment) and make appropriate responses. It is a fundamental characteristic of all living organisms.",
        "h": "Irritability = detect stimuli + respond appropriately.",
        "yr": "SS1"
      },
      {
        "q": "Phototropism is the growth movement of a plant in response to:",
        "o": ["Gravity", "Water", "Light", "Temperature"],
        "a": 2,
        "e": "Phototropism is the directional growth response of a plant to light. Shoots show positive phototropism (grow toward light) while roots show negative phototropism. It is mediated by the hormone auxin.",
        "h": "Phototropism = plant growth response to LIGHT.",
        "yr": "SS1"
      },
      {
        "q": "Which process directly increases the NUMBER of cells in a growing organism?",
        "o": ["Cell enlargement", "Differentiation", "Cell division (mitosis)", "Respiration"],
        "a": 2,
        "e": "Cell division by mitosis increases the number of cells. Cell enlargement increases the size of existing cells. Differentiation makes cells specialized. Only mitosis directly increases cell number.",
        "h": "Cell division (mitosis) = increases cell NUMBER.",
        "yr": "SS1"
      },
      {
        "q": "Geotropism in plant roots means that roots:",
        "o": [
          "Grow toward light",
          "Grow away from gravity (upward)",
          "Grow in the direction of gravity (downward) — positive geotropism",
          "Grow toward water"
        ],
        "a": 2,
        "e": "Roots show positive geotropism — they grow in the direction of gravitational pull (downward) to anchor the plant and reach water in soil. Shoots show negative geotropism (grow upward, against gravity).",
        "h": "Root geotropism = positive (grows downward toward gravity).",
        "yr": "SS1"
      },
      {
        "q": "Cell differentiation during growth leads to:",
        "o": [
          "All cells remaining identical",
          "Cells becoming specialized for specific functions",
          "Cells dying",
          "Increased cell division rate"
        ],
        "a": 1,
        "e": "Cell differentiation is the process by which cells develop specific structures and functions to perform specialized roles. For example, undifferentiated stem cells differentiate into nerve cells, muscle cells, red blood cells, etc.",
        "h": "Differentiation = cells becoming specialized for specific functions.",
        "yr": "SS1"
      },
      {
        "q": "A plant bending toward a window is an example of:",
        "o": ["Geotropism", "Phototropism", "Hydrotropism", "Thigmotropism"],
        "a": 1,
        "e": "A plant bending toward a window (light source) demonstrates positive phototropism — growth toward light. The hormone auxin redistributes to the shaded side, causing that side to grow faster, bending the shoot toward the light.",
        "h": "Plant bending toward light = positive phototropism.",
        "yr": "SS1"
      },
      {
        "q": "The growth of roots toward water in soil is called:",
        "o": ["Phototropism", "Geotropism", "Hydrotropism", "Thigmotropism"],
        "a": 2,
        "e": "Hydrotropism is the directional growth response of plant roots toward water. Roots show positive hydrotropism — they grow toward moisture in the soil, which helps the plant absorb water for survival.",
        "h": "Growth toward water = hydrotropism (hydro = water).",
        "yr": "SS1"
      },
      {
        "q": "Why is growth measured as dry mass rather than wet mass?",
        "o": [
          "Dry mass is easier to measure",
          "Wet mass cannot be measured",
          "Dry mass is not affected by temporary changes in water content and more accurately reflects increase in organic matter",
          "Dry mass measures height"
        ],
        "a": 2,
        "e": "Wet mass varies with the organism's water content, which can change due to drinking, dehydration, etc. Dry mass (measured after removing all water by drying) reflects only the actual organic material accumulated and is not affected by water fluctuations.",
        "h": "Dry mass unaffected by water content → more accurate measure of real growth.",
        "yr": "SS1"
      },
      {
        "q": "An Amoeba moving toward a food source is demonstrating:",
        "o": ["Phototropism", "Positive chemotaxis", "Geotropism", "Thigmotropism"],
        "a": 1,
        "e": "Chemotaxis is the movement of an organism toward (positive) or away from (negative) a chemical stimulus. Amoeba moves toward chemical substances released by food particles — this is positive chemotaxis, a form of irritability.",
        "h": "Amoeba moving toward food chemicals = positive chemotaxis.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is an importance of irritability to animals?",
        "o": [
          "It allows animals to photosynthesize",
          "It enables animals to detect danger and make appropriate responses for survival",
          "It increases the animal's growth rate",
          "It allows excretion of waste"
        ],
        "a": 1,
        "e": "Irritability enables animals to detect predators, food, mates, and environmental changes. This ability to respond appropriately is critical for survival — for example, fleeing from a predator or seeking shelter from extreme temperatures.",
        "h": "Irritability allows animals to detect danger and respond → survival.",
        "yr": "SS1"
      },
      {
        "q": "Growth in plants occurs mainly in regions called:",
        "o": ["Stomata", "Meristems", "Chloroplasts", "Phloem"],
        "a": 1,
        "e": "Meristems are regions of actively dividing cells in plants. They are found at the tips of roots (root apical meristem) and shoots (shoot apical meristem), and in the cambium. Growth in plants is localized to these meristematic regions.",
        "h": "Plant growth regions = meristems (apical and lateral).",
        "yr": "SS1"
      },
      {
        "q": "The two steps in the process of irritability are:",
        "o": [
          "Growth and reproduction",
          "Reception (detecting the stimulus) and response (reacting to it)",
          "Nutrition and excretion",
          "Digestion and absorption"
        ],
        "a": 1,
        "e": "Irritability involves two steps: (1) reception — the organism detects a stimulus through receptors (e.g., eyes, ears, nerve endings), and (2) response — the organism reacts appropriately (e.g., muscle contraction, hormone secretion, growth movement).",
        "h": "Irritability = reception (detect stimulus) + response (react).",
        "yr": "SS1"
      },
      {
        "q": "A reflex action (e.g., pulling your hand from a hot object) is an example of:",
        "o": ["Growth", "Nutrition", "Irritability (rapid involuntary response to stimulus)", "Respiration"],
        "a": 2,
        "e": "A reflex action is a rapid, involuntary response to a stimulus (e.g., heat, pain). It is a clear demonstration of irritability — detecting a stimulus (pain/heat receptors) and making a rapid protective response (withdrawing the hand).",
        "h": "Reflex action = rapid involuntary response to stimulus = irritability.",
        "yr": "SS1"
      },
      {
        "q": "During germination of a seed, the seedling may show a temporary decrease in WET mass. This is because:",
        "o": [
          "The seed is not growing",
          "The seed is losing water through transpiration while consuming stored food, reducing wet mass even as dry mass increases",
          "Photosynthesis has not started",
          "The root is not absorbing water"
        ],
        "a": 1,
        "e": "During early germination, the seed uses stored food for energy and growth but may lose water through transpiration. The wet mass can decrease, but dry mass increases as new organic matter (cells) is synthesized. This shows why dry mass is the better measure.",
        "h": "Germinating seed: wet mass may decrease but dry mass increases as new organic matter forms.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is NOT a method of measuring growth?",
        "o": ["Increase in dry mass", "Increase in cell number", "Change in body temperature", "Increase in height"],
        "a": 2,
        "e": "Change in body temperature is NOT a measure of growth — it reflects metabolic rate and heat regulation. Growth is measured by dry mass, wet mass, height/length, cell number, or leaf area.",
        "h": "Body temperature change does NOT measure growth.",
        "yr": "SS1"
      },
      {
        "q": "In animals, growth is:",
        "o": [
          "Localized to specific regions (like plant meristems)",
          "Generally distributed throughout the body (intercalary growth)",
          "Unlimited throughout their lifetime",
          "Only occurs in juvenile stages in most mammals"
        ],
        "a": 3,
        "e": "In most mammals, significant growth occurs mainly during juvenile (young) stages. After reaching maturity, growth largely stops (unlike trees which can grow indefinitely). However, some tissues (skin, blood cells) continuously replace themselves.",
        "h": "In mammals: growth mainly during juvenile stages; slows/stops at maturity.",
        "yr": "SS1"
      },
      {
        "q": "Hydrotropism helps plants by:",
        "o": [
          "Orienting leaves toward sunlight",
          "Directing roots toward water sources in the soil",
          "Causing stems to grow upward",
          "Helping flowers open and close"
        ],
        "a": 1,
        "e": "Hydrotropism directs root growth toward regions of higher moisture in the soil. This ensures the plant's roots can locate and absorb water efficiently, which is essential for photosynthesis, nutrient transport, and cell turgor.",
        "h": "Hydrotropism directs roots toward water → efficient water absorption.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following correctly pairs a stimulus with its response in plants?",
        "o": [
          "Light → root grows toward light (positive phototropism in roots)",
          "Gravity → shoot grows downward (positive geotropism in shoots)",
          "Light → shoot bends toward light (positive phototropism in shoots)",
          "Water → shoot grows toward water"
        ],
        "a": 2,
        "e": "Shoots show positive phototropism — they grow toward light. This allows leaves to receive maximum sunlight for photosynthesis. Roots show negative phototropism (grow away from light) and positive geotropism (grow downward).",
        "h": "Shoot + light = positive phototropism (bends toward light).",
        "yr": "SS1"
      }
    ]
  }
  ,
  {
    "topic": "Tissue and Supporting System",
    "topicCode": "SS1-BIO-09",
    "module": "Organisation of Life",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  A <span class=\"learn-keyword\">tissue</span> is a group of similar cells that work together to perform a specific function. The study of tissues is called <span class=\"learn-keyword\">histology</span>. In multicellular organisms, cells are organized into tissues, tissues into organs, organs into organ systems, and systems into the complete organism.\n</div>\n\n<h3 class=\"learn-subheading\">1. Levels of Organisation</h3>\n<p class=\"learn-p\"><strong>Cell → Tissue → Organ → Organ System → Organism</strong></p>\n<ul class=\"learn-list\">\n  <li><strong>Cell:</strong> Basic unit (e.g., nerve cell, muscle cell)</li>\n  <li><strong>Tissue:</strong> Group of similar cells with same function (e.g., muscle tissue)</li>\n  <li><strong>Organ:</strong> Group of tissues working together (e.g., heart, leaf)</li>\n  <li><strong>Organ System:</strong> Group of organs with related functions (e.g., digestive system)</li>\n  <li><strong>Organism:</strong> Complete living individual</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Types of Animal Tissues</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Tissue Type</th><th>Function</th><th>Example</th></tr></thead>\n    <tbody>\n      <tr><td>Epithelial</td><td>Covers body surfaces; protection, absorption, secretion</td><td>Skin, gut lining, lung alveoli</td></tr>\n      <tr><td>Connective</td><td>Supports, binds, and connects other tissues</td><td>Bone, cartilage, blood, tendons</td></tr>\n      <tr><td>Muscle</td><td>Contraction for movement</td><td>Skeletal, smooth, cardiac muscle</td></tr>\n      <tr><td>Nervous</td><td>Transmits electrical impulses for communication</td><td>Neurons (brain, spinal cord, nerves)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">3. Types of Plant Tissues</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Tissue Type</th><th>Function</th><th>Example/Location</th></tr></thead>\n    <tbody>\n      <tr><td>Meristematic</td><td>Cell division for growth</td><td>Root and shoot tips, cambium</td></tr>\n      <tr><td>Epidermal</td><td>Protection; reduces water loss</td><td>Outer layer of leaves and stems</td></tr>\n      <tr><td>Parenchyma</td><td>Photosynthesis, storage</td><td>Mesophyll of leaves</td></tr>\n      <tr><td>Collenchyma</td><td>Flexible support in young stems</td><td>Stems of herbaceous plants</td></tr>\n      <tr><td>Sclerenchyma</td><td>Rigid support and strength (dead cells)</td><td>Fibres in stems; seed coats</td></tr>\n      <tr><td>Xylem</td><td>Transport water and minerals upward</td><td>Vascular bundles of stems</td></tr>\n      <tr><td>Phloem</td><td>Transport sugars (sucrose) to all parts</td><td>Vascular bundles of stems</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">4. Supporting Systems</h3>\n<p class=\"learn-p\">Supporting systems provide structural integrity. In animals, the skeleton supports the body. Two types:</p>\n<ul class=\"learn-list\">\n  <li><strong>Exoskeleton:</strong> External skeleton made of chitin (e.g., insects, crustaceans)</li>\n  <li><strong>Endoskeleton:</strong> Internal skeleton made of bone/cartilage (e.g., vertebrates including humans)</li>\n  <li>Plants are supported by: turgor pressure in cells, cellulose cell walls, collenchyma/sclerenchyma tissues, and woody tissue (xylem)</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Know the four types of animal tissues: Epithelial, Connective, Muscle, Nervous (ECMN). Know that Xylem transports WATER (upward) and Phloem transports SUGARS (sucrose — both up and down). Xylem cells are dead; phloem cells are living.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Tissues are groups of similar cells performing a common function. Animals have four main tissue types: epithelial, connective, muscle, and nervous. Plants have meristematic, epidermal, parenchyma, collenchyma, sclerenchyma, xylem, and phloem tissues. Supporting systems (skeleton in animals, turgor/cell walls in plants) provide structural integrity.\n</div>\n  ",
    "questions": [
      {
        "q": "A tissue is best defined as:",
        "o": [
          "A single specialized cell",
          "A group of similar cells that work together to perform a specific function",
          "A collection of different organs",
          "A complete organism"
        ],
        "a": 1,
        "e": "A tissue is a group of cells that are similar in structure and work together to perform the same function. Examples include muscle tissue (contraction), nervous tissue (impulse transmission), and xylem tissue (water transport in plants).",
        "h": "Tissue = group of similar cells performing a specific function.",
        "yr": "SS1"
      },
      {
        "q": "What is the correct order of biological organisation from simplest to most complex?",
        "o": [
          "Organ → Tissue → Cell → Organism → Organ system",
          "Cell → Tissue → Organ → Organ system → Organism",
          "Tissue → Cell → Organ → Organism → Organ system",
          "Organism → Organ system → Organ → Tissue → Cell"
        ],
        "a": 1,
        "e": "The correct hierarchy from simplest to most complex is: Cell → Tissue → Organ → Organ System → Organism. Each level is made up of the level below it and contributes to the level above.",
        "h": "Cell → Tissue → Organ → Organ System → Organism.",
        "yr": "SS1"
      },
      {
        "q": "Which animal tissue is responsible for transmitting electrical impulses?",
        "o": ["Epithelial tissue", "Connective tissue", "Muscle tissue", "Nervous tissue"],
        "a": 3,
        "e": "Nervous tissue is composed of neurons (nerve cells) that transmit electrical impulses rapidly through the body. It forms the brain, spinal cord, and peripheral nerves, enabling coordination and communication.",
        "h": "Nervous tissue = neurons = transmit electrical impulses.",
        "yr": "SS1"
      },
      {
        "q": "Which plant tissue is responsible for transporting water and mineral salts from roots to leaves?",
        "o": ["Phloem", "Parenchyma", "Xylem", "Sclerenchyma"],
        "a": 2,
        "e": "Xylem tissue transports water and dissolved mineral salts from the roots upward to the leaves through a process driven by transpiration (water loss from leaves). Xylem cells (tracheids and vessels) are dead and hollow.",
        "h": "Xylem = transports water and minerals UPWARD (roots → leaves).",
        "yr": "SS1"
      },
      {
        "q": "Phloem tissue in plants is responsible for:",
        "o": [
          "Transporting water upward from roots",
          "Providing rigid structural support",
          "Transporting sugars (sucrose) produced during photosynthesis to all parts of the plant",
          "Cell division for growth"
        ],
        "a": 2,
        "e": "Phloem transports organic solutes — mainly sucrose (the form in which photosynthetic products are transported) — from the leaves (source) to other parts of the plant (sinks) such as roots, growing tips, and storage organs.",
        "h": "Phloem = transports sugars (sucrose) from leaves to all parts of plant.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is an example of connective tissue?",
        "o": ["Skin", "Neurons", "Cardiac muscle", "Bone"],
        "a": 3,
        "e": "Bone is a connective tissue — it supports and protects other tissues and organs. Other examples of connective tissue include cartilage, tendons, ligaments, and blood. Connective tissues are characterized by an extracellular matrix.",
        "h": "Connective tissue examples: bone, cartilage, blood, tendons, ligaments.",
        "yr": "SS1"
      },
      {
        "q": "Which plant tissue contains actively dividing cells responsible for growth?",
        "o": ["Sclerenchyma", "Xylem", "Meristematic tissue", "Epidermal tissue"],
        "a": 2,
        "e": "Meristematic tissue consists of undifferentiated, actively dividing cells. Found at root tips, shoot tips, and cambium, these cells divide continuously by mitosis to produce new cells for growth.",
        "h": "Meristematic tissue = actively dividing cells = region of plant growth.",
        "yr": "SS1"
      },
      {
        "q": "The study of tissues is called:",
        "o": ["Ecology", "Histology", "Physiology", "Cytology"],
        "a": 1,
        "e": "Histology is the branch of biology that studies the microscopic structure of tissues. It involves preparing thin sections of tissues, staining them, and examining them under a microscope.",
        "h": "Histology = study of tissues (histo = tissue).",
        "yr": "SS1"
      },
      {
        "q": "Epithelial tissue serves the function of:",
        "o": [
          "Transmitting nerve impulses",
          "Contracting to produce movement",
          "Covering body surfaces and lining cavities for protection, absorption, and secretion",
          "Transporting water in plants"
        ],
        "a": 2,
        "e": "Epithelial tissue covers external and internal body surfaces. It provides protection (skin), facilitates absorption (gut lining), and enables secretion (gland cells). It is characterized by tightly packed cells with little extracellular matrix.",
        "h": "Epithelial tissue = covers surfaces; protection, absorption, secretion.",
        "yr": "SS1"
      },
      {
        "q": "An insect has an exoskeleton made of chitin. This means its skeleton is:",
        "o": [
          "Made of bone inside the body",
          "Made of cartilage",
          "External, forming the outer surface of the body",
          "Made of cellulose"
        ],
        "a": 2,
        "e": "An exoskeleton is an external skeleton that forms the outer covering of the organism. In insects and crustaceans, it is made of chitin — a tough polysaccharide. It provides protection and structural support but must be shed (moulted) during growth.",
        "h": "Exoskeleton = external skeleton (chitin in insects/crustaceans).",
        "yr": "SS1"
      },
      {
        "q": "Sclerenchyma tissue in plants provides:",
        "o": [
          "Transport of water",
          "Photosynthesis",
          "Rigid structural support (cells are dead at maturity with thick walls)",
          "Cell division for growth"
        ],
        "a": 2,
        "e": "Sclerenchyma cells have thick, lignified cell walls and are dead at functional maturity. They provide rigid mechanical support to plant organs. Examples include fibres in stems and stone cells in seed coats.",
        "h": "Sclerenchyma = rigid support (dead cells with thick lignified walls).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following correctly pairs a tissue type with its function in animals?",
        "o": [
          "Muscle tissue → transmits electrical impulses",
          "Nervous tissue → contracts to produce movement",
          "Connective tissue → supports and binds other tissues",
          "Epithelial tissue → provides mechanical strength only"
        ],
        "a": 2,
        "e": "Connective tissue supports, binds, and connects other tissues. Muscle tissue contracts (movement), nervous tissue transmits impulses, and epithelial tissue covers surfaces and is involved in absorption and secretion.",
        "h": "Connective tissue = supports and binds other tissues.",
        "yr": "SS1"
      },
      {
        "q": "Turgor pressure in plant cells contributes to plant support because:",
        "o": [
          "It breaks down the cell wall",
          "It causes cells to become firm as water fills the vacuole and pushes against the cell wall",
          "It removes water from cells",
          "It transports sugar through phloem"
        ],
        "a": 1,
        "e": "When a plant cell is fully turgid, water fills the vacuole and pushes outward against the rigid cell wall, making the cell firm. This turgor pressure in millions of cells collectively gives herbaceous (non-woody) plants their structural rigidity.",
        "h": "Turgor pressure = water in vacuole pushes against cell wall → firm cells → plant support.",
        "yr": "SS1"
      },
      {
        "q": "Parenchyma tissue in plant leaves is primarily associated with:",
        "o": ["Water transport", "Structural support", "Photosynthesis and storage", "Cell division"],
        "a": 2,
        "e": "Parenchyma cells are living, thin-walled cells found throughout the plant. In leaves, they form the mesophyll layer where photosynthesis occurs. In roots and stems, they store starch, water, and other substances.",
        "h": "Parenchyma = photosynthesis (in leaves) and storage.",
        "yr": "SS1"
      },
      {
        "q": "The human skeleton is an endoskeleton. This means it is:",
        "o": [
          "External and made of chitin",
          "Internal and made of bone and cartilage",
          "External and made of calcium only",
          "Absent in humans"
        ],
        "a": 1,
        "e": "An endoskeleton is an internal skeleton. In vertebrates (including humans), it is made of bone and cartilage. It supports and protects internal organs, provides attachment points for muscles, and allows movement at joints.",
        "h": "Endoskeleton = internal skeleton (bone and cartilage in vertebrates).",
        "yr": "SS1"
      },
      {
        "q": "Which tissue type in animals includes blood?",
        "o": ["Epithelial", "Nervous", "Muscle", "Connective"],
        "a": 3,
        "e": "Blood is classified as a connective tissue because it has cells (red blood cells, white blood cells, platelets) suspended in a liquid extracellular matrix (plasma). Like other connective tissues, it connects and supports body systems through transport.",
        "h": "Blood = connective tissue (cells in plasma extracellular matrix).",
        "yr": "SS1"
      },
      {
        "q": "Xylem cells are dead at maturity. This is advantageous because:",
        "o": [
          "Dead cells can divide faster",
          "Empty, dead cells form hollow tubes that allow unobstructed water transport",
          "Dead cells can photosynthesize",
          "Dead cells provide nutrients to the plant"
        ],
        "a": 1,
        "e": "Xylem vessels are formed by dead cells whose end walls break down, forming continuous hollow tubes. This allows water to flow freely upward through the plant without cellular contents obstructing the flow.",
        "h": "Xylem = dead cells → hollow tubes → unobstructed water transport.",
        "yr": "SS1"
      },
      {
        "q": "Collenchyma tissue provides what type of support to plants?",
        "o": [
          "Rigid, permanent support (dead cells)",
          "Flexible support in young, still-growing stems",
          "Support through water pressure only",
          "Support by transporting minerals"
        ],
        "a": 1,
        "e": "Collenchyma provides flexible support in young, growing parts of plants. Unlike sclerenchyma (rigid, dead), collenchyma cells are living and have unevenly thickened cell walls. They allow the stem to bend without breaking.",
        "h": "Collenchyma = flexible support in young stems (living cells).",
        "yr": "SS1"
      },
      {
        "q": "Which statement about muscle tissue is correct?",
        "o": [
          "Muscle tissue is only found in the heart",
          "There are three types of muscle tissue: skeletal, smooth, and cardiac",
          "Muscle tissue transmits electrical impulses",
          "Muscle tissue is a type of connective tissue"
        ],
        "a": 1,
        "e": "There are three types of muscle tissue: skeletal (voluntary, attached to bones), smooth (involuntary, found in organs like gut and blood vessels), and cardiac (involuntary, only in the heart). All three function by contraction.",
        "h": "3 muscle types: skeletal (voluntary), smooth (involuntary), cardiac (heart).",
        "yr": "SS1"
      },
      {
        "q": "An organ is best described as:",
        "o": [
          "A single type of tissue",
          "A group of different tissues working together to perform a specific function",
          "A collection of organisms",
          "A type of cell organelle"
        ],
        "a": 1,
        "e": "An organ is a structure made up of two or more different types of tissues that work together to perform a specific function. For example, the heart contains cardiac muscle tissue, connective tissue, epithelial tissue, and nervous tissue.",
        "h": "Organ = two or more tissues working together for a specific function.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Nutrition",
    "topicCode": "SS1-BIO-10",
    "module": "Nutrition and Health",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Nutrition</span> is the process by which organisms obtain and use food for energy, growth, and maintenance of life. There are two fundamental types: <span class=\"learn-keyword\">autotrophic</span> (self-feeding) and <span class=\"learn-keyword\">heterotrophic</span> (other-feeding). Understanding these types reveals how energy flows through ecosystems.\n</div>\n\n<h3 class=\"learn-subheading\">1. Autotrophic Nutrition</h3>\n<p class=\"learn-p\">Autotrophs manufacture their own food from simple inorganic substances.</p>\n<ul class=\"learn-list\">\n  <li><strong>Photosynthesis (Photoautotrophs):</strong> Use light energy to convert CO₂ + H₂O into glucose. Examples: green plants, algae, cyanobacteria</li>\n  <li><strong>Chemosynthesis (Chemoautotrophs):</strong> Use chemical energy from oxidizing inorganic compounds. Examples: nitrifying bacteria (<em>Nitrosomonas, Nitrobacter</em>)</li>\n</ul>\n<p class=\"learn-p\"><strong>Photosynthesis equation:</strong> 6CO₂ + 6H₂O + light energy → C₆H₁₂O₆ + 6O₂</p>\n\n<h3 class=\"learn-subheading\">2. Heterotrophic Nutrition</h3>\n<p class=\"learn-p\">Heterotrophs cannot make their own food and must consume organic matter.</p>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Type</th><th>Method</th><th>Examples</th></tr></thead>\n    <tbody>\n      <tr><td>Holozoic</td><td>Ingesting solid food; full digestion</td><td>Humans, most animals</td></tr>\n      <tr><td>Saprophytic (Saprotrophic)</td><td>Secreting enzymes; absorbing products from dead matter</td><td>Fungi, many bacteria</td></tr>\n      <tr><td>Parasitic</td><td>Living on/in a host; obtaining food at host's expense</td><td>Tapeworm, <em>Plasmodium</em>, mistletoe</td></tr>\n      <tr><td>Symbiotic (mutualistic)</td><td>Both organisms benefit</td><td>Nitrogen-fixing bacteria in legume roots</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">3. Holozoic Nutrition — Five Stages</h3>\n<ol>\n  <li><strong>Ingestion:</strong> Taking food into the mouth/body</li>\n  <li><strong>Digestion:</strong> Breaking down complex molecules (mechanical + chemical)</li>\n  <li><strong>Absorption:</strong> Taking up digested nutrients into the blood/lymph</li>\n  <li><strong>Assimilation:</strong> Using absorbed nutrients in metabolism</li>\n  <li><strong>Egestion:</strong> Expelling undigested food as faeces</li>\n</ol>\n\n<h3 class=\"learn-subheading\">4. Classes of Food Nutrients</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Nutrient</th><th>Function</th><th>Example Sources</th></tr></thead>\n    <tbody>\n      <tr><td>Carbohydrates</td><td>Primary energy source</td><td>Rice, yam, bread</td></tr>\n      <tr><td>Proteins</td><td>Growth, repair, enzymes, hormones</td><td>Meat, fish, beans</td></tr>\n      <tr><td>Lipids (Fats/Oils)</td><td>Energy reserve, insulation, cell membranes</td><td>Palm oil, groundnut</td></tr>\n      <tr><td>Vitamins</td><td>Regulate metabolic reactions</td><td>Fruits, vegetables</td></tr>\n      <tr><td>Minerals</td><td>Structural (Ca, P in bones); regulatory (Na, K)</td><td>Milk, green leafy vegetables</td></tr>\n      <tr><td>Water</td><td>Solvent; transport medium; reactions</td><td>All foods, drinking water</td></tr>\n      <tr><td>Roughage (fibre)</td><td>Aids bowel movement, prevents constipation</td><td>Vegetables, whole grains</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Remember that photosynthesis is the REVERSE of aerobic respiration in terms of reactants and products: Photosynthesis consumes CO₂ and produces O₂; respiration consumes O₂ and produces CO₂. Autotrophs are the PRODUCERS in food chains.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Autotrophs (plants, algae) make food via photosynthesis or chemosynthesis. Heterotrophs obtain food by holozoic (ingestion), saprophytic (absorption from dead matter), or parasitic nutrition. Holozoic nutrition has 5 stages: ingestion → digestion → absorption → assimilation → egestion. The 7 classes of nutrients are carbohydrates, proteins, lipids, vitamins, minerals, water, and roughage.\n</div>\n  ",
    "questions": [
      {
        "q": "Which type of nutrition involves an organism making its own food from inorganic substances?",
        "o": ["Holozoic", "Heterotrophic", "Autotrophic", "Saprophytic"],
        "a": 2,
        "e": "Autotrophic nutrition involves organisms manufacturing their own food from simple inorganic substances (CO₂, water, mineral salts) using an external energy source (light or chemical energy). Green plants are the most common autotrophs.",
        "h": "Autotrophic = self-feeding, makes food from inorganic materials (plants).",
        "yr": "SS1"
      },
      {
        "q": "The word equation for photosynthesis is:",
        "o": [
          "Glucose + Oxygen → Carbon dioxide + Water + Energy",
          "Carbon dioxide + Water + Light → Glucose + Oxygen",
          "Glucose + Water → Oxygen + Carbon dioxide",
          "Oxygen + Carbon dioxide → Glucose + Water"
        ],
        "a": 1,
        "e": "Photosynthesis: CO₂ + H₂O + light energy → C₆H₁₂O₆ + O₂. Plants absorb CO₂ through stomata and water through roots, and use light energy (via chlorophyll) to produce glucose and release oxygen as a by-product.",
        "h": "Photosynthesis: CO₂ + H₂O + light → glucose + O₂.",
        "yr": "SS1"
      },
      {
        "q": "A tapeworm lives in the human intestine and absorbs digested food. This is an example of:",
        "o": ["Holozoic nutrition", "Autotrophic nutrition", "Saprophytic nutrition", "Parasitic nutrition"],
        "a": 3,
        "e": "A tapeworm is a parasite — it lives inside a host (human) and obtains pre-digested nutrients from the host's intestine, harming the host in the process. It has no digestive system of its own and simply absorbs nutrients.",
        "h": "Tapeworm = parasite = parasitic nutrition (harms host).",
        "yr": "SS1"
      },
      {
        "q": "Which stage of holozoic nutrition involves breaking down complex food molecules into simpler ones?",
        "o": ["Ingestion", "Absorption", "Digestion", "Assimilation"],
        "a": 2,
        "e": "Digestion is the second stage of holozoic nutrition. It involves breaking down large, complex food molecules (starch, proteins, fats) into smaller, soluble molecules (glucose, amino acids, fatty acids) through mechanical and chemical (enzymatic) processes.",
        "h": "Digestion = breaking complex food molecules into smaller soluble ones.",
        "yr": "SS1"
      },
      {
        "q": "The fungus Rhizopus secretes enzymes onto bread and absorbs the products. This is:",
        "o": ["Holozoic nutrition", "Parasitic nutrition", "Saprophytic nutrition", "Autotrophic nutrition"],
        "a": 2,
        "e": "Saprophytic (saprotrophic) nutrition involves secreting digestive enzymes onto dead or decaying organic matter, and absorbing the soluble products. Rhizopus (bread mould) is a classic saprophyte.",
        "h": "Saprophytic = secretes enzymes onto dead matter + absorbs products.",
        "yr": "SS1"
      },
      {
        "q": "Which nutrient class is the primary energy source in the human diet?",
        "o": ["Proteins", "Lipids", "Carbohydrates", "Vitamins"],
        "a": 2,
        "e": "Carbohydrates (starch, sugars) are the primary energy source. They are broken down to glucose, which is used in cellular respiration to produce ATP. They provide 4 kcal/g. While lipids provide more energy per gram (9 kcal/g), carbohydrates are the preferred immediate energy source.",
        "h": "Carbohydrates = primary energy source (broken down to glucose).",
        "yr": "SS1"
      },
      {
        "q": "Assimilation in holozoic nutrition refers to:",
        "o": [
          "Taking food into the mouth",
          "Expelling undigested food",
          "Using absorbed nutrients in the body's metabolic processes",
          "Breaking down food with enzymes"
        ],
        "a": 2,
        "e": "Assimilation is the stage where absorbed nutrients are incorporated into the body's metabolic processes — glucose is used in respiration, amino acids are used to build proteins, fatty acids form cell membranes, etc.",
        "h": "Assimilation = using absorbed nutrients in metabolism.",
        "yr": "SS1"
      },
      {
        "q": "Egestion differs from excretion in that egestion involves:",
        "o": [
          "Removal of metabolic waste products",
          "Removal of undigested food (faeces) that never entered the body cells",
          "Secretion of enzymes",
          "Absorption of water in the large intestine"
        ],
        "a": 1,
        "e": "Egestion is the expulsion of undigested food as faeces. This material was never absorbed into body cells. Excretion, by contrast, removes metabolic waste products (CO₂, urea) that were produced inside cells.",
        "h": "Egestion = expelling undigested food (faeces); NOT the same as excretion.",
        "yr": "SS1"
      },
      {
        "q": "Chemoautotrophic bacteria obtain energy from:",
        "o": [
          "Sunlight",
          "Organic matter in dead organisms",
          "Oxidizing inorganic compounds like ammonia or hydrogen sulphide",
          "Parasitizing other organisms"
        ],
        "a": 2,
        "e": "Chemoautotrophs (e.g., nitrifying bacteria like Nitrosomonas, Nitrobacter) obtain energy by oxidizing inorganic compounds such as ammonia (NH₃) or hydrogen sulphide (H₂S), and use this energy to fix CO₂ into organic compounds — without needing sunlight.",
        "h": "Chemoautotrophs = energy from oxidizing inorganic chemicals (no sunlight needed).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following correctly describes saprophytic nutrition?",
        "o": [
          "Ingesting whole food and digesting it internally",
          "Making food by photosynthesis",
          "Secreting enzymes onto external organic matter and absorbing the digested products",
          "Living on a living host and absorbing nutrients"
        ],
        "a": 2,
        "e": "Saprophytic nutrition: the organism (usually fungi or bacteria) secretes digestive enzymes outside its body onto dead/decaying organic matter (external digestion), then absorbs the soluble products through its body surface.",
        "h": "Saprophytic = external digestion (enzymes secreted out) + absorption.",
        "yr": "SS1"
      },
      {
        "q": "Proteins are important in nutrition mainly because they:",
        "o": [
          "Provide the primary source of quick energy",
          "Are needed for growth, tissue repair, synthesis of enzymes and hormones",
          "Provide insulation and energy reserves",
          "Regulate bowel movement"
        ],
        "a": 1,
        "e": "Proteins are made of amino acids and serve as building blocks for growth and repair, enzymes (catalysts), hormones (chemical messengers), antibodies (immune defence), and structural components (muscle, hair, nails).",
        "h": "Proteins = growth, repair, enzymes, hormones, antibodies.",
        "yr": "SS1"
      },
      {
        "q": "The five stages of holozoic nutrition in the correct order are:",
        "o": [
          "Ingestion → Absorption → Digestion → Assimilation → Egestion",
          "Ingestion → Digestion → Absorption → Assimilation → Egestion",
          "Digestion → Ingestion → Absorption → Egestion → Assimilation",
          "Ingestion → Digestion → Egestion → Absorption → Assimilation"
        ],
        "a": 1,
        "e": "The correct order is: Ingestion (taking in food) → Digestion (breaking down) → Absorption (into blood/lymph) → Assimilation (using in metabolism) → Egestion (expelling undigested matter).",
        "h": "Holozoic: Ingestion → Digestion → Absorption → Assimilation → Egestion.",
        "yr": "SS1"
      },
      {
        "q": "Which vitamin deficiency causes scurvy?",
        "o": ["Vitamin A", "Vitamin B12", "Vitamin C", "Vitamin D"],
        "a": 2,
        "e": "Vitamin C (ascorbic acid) deficiency causes scurvy — characterized by bleeding gums, swollen joints, and skin problems. Vitamin C is essential for collagen synthesis (which strengthens connective tissue). It is found in citrus fruits and green vegetables.",
        "h": "Scurvy = Vitamin C deficiency.",
        "yr": "SS1"
      },
      {
        "q": "What role do minerals play in nutrition?",
        "o": [
          "Provide primary energy",
          "Build all body proteins",
          "Serve structural and regulatory roles (e.g., calcium in bones; iron in haemoglobin)",
          "Digest all types of food"
        ],
        "a": 2,
        "e": "Minerals serve two main roles: structural (calcium and phosphorus in bones and teeth; iron in haemoglobin) and regulatory (sodium and potassium in nerve function; iodine in thyroid hormone production).",
        "h": "Minerals: structural (Ca in bones) and regulatory (Na, K in nerves).",
        "yr": "SS1"
      },
      {
        "q": "Roughage (dietary fibre) is important in the diet because it:",
        "o": [
          "Provides energy",
          "Builds muscles",
          "Stimulates peristalsis and prevents constipation",
          "Provides vitamins"
        ],
        "a": 2,
        "e": "Dietary fibre (roughage) adds bulk to food in the large intestine, stimulating peristalsis (rhythmic muscular contractions) that move food along the gut. It prevents constipation, reduces risk of bowel cancer, and slows sugar absorption.",
        "h": "Roughage = adds bulk → stimulates peristalsis → prevents constipation.",
        "yr": "SS1"
      },
      {
        "q": "Water is important as a nutrient because it:",
        "o": [
          "Provides energy for metabolism",
          "Acts as the universal solvent, transport medium, and medium for metabolic reactions",
          "Builds cell walls",
          "Is the same as roughage"
        ],
        "a": 1,
        "e": "Water serves many roles: it dissolves substances (universal solvent), transports nutrients and waste in blood/lymph, provides a medium for all metabolic reactions, regulates body temperature (through sweating), and maintains cell turgor.",
        "h": "Water = solvent, transport medium, medium for metabolic reactions.",
        "yr": "SS1"
      },
      {
        "q": "Mutualistic (symbiotic) nutrition involves:",
        "o": [
          "One organism benefiting at the expense of the other",
          "Both organisms in the relationship benefiting",
          "One organism making food from sunlight",
          "An organism feeding on dead matter"
        ],
        "a": 1,
        "e": "In mutualistic (symbiotic) nutrition, both organisms benefit from the relationship. For example, Rhizobium bacteria live in legume root nodules — the bacteria fix nitrogen for the plant and receive organic compounds (sugars) from the plant in return.",
        "h": "Mutualism = both organisms benefit (e.g., Rhizobium in legume roots).",
        "yr": "SS1"
      },
      {
        "q": "Lipids (fats and oils) provide how many kilocalories per gram?",
        "o": ["4 kcal/g", "7 kcal/g", "9 kcal/g", "2 kcal/g"],
        "a": 2,
        "e": "Lipids provide approximately 9 kcal per gram — more than twice the energy of carbohydrates or proteins (which both provide ~4 kcal/g). This makes lipids the most concentrated energy store in the body.",
        "h": "Lipids = 9 kcal/g (most concentrated energy; carbs/proteins = 4 kcal/g).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a parasitic plant?",
        "o": ["Maize", "Mistletoe", "Mushroom", "Fern"],
        "a": 1,
        "e": "Mistletoe is a hemi-parasite — it attaches to tree branches with haustoria, drawing water and mineral salts from the host tree. It can photosynthesize but depends on the host for water and minerals. It harms the host tree.",
        "h": "Mistletoe = parasitic plant (attaches to host tree, draws water/minerals).",
        "yr": "SS1"
      },
      {
        "q": "The green colour of plant leaves is due to the presence of:",
        "o": ["Xanthophyll", "Carotene", "Chlorophyll", "Anthocyanin"],
        "a": 2,
        "e": "Chlorophyll is the primary photosynthetic pigment in plants and gives leaves their green colour. It absorbs red and blue light for photosynthesis and reflects green light (which is why leaves appear green).",
        "h": "Green leaves = chlorophyll (absorbs red/blue light, reflects green).",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Basic Ecological Concepts",
    "topicCode": "SS1-BIO-11",
    "module": "Ecology",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Ecology</span> is the scientific study of the relationships between living organisms and their environment. It examines how organisms interact with each other and with the physical (abiotic) factors of their habitat. Key concepts include habitat, ecosystem, biotic factors, and abiotic factors.\n</div>\n\n<h3 class=\"learn-subheading\">1. Habitat</h3>\n<p class=\"learn-p\">A <span class=\"learn-keyword\">habitat</span> is the specific place or locality where an organism normally lives and finds what it needs to survive (food, water, shelter, mates). Examples: a pond, a forest, the soil, a coral reef.</p>\n\n<h3 class=\"learn-subheading\">2. Ecosystem</h3>\n<p class=\"learn-p\">An <span class=\"learn-keyword\">ecosystem</span> is a community of living organisms (biotic component) interacting with the non-living (abiotic) components of their environment as a functional unit. Examples: a pond ecosystem, forest ecosystem, savanna ecosystem.</p>\n<ul class=\"learn-list\">\n  <li><strong>Biotic component:</strong> All living organisms (producers, consumers, decomposers)</li>\n  <li><strong>Abiotic component:</strong> Non-living physical factors (temperature, light, water, soil, pH, wind)</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Ecological Factors</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Abiotic Factors</th><th>Effect on Organisms</th></tr></thead>\n    <tbody>\n      <tr><td>Light intensity</td><td>Affects photosynthesis rate; determines plant distribution</td></tr>\n      <tr><td>Temperature</td><td>Affects enzyme activity and metabolic rates</td></tr>\n      <tr><td>Water/humidity</td><td>Essential for life; drought limits distribution</td></tr>\n      <tr><td>Soil composition (edaphic)</td><td>pH, mineral content, texture affect plant growth</td></tr>\n      <tr><td>Wind</td><td>Pollination, seed dispersal, affects water loss</td></tr>\n      <tr><td>Salinity</td><td>High salinity limits freshwater organisms</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">4. Biotic Factors</h3>\n<ul class=\"learn-list\">\n  <li><strong>Competition:</strong> Organisms competing for the same limited resource</li>\n  <li><strong>Predation:</strong> One organism (predator) killing and eating another (prey)</li>\n  <li><strong>Parasitism:</strong> One organism (parasite) benefits; host is harmed</li>\n  <li><strong>Mutualism:</strong> Both organisms benefit</li>\n  <li><strong>Commensalism:</strong> One benefits; the other is neither helped nor harmed</li>\n</ul>\n\n<h3 class=\"learn-subheading\">5. Population, Community, Biome, Biosphere</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Term</th><th>Definition</th><th>Example</th></tr></thead>\n    <tbody>\n      <tr><td>Population</td><td>All individuals of one species in an area at a time</td><td>All grasshoppers in a field</td></tr>\n      <tr><td>Community</td><td>All populations of different species in an area</td><td>All organisms in a pond</td></tr>\n      <tr><td>Ecosystem</td><td>Community + abiotic factors</td><td>Pond + its water, light, temperature</td></tr>\n      <tr><td>Biome</td><td>Large geographic area with characteristic climate and organisms</td><td>Tropical rainforest, savanna</td></tr>\n      <tr><td>Biosphere</td><td>All parts of Earth supporting life</td><td>Entire Earth's life zone</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Remember: Population (one species) → Community (all species) → Ecosystem (community + abiotic) → Biome → Biosphere. Abiotic means non-living; biotic means living. Ecology studies interactions BETWEEN organisms AND between organisms and their abiotic environment.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Ecology is the study of organisms and their environment. A habitat is where an organism lives. An ecosystem includes biotic (living) and abiotic (non-living) components. Abiotic factors include light, temperature, water, soil. Biotic factors include competition, predation, parasitism, mutualism. The hierarchy is: Population → Community → Ecosystem → Biome → Biosphere.\n</div>\n  ",
    "questions": [
      {
        "q": "Ecology is best defined as the study of:",
        "o": [
          "Only plants in their natural environment",
          "The relationships between living organisms and their environment",
          "The internal structure of organisms",
          "The classification of organisms"
        ],
        "a": 1,
        "e": "Ecology is the scientific study of interactions between organisms and their biotic (living) and abiotic (non-living) environment. It examines how organisms are distributed, how populations change, and how energy flows through ecosystems.",
        "h": "Ecology = study of interactions between organisms and their environment.",
        "yr": "SS1"
      },
      {
        "q": "A habitat is best described as:",
        "o": [
          "All organisms living in an area",
          "The specific place where an organism normally lives",
          "The chemical composition of soil",
          "The climate of a large region"
        ],
        "a": 1,
        "e": "A habitat is the specific location where an organism naturally lives and finds all it needs (food, water, shelter, mates). Examples include a pond, a tree trunk, the forest floor, or the ocean surface.",
        "h": "Habitat = the specific place where an organism normally lives.",
        "yr": "SS1"
      },
      {
        "q": "An ecosystem includes:",
        "o": [
          "Only the living organisms in an area",
          "Only the non-living factors of an environment",
          "All living organisms and the abiotic factors in an area, interacting together",
          "Only plants and animals in a region"
        ],
        "a": 2,
        "e": "An ecosystem includes both biotic (living: plants, animals, fungi, bacteria) and abiotic (non-living: light, temperature, water, soil, pH) components interacting as a functional unit. Energy flows and materials cycle within ecosystems.",
        "h": "Ecosystem = biotic (living) + abiotic (non-living) components interacting.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is an ABIOTIC factor?",
        "o": ["Competition between plants", "Predation", "Temperature", "Parasitism"],
        "a": 2,
        "e": "Temperature is an abiotic (non-living) factor that affects the distribution and activity of organisms by influencing enzyme activity and metabolic rates. Competition, predation, and parasitism are biotic (living) factors.",
        "h": "Temperature = abiotic (non-living); competition/predation = biotic (living).",
        "yr": "SS1"
      },
      {
        "q": "A population in ecology refers to:",
        "o": [
          "All organisms of different species in a habitat",
          "All individuals of the same species living in the same area at the same time",
          "The non-living components of an ecosystem",
          "A large region with a distinctive climate"
        ],
        "a": 1,
        "e": "A population is all the individuals of the same species living in the same geographic area at the same time and capable of interbreeding. Example: all the tilapia fish in a pond.",
        "h": "Population = all individuals of ONE species in one area at one time.",
        "yr": "SS1"
      },
      {
        "q": "A community in ecology consists of:",
        "o": [
          "All members of one species",
          "All the different populations of different species living in the same area",
          "Only the abiotic factors of an area",
          "Only producers in a habitat"
        ],
        "a": 1,
        "e": "A community is all the populations of different species living and interacting in the same area. For example, a pond community includes all fish, frogs, algae, insects, bacteria, etc. living in that pond.",
        "h": "Community = all populations of ALL different species in one area.",
        "yr": "SS1"
      },
      {
        "q": "Which biotic interaction benefits one organism while the other is harmed?",
        "o": ["Mutualism", "Commensalism", "Parasitism", "Competition"],
        "a": 2,
        "e": "Parasitism: the parasite benefits (obtains nutrients/shelter) while the host is harmed (loses nutrients, may become diseased). Examples: tapeworm in humans, Plasmodium in red blood cells.",
        "h": "Parasitism: parasite benefits (+), host is harmed (−).",
        "yr": "SS1"
      },
      {
        "q": "Mutualism is an ecological relationship in which:",
        "o": [
          "One organism benefits and the other is unaffected",
          "Both organisms benefit",
          "One organism benefits and the other is harmed",
          "Both organisms compete"
        ],
        "a": 1,
        "e": "In mutualism, both organisms benefit. Example: Rhizobium bacteria in legume root nodules — bacteria fix nitrogen (benefiting the plant) and receive organic nutrients from the plant (benefiting the bacteria).",
        "h": "Mutualism = BOTH organisms benefit (+/+).",
        "yr": "SS1"
      },
      {
        "q": "The biosphere refers to:",
        "o": [
          "A specific pond habitat",
          "All the abiotic factors on Earth",
          "All parts of Earth where living organisms exist",
          "The soil layer of the Earth"
        ],
        "a": 2,
        "e": "The biosphere is the thin layer of Earth — including parts of the atmosphere, hydrosphere, and lithosphere — where life exists. It extends from the ocean floor to the upper atmosphere.",
        "h": "Biosphere = all parts of Earth supporting life.",
        "yr": "SS1"
      },
      {
        "q": "Light is an important abiotic factor because it:",
        "o": [
          "Increases soil pH",
          "Determines the rate of photosynthesis and affects plant distribution",
          "Directly provides nutrients",
          "Is a biotic factor"
        ],
        "a": 1,
        "e": "Light is essential for photosynthesis — the primary source of energy in most ecosystems. Light intensity and duration affect plant growth and distribution. It also influences animal behaviour (circadian rhythms, migration, reproduction).",
        "h": "Light = abiotic factor; drives photosynthesis; affects plant distribution.",
        "yr": "SS1"
      },
      {
        "q": "Commensalism is an ecological relationship in which:",
        "o": [
          "Both organisms benefit",
          "One benefits; the other is harmed",
          "One benefits; the other is neither helped nor harmed",
          "Both are harmed"
        ],
        "a": 2,
        "e": "In commensalism, one organism benefits and the other is unaffected (neither helped nor harmed). Example: epiphytic plants (orchids) grow on tree branches for support and light — the orchid benefits; the tree is unaffected.",
        "h": "Commensalism: one benefits (+), other unaffected (0).",
        "yr": "SS1"
      },
      {
        "q": "Edaphic factors are ecological factors relating to:",
        "o": ["Water temperature", "Light intensity", "Soil (composition, pH, texture)", "Wind speed"],
        "a": 2,
        "e": "Edaphic factors are soil-related abiotic factors including soil pH, mineral content, texture (clay/sandy/loam), organic matter, and drainage. They significantly influence which plants can grow in an area.",
        "h": "Edaphic factors = soil-related abiotic factors (pH, minerals, texture).",
        "yr": "SS1"
      },
      {
        "q": "A biome is best described as:",
        "o": [
          "A single pond or forest habitat",
          "A large geographic area characterized by a distinctive climate, vegetation, and wildlife",
          "All organisms on Earth",
          "The same as an ecosystem"
        ],
        "a": 1,
        "e": "A biome is a large-scale geographic region characterized by a particular climate (temperature and rainfall patterns) and associated plant and animal communities. Examples: tropical rainforest, savanna, desert, tundra.",
        "h": "Biome = large region with distinctive climate, plants, and animals.",
        "yr": "SS1"
      },
      {
        "q": "Competition between organisms is most intense when they:",
        "o": [
          "Occupy different niches",
          "Belong to different kingdoms",
          "Compete for the same limited resources in the same area",
          "Are predator and prey"
        ],
        "a": 2,
        "e": "Competition is most intense (intraspecific or interspecific) when organisms require the same limited resources — food, water, light, space, mates — in the same location. The stronger competitor typically survives (competitive exclusion principle).",
        "h": "Competition most intense when organisms need same limited resources in same area.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is the correct order from smallest to largest ecological unit?",
        "o": [
          "Biosphere → Biome → Ecosystem → Community → Population",
          "Population → Community → Ecosystem → Biome → Biosphere",
          "Ecosystem → Biome → Population → Community → Biosphere",
          "Community → Population → Ecosystem → Biosphere → Biome"
        ],
        "a": 1,
        "e": "From smallest to largest: Population (one species) → Community (all species) → Ecosystem (community + abiotic) → Biome (large climate zone) → Biosphere (all life on Earth).",
        "h": "Population → Community → Ecosystem → Biome → Biosphere.",
        "yr": "SS1"
      },
      {
        "q": "Predation as a biotic factor involves:",
        "o": [
          "Both organisms benefiting",
          "One organism (predator) hunting and consuming another organism (prey)",
          "One organism living inside another",
          "Two organisms competing for water"
        ],
        "a": 1,
        "e": "Predation is a biotic interaction where a predator captures and kills another organism (prey) for food. The predator benefits (+) and the prey is harmed (killed, −). Examples: lion eating a gazelle; eagle catching a rabbit.",
        "h": "Predation: predator kills and eats prey (predator +, prey −).",
        "yr": "SS1"
      },
      {
        "q": "Which abiotic factor most directly limits the distribution of marine and freshwater organisms?",
        "o": ["Temperature only", "Light only", "Salinity (salt concentration)", "Soil pH"],
        "a": 2,
        "e": "Salinity is a key abiotic factor in aquatic habitats. Freshwater organisms cannot survive in saltwater (and vice versa) due to osmotic differences. Salinity thus sharply limits the distribution of aquatic organisms.",
        "h": "Salinity limits distribution of aquatic organisms (freshwater vs marine).",
        "yr": "SS1"
      },
      {
        "q": "Nitrogen-fixing bacteria in the root nodules of legumes represent which type of biotic interaction?",
        "o": ["Parasitism", "Predation", "Mutualism", "Commensalism"],
        "a": 2,
        "e": "Nitrogen-fixing bacteria (Rhizobium) in legume root nodules represent mutualism. The bacteria fix atmospheric N₂ into ammonia for the plant (plant benefits); the plant provides organic nutrients (carbohydrates) to the bacteria (bacteria benefit).",
        "h": "Rhizobium in legume roots = mutualism (both benefit).",
        "yr": "SS1"
      },
      {
        "q": "Why is temperature an important abiotic factor?",
        "o": [
          "It provides food for animals",
          "It affects enzyme activity and metabolic rates, limiting where organisms can survive",
          "It is only important for plants",
          "It determines soil pH directly"
        ],
        "a": 1,
        "e": "Temperature affects the rate of enzyme-catalyzed reactions and metabolism. Each organism has an optimal temperature range. Extreme temperatures denature enzymes and inhibit metabolism, limiting distribution. For example, polar bears are adapted to cold; cactuses to heat.",
        "h": "Temperature affects enzyme activity and metabolism → limits organism distribution.",
        "yr": "SS1"
      },
      {
        "q": "An ecological niche refers to:",
        "o": [
          "The physical location where an organism lives",
          "The role and position of an organism in its ecosystem, including its function, diet, and interactions",
          "The classification of an organism",
          "The reproductive rate of a population"
        ],
        "a": 1,
        "e": "An ecological niche is the role an organism plays in its ecosystem — not just where it lives (habitat) but what it eats, how it feeds, its activity pattern, and all its interactions with other species and abiotic factors. No two species can occupy exactly the same niche indefinitely.",
        "h": "Niche = organism's role in ecosystem (function, diet, interactions), not just location.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Population Studies",
    "topicCode": "SS1-BIO-12",
    "module": "Ecology",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  A <span class=\"learn-keyword\">population</span> is all the individuals of the same species living in a defined area at a given time. Population ecology examines how populations change in size over time, what factors control these changes, and how populations interact with their environment.\n</div>\n\n<h3 class=\"learn-subheading\">1. Population Density</h3>\n<p class=\"learn-p\"><strong>Population density</strong> is the number of individuals of a species per unit area (or volume):</p>\n<p class=\"learn-p\" style=\"text-align:center;\"><strong>Population density = Number of individuals / Area (or volume)</strong></p>\n<p class=\"learn-p\">High density means many individuals per unit area; low density means few. Density affects resource availability, competition, and disease spread.</p>\n\n<h3 class=\"learn-subheading\">2. Population Size</h3>\n<p class=\"learn-p\">Population size is the total number of individuals in a population. It is estimated using:</p>\n<ul class=\"learn-list\">\n  <li><strong>Direct count:</strong> Count all individuals (practical only for small, visible populations)</li>\n  <li><strong>Quadrat method:</strong> Count organisms in sample squares (quadrats) and extrapolate</li>\n  <li><strong>Mark-recapture (Lincoln-Petersen index):</strong> Capture, mark, release, then recapture; use the ratio to estimate total: N = (M × C) / R, where M = marked first catch, C = second catch, R = marked in second catch</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Population Growth</h3>\n<p class=\"learn-p\">Population size changes according to:</p>\n<p class=\"learn-p\" style=\"text-align:center;\"><strong>Population change = (Births + Immigration) − (Deaths + Emigration)</strong></p>\n<ul class=\"learn-list\">\n  <li><strong>Exponential (J-shaped) growth:</strong> Occurs when resources are unlimited; population grows at an accelerating rate</li>\n  <li><strong>Logistic (S-shaped) growth:</strong> Growth slows as the population approaches the <strong>carrying capacity (K)</strong> — the maximum population size the environment can sustain</li>\n</ul>\n\n<h3 class=\"learn-subheading\">4. Population Regulation Factors</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Factor Type</th><th>Examples</th><th>Effect</th></tr></thead>\n    <tbody>\n      <tr><td>Density-dependent (biotic)</td><td>Competition, predation, disease, parasitism</td><td>Effect intensifies as density increases</td></tr>\n      <tr><td>Density-independent (abiotic)</td><td>Drought, floods, extreme temperature, fire</td><td>Effect same regardless of density</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> The mark-recapture formula is N = (M × C) / R. The four factors affecting population size are Births, Deaths, Immigration, and Emigration (BDIE). Births and Immigration increase population; Deaths and Emigration decrease it.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Population density = number per unit area. Population size is estimated by direct count, quadrat, or mark-recapture methods. Population growth depends on births + immigration – deaths – emigration. J-shaped growth = unlimited resources; S-shaped (logistic) growth = carrying capacity limits. Density-dependent factors (predation, disease) and density-independent factors (abiotic disasters) regulate populations.\n</div>\n  ",
    "questions": [
      {
        "q": "Population density is defined as:",
        "o": [
          "The total number of individuals in a population",
          "The number of individuals of a species per unit area or volume",
          "The rate of population growth",
          "The maximum number a habitat can support"
        ],
        "a": 1,
        "e": "Population density = number of individuals / area (or volume). It tells us how crowded a population is in a given space. High density means many individuals per unit area, which can increase competition and disease transmission.",
        "h": "Population density = number of individuals per unit area (or volume).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following factors INCREASES population size?",
        "o": ["Emigration", "Increased death rate", "Immigration", "Predation"],
        "a": 2,
        "e": "Population change = (Births + Immigration) − (Deaths + Emigration). Immigration (individuals moving into an area) increases population size. Emigration, increased death rate, and predation all decrease population size.",
        "h": "Population increases by births and immigration; decreases by deaths and emigration.",
        "yr": "SS1"
      },
      {
        "q": "In the mark-recapture method, 40 fish are caught, marked, and released. In a second catch of 60 fish, 8 are marked. What is the estimated population size?",
        "o": ["200", "240", "300", "480"],
        "a": 2,
        "e": "N = (M × C) / R = (40 × 60) / 8 = 2400 / 8 = 300. This formula assumes marked individuals mix randomly with the population and the probability of capture is equal for all individuals.",
        "h": "N = (M × C) / R = (40 × 60) / 8 = 300.",
        "yr": "SS1"
      },
      {
        "q": "The carrying capacity (K) of an environment refers to:",
        "o": [
          "The birth rate of a population",
          "The maximum population size the environment can sustainably support given available resources",
          "The number of species in an ecosystem",
          "The rate of immigration"
        ],
        "a": 1,
        "e": "Carrying capacity (K) is the maximum population size that a particular environment can sustain indefinitely, given the available food, water, space, and other resources. At K, birth rate equals death rate and population size stabilizes.",
        "h": "Carrying capacity (K) = maximum sustainable population size the environment supports.",
        "yr": "SS1"
      },
      {
        "q": "J-shaped population growth occurs when:",
        "o": [
          "Resources are limited and competition is high",
          "Resources are unlimited and the population grows without restriction",
          "The population has reached carrying capacity",
          "Predation is intense"
        ],
        "a": 1,
        "e": "J-shaped (exponential) growth occurs when a population has abundant resources, no significant predation, and no competition — it grows at an accelerating rate. This is typical of populations introduced to a new environment with abundant resources.",
        "h": "J-shaped growth = unlimited resources, no restrictions, exponential increase.",
        "yr": "SS1"
      },
      {
        "q": "S-shaped (logistic) population growth describes:",
        "o": [
          "Unlimited exponential growth",
          "Growth that accelerates, then slows as it approaches carrying capacity, forming an S-curve",
          "A population in immediate decline",
          "Growth that is always constant"
        ],
        "a": 1,
        "e": "Logistic (S-shaped) growth: a population initially grows rapidly (exponential phase), then growth slows as resources become limiting and density-dependent factors increase, eventually leveling off at the carrying capacity (K).",
        "h": "S-shaped growth = rapid growth, then slows, levels at carrying capacity (K).",
        "yr": "SS1"
      },
      {
        "q": "Which method is most appropriate for estimating the population of plants in a meadow?",
        "o": ["Mark-recapture", "Direct individual count", "Quadrat method", "Aerial photography only"],
        "a": 2,
        "e": "The quadrat method is most appropriate for non-mobile organisms like plants. A series of quadrat frames (squares) are randomly placed in the meadow, organisms within each are counted, and the results are scaled up to estimate the total population.",
        "h": "Quadrat method = best for non-mobile organisms (plants, slow-moving invertebrates).",
        "yr": "SS1"
      },
      {
        "q": "Density-dependent factors that regulate population size include:",
        "o": [
          "Floods and droughts",
          "Volcanic eruptions",
          "Competition, predation, and disease",
          "Extreme temperature changes"
        ],
        "a": 2,
        "e": "Density-dependent factors have greater effects when population density is high. As density increases, competition for resources intensifies, predators find prey more easily, and diseases spread more rapidly. These factors naturally regulate population size.",
        "h": "Density-dependent = biotic (competition, predation, disease) — effect increases with density.",
        "yr": "SS1"
      },
      {
        "q": "A wildfire that destroys 50% of a forest population regardless of how many animals live there is an example of a:",
        "o": [
          "Density-dependent factor",
          "Biotic factor",
          "Density-independent factor",
          "Carrying capacity"
        ],
        "a": 2,
        "e": "A wildfire is density-independent — its effect on the population does not depend on how large the population is. It kills a proportion of the population regardless of density. Other density-independent factors include floods, storms, and drought.",
        "h": "Wildfire = density-independent factor (effect unrelated to population density).",
        "yr": "SS1"
      },
      {
        "q": "Emigration, as a population factor, refers to:",
        "o": [
          "Individuals moving INTO an area",
          "Individuals moving OUT of an area to another location",
          "Birth rate of a population",
          "Death due to disease"
        ],
        "a": 1,
        "e": "Emigration is the movement of individuals OUT of a population to another area. It reduces population size in the source population. Immigration is the opposite — individuals moving INTO an area.",
        "h": "Emigration = individuals LEAVING a population (reduces size).",
        "yr": "SS1"
      },
      {
        "q": "The quadrat method involves:",
        "o": [
          "Counting all individuals in an entire area",
          "Catching, marking, and recapturing animals",
          "Counting organisms within randomly placed sample squares and extrapolating to the total area",
          "Photographing organisms from aircraft"
        ],
        "a": 2,
        "e": "In the quadrat method, small square frames (quadrats) of known size are placed randomly in the study area. Organisms within each quadrat are counted. The average count per quadrat is multiplied by the total area to estimate population size.",
        "h": "Quadrat = count in sample squares → extrapolate to total area.",
        "yr": "SS1"
      },
      {
        "q": "When birth rate equals death rate and immigration equals emigration, the population is said to be:",
        "o": ["Growing rapidly", "At zero population growth (stable)", "Declining", "At carrying capacity only"],
        "a": 1,
        "e": "When births + immigration = deaths + emigration, the population size remains constant — this is zero population growth. The population is stable. This can occur at or below carrying capacity.",
        "h": "Births + immigration = deaths + emigration → stable population (zero growth).",
        "yr": "SS1"
      },
      {
        "q": "Intraspecific competition is competition between:",
        "o": [
          "Different species for the same resource",
          "Individuals of the SAME species for the same resource",
          "Predator and prey",
          "Parasite and host"
        ],
        "a": 1,
        "e": "Intraspecific competition occurs between individuals of the SAME species competing for the same limited resources (food, space, mates, water). It is most intense because they have identical resource requirements.",
        "h": "Intraspecific competition = same species competing (most intense).",
        "yr": "SS1"
      },
      {
        "q": "What assumption is made in the mark-recapture method?",
        "o": [
          "All individuals are visible and can be directly counted",
          "Marked individuals mix randomly with the population and marks do not affect survival",
          "The population is only made up of marked individuals",
          "The population changes rapidly between captures"
        ],
        "a": 1,
        "e": "The mark-recapture method assumes: (1) marked individuals mix randomly back into the population, (2) marks don't affect survival or behaviour, (3) population size and composition don't change significantly between the two sampling occasions.",
        "h": "Mark-recapture assumes random mixing, marks don't affect survival, stable population.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following would cause a population to DECREASE in size?",
        "o": [
          "High birth rate",
          "Low predation pressure",
          "High emigration and high death rate",
          "Abundant food resources"
        ],
        "a": 2,
        "e": "Population decreases when deaths + emigration exceed births + immigration. High emigration (many leaving) combined with high death rate (many dying) will reduce population size. Abundant food and low predation generally increase population.",
        "h": "Population decreases: high death rate + high emigration > births + immigration.",
        "yr": "SS1"
      },
      {
        "q": "The relationship between population size and time during logistic growth shows the population:",
        "o": [
          "Growing continuously without limit",
          "Declining immediately",
          "Growing rapidly, then slowing and stabilizing at carrying capacity",
          "Remaining constant throughout"
        ],
        "a": 2,
        "e": "Logistic (S-shaped) growth: the population starts with slow growth (lag phase), then grows rapidly (exponential phase), then slows as resources become limiting, and finally levels off at the carrying capacity (K). This forms an 'S' (sigmoid) curve.",
        "h": "Logistic growth: slow start → rapid growth → slows → levels at K (S-curve).",
        "yr": "SS1"
      },
      {
        "q": "Interspecific competition involves:",
        "o": [
          "Individuals of the same species competing",
          "Organisms of DIFFERENT species competing for the same resource",
          "One organism eating another",
          "One organism living inside another"
        ],
        "a": 1,
        "e": "Interspecific competition occurs between individuals of DIFFERENT species that require the same limited resource. For example, lions and hyenas competing for the same prey in a savanna.",
        "h": "Interspecific competition = different species competing for same resource.",
        "yr": "SS1"
      },
      {
        "q": "What does the acronym BDIE help us remember in population dynamics?",
        "o": [
          "Types of biotic factors",
          "The four factors that change population size: Births, Deaths, Immigration, Emigration",
          "Levels of ecological organisation",
          "Types of food chains"
        ],
        "a": 1,
        "e": "BDIE stands for Births, Deaths, Immigration, Emigration — the four factors that determine changes in population size. Population change = (B + I) − (D + E). B and I increase population; D and E decrease it.",
        "h": "BDIE: Births + Immigration increase; Deaths + Emigration decrease population.",
        "yr": "SS1"
      },
      {
        "q": "High population density is likely to increase the spread of disease because:",
        "o": [
          "Diseases only spread when density is low",
          "Crowding increases contact between individuals, making transmission of pathogens easier",
          "High density reduces immune function",
          "Pathogens disappear at low density"
        ],
        "a": 1,
        "e": "In a dense population, individuals are in closer contact, making it easier for pathogens (viruses, bacteria, parasites) to spread from host to host. This is why epidemics are more likely in densely populated areas — disease is a density-dependent regulatory factor.",
        "h": "High density → more contact → easier pathogen transmission → density-dependent factor.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following correctly describes natality?",
        "o": [
          "The death rate of a population",
          "The movement of individuals into a population",
          "The birth rate — the number of new individuals added to the population per unit time",
          "The maximum size of a population"
        ],
        "a": 2,
        "e": "Natality (birth rate) is the number of new individuals born into a population per unit time. Together with mortality (death rate), immigration, and emigration, it determines the overall rate of population change.",
        "h": "Natality = birth rate (new individuals added to population per unit time).",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Functioning Ecosystem",
    "topicCode": "SS1-BIO-13",
    "module": "Ecology",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  A <span class=\"learn-keyword\">functioning ecosystem</span> depends on the continuous flow of energy and cycling of materials. Organisms are classified by how they obtain energy: <span class=\"learn-keyword\">autotrophs</span> (producers) and <span class=\"learn-keyword\">heterotrophs</span> (consumers). Energy flows through food chains and food webs, while materials cycle continuously through biogeochemical cycles.\n</div>\n\n<h3 class=\"learn-subheading\">1. Autotrophy and Heterotrophy in Ecosystems</h3>\n<ul class=\"learn-list\">\n  <li><strong>Producers (Autotrophs):</strong> Plants and algae that manufacture food via photosynthesis — form the base of all food chains</li>\n  <li><strong>Primary consumers (Herbivores):</strong> Eat producers — e.g., grasshoppers, rabbits</li>\n  <li><strong>Secondary consumers (Carnivores/Omnivores):</strong> Eat primary consumers — e.g., frogs, foxes</li>\n  <li><strong>Tertiary consumers:</strong> Eat secondary consumers — e.g., eagles</li>\n  <li><strong>Decomposers:</strong> Break down dead organic matter and recycle nutrients — e.g., bacteria, fungi</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Food Chains and Food Webs</h3>\n<p class=\"learn-p\">A <strong>food chain</strong> shows the flow of energy from one organism to the next:</p>\n<p class=\"learn-p\" style=\"text-align:center;\"><em>Grass → Grasshopper → Frog → Snake → Eagle</em></p>\n<p class=\"learn-p\">A <strong>food web</strong> is a network of interconnected food chains, showing the more realistic feeding relationships in an ecosystem. It provides stability — if one species disappears, others can compensate.</p>\n\n<h3 class=\"learn-subheading\">3. Trophic Levels</h3>\n<p class=\"learn-p\">Each step in a food chain is a <strong>trophic level</strong>:</p>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Trophic Level</th><th>Organism Type</th><th>Example</th></tr></thead>\n    <tbody>\n      <tr><td>1st (T1)</td><td>Producers</td><td>Grass, algae</td></tr>\n      <tr><td>2nd (T2)</td><td>Primary consumers (Herbivores)</td><td>Grasshopper, rabbit</td></tr>\n      <tr><td>3rd (T3)</td><td>Secondary consumers</td><td>Frog, fox</td></tr>\n      <tr><td>4th (T4)</td><td>Tertiary consumers</td><td>Snake, eagle</td></tr>\n      <tr><td>Decomposers</td><td>Decomposers/Detritivores</td><td>Bacteria, fungi, earthworms</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">4. Energy Flow and the 10% Rule</h3>\n<p class=\"learn-p\">Energy enters ecosystems through photosynthesis and is lost at each trophic level as heat during respiration. Only about <strong>10% of the energy</strong> at one trophic level is transferred to the next. This is why food chains rarely exceed 4–5 levels and why pyramids of energy are always upright.</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Energy flows in ONE direction through food chains (producer → consumer). Materials (carbon, nitrogen, water) cycle. Remember the 10% rule: only 10% of energy passes from one level to the next (90% is lost as heat). Arrows in food chains point in the direction of energy flow — from prey to predator.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Ecosystems function through energy flow (one-way) and nutrient cycling. Producers (autotrophs) make food; consumers (heterotrophs) eat others; decomposers recycle nutrients. Food chains show energy pathways; food webs show complexity. Five trophic levels: producers, primary, secondary, tertiary consumers, and decomposers. Only ~10% of energy transfers between trophic levels.\n</div>\n  ",
    "questions": [
      {
        "q": "Which organisms form the BASE of every food chain?",
        "o": ["Carnivores", "Decomposers", "Producers (autotrophs)", "Herbivores"],
        "a": 2,
        "e": "Producers (autotrophs — mainly green plants and algae) form the base of every food chain. They capture sunlight energy through photosynthesis and convert it into organic compounds that all other trophic levels ultimately depend on.",
        "h": "Food chain base = producers/autotrophs (plants, algae).",
        "yr": "SS1"
      },
      {
        "q": "In the food chain: Grass → Grasshopper → Frog → Snake → Eagle, the frog is a:",
        "o": ["Producer", "Primary consumer", "Secondary consumer", "Tertiary consumer"],
        "a": 2,
        "e": "In this food chain, Grass (producer, T1) → Grasshopper (primary consumer, T2) → Frog (secondary consumer, T3) → Snake (T4) → Eagle (T5). The frog eats the grasshopper (primary consumer) making it a secondary consumer.",
        "h": "Frog eats grasshopper (primary consumer) → frog = secondary consumer.",
        "yr": "SS1"
      },
      {
        "q": "A food web is more realistic than a single food chain because:",
        "o": [
          "It shows only two species",
          "It represents only plants",
          "It shows the complex network of feeding relationships between many species",
          "It eliminates decomposers"
        ],
        "a": 2,
        "e": "A food web shows multiple interconnected food chains and represents the true complexity of feeding relationships in an ecosystem. Most organisms eat more than one type of food and are eaten by more than one predator, creating a web of interactions.",
        "h": "Food web = network of interconnected food chains (more realistic than one chain).",
        "yr": "SS1"
      },
      {
        "q": "The 10% rule in energy transfer means that:",
        "o": [
          "All energy is passed from one trophic level to the next",
          "Only about 10% of the energy at one trophic level is transferred to the next; 90% is lost as heat",
          "10% of organisms survive predation",
          "Producers use 10% of sunlight"
        ],
        "a": 1,
        "e": "At each trophic level, organisms use energy for respiration, movement, and heat production. Only ~10% of the energy is stored in body tissues and transferred to the next trophic level when eaten. This limits food chain length to 4–5 levels.",
        "h": "10% rule: only 10% of energy transfers between trophic levels; 90% lost as heat.",
        "yr": "SS1"
      },
      {
        "q": "Decomposers play which essential role in an ecosystem?",
        "o": [
          "They produce food through photosynthesis",
          "They eat living animals",
          "They break down dead organic matter and recycle nutrients back into the ecosystem",
          "They form the base of food chains"
        ],
        "a": 2,
        "e": "Decomposers (bacteria, fungi, earthworms) break down dead organisms and waste material, releasing nutrients (nitrogen, phosphorus, carbon) back into the soil and atmosphere. Without decomposers, nutrients would remain locked in dead matter.",
        "h": "Decomposers = break down dead matter → recycle nutrients → essential for nutrient cycling.",
        "yr": "SS1"
      },
      {
        "q": "Which trophic level do herbivores (plant-eaters) occupy?",
        "o": ["First trophic level", "Second trophic level", "Third trophic level", "Fourth trophic level"],
        "a": 1,
        "e": "Herbivores are primary consumers — they eat producers (plants at the 1st trophic level) and therefore occupy the 2nd trophic level. Examples: rabbits, grasshoppers, cattle, and caterpillars.",
        "h": "Herbivores = primary consumers = 2nd trophic level.",
        "yr": "SS1"
      },
      {
        "q": "The arrow in a food chain (e.g., Grass → Rabbit) represents:",
        "o": [
          "The direction the organism moves",
          "The direction of energy flow (from prey to predator)",
          "The size of the organism",
          "The direction of nutrient cycling"
        ],
        "a": 1,
        "e": "Arrows in food chains point in the direction of energy flow — from the organism being eaten (prey/prey) to the organism eating it (predator/consumer). Grass → Rabbit means energy flows FROM grass TO rabbit.",
        "h": "Arrow = direction of energy flow (from food source to consumer).",
        "yr": "SS1"
      },
      {
        "q": "Why does an ecosystem usually support fewer tertiary consumers than primary consumers?",
        "o": [
          "Tertiary consumers are larger",
          "Due to the 10% energy rule, less energy is available at higher trophic levels",
          "Tertiary consumers reproduce more slowly",
          "Primary consumers eat more food"
        ],
        "a": 1,
        "e": "With each trophic level transfer, ~90% of energy is lost as heat. So much less energy reaches tertiary consumers than primary consumers. Less available energy supports fewer individuals — hence fewer top predators (tertiary consumers) in every ecosystem.",
        "h": "10% energy rule → less energy at higher levels → fewer top predators.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a decomposer?",
        "o": ["Grasshopper", "Eagle", "Mushroom (fungus)", "Grass"],
        "a": 2,
        "e": "Mushrooms (fungi) are decomposers — they secrete enzymes onto dead organic matter, break it down, and absorb the nutrients. Other decomposers include bacteria and earthworms. They play a critical role in nutrient cycling.",
        "h": "Mushroom (fungus) = decomposer (breaks down dead matter).",
        "yr": "SS1"
      },
      {
        "q": "Energy enters most ecosystems through:",
        "o": [
          "Decomposition by bacteria",
          "Photosynthesis by producers converting sunlight to chemical energy",
          "Respiration by all organisms",
          "The consumption of animals by carnivores"
        ],
        "a": 1,
        "e": "Energy enters most ecosystems via photosynthesis — producers capture light energy from the sun and convert it into chemical energy stored in glucose. This is the primary energy input for almost all food chains on Earth.",
        "h": "Energy enters ecosystems via photosynthesis by producers (sunlight → chemical energy).",
        "yr": "SS1"
      },
      {
        "q": "A pyramid of numbers shows that:",
        "o": [
          "Energy increases from producers to top consumers",
          "There are usually more organisms at lower trophic levels than at higher levels",
          "All trophic levels have equal numbers",
          "Decomposers are always most numerous"
        ],
        "a": 1,
        "e": "A pyramid of numbers typically shows more organisms at lower trophic levels (many producers) and fewer at higher levels (few top predators). This reflects the 10% energy rule — less energy is available at each successive level.",
        "h": "Pyramid of numbers: more at bottom (producers), fewer at top (consumers).",
        "yr": "SS1"
      },
      {
        "q": "If all frogs were removed from the food chain: Grass → Grasshopper → Frog → Snake → Eagle, which would most immediately increase in population?",
        "o": ["Grass", "Eagle", "Grasshopper", "Snake"],
        "a": 2,
        "e": "If frogs (which eat grasshoppers) are removed, grasshoppers have fewer predators. With less predation pressure, grasshopper populations would increase (overpopulation). This could then lead to overgrazing of grass.",
        "h": "Remove frogs → less predation on grasshoppers → grasshopper population increases.",
        "yr": "SS1"
      },
      {
        "q": "Carnivores occupy which position in a food chain?",
        "o": [
          "Only the first trophic level",
          "At least the third trophic level (secondary consumers or higher)",
          "Only the second trophic level",
          "They are decomposers"
        ],
        "a": 1,
        "e": "Carnivores eat other animals and therefore occupy at least the 3rd trophic level (secondary consumers) or higher. A carnivore that eats a herbivore is a secondary consumer (T3); one that eats a secondary consumer is a tertiary consumer (T4).",
        "h": "Carnivores = T3 or higher (eat herbivores or other carnivores).",
        "yr": "SS1"
      },
      {
        "q": "Which statement about energy flow in ecosystems is correct?",
        "o": [
          "Energy cycles continuously like water and carbon",
          "Energy flows in one direction and is eventually lost as heat",
          "Energy is created by consumers",
          "Energy is recycled by decomposers"
        ],
        "a": 1,
        "e": "Energy flows in ONE direction through ecosystems — from the sun → producers → consumers → decomposers — and is ultimately lost as heat. Unlike nutrients (which cycle), energy does not cycle. New energy must continuously enter via photosynthesis.",
        "h": "Energy flows ONE WAY and is lost as heat (unlike nutrients which cycle).",
        "yr": "SS1"
      },
      {
        "q": "An omnivore is an organism that:",
        "o": [
          "Eats only plants",
          "Eats only animals",
          "Eats both plants and animals",
          "Only decomposes dead matter"
        ],
        "a": 2,
        "e": "Omnivores eat both plants (autotrophs) and animals (heterotrophs). Examples include humans, pigs, bears, and crows. Omnivores can occupy multiple trophic levels simultaneously depending on what they are eating.",
        "h": "Omnivore = eats both plants and animals (occupies multiple trophic levels).",
        "yr": "SS1"
      },
      {
        "q": "Food webs provide greater ecosystem stability than single food chains because:",
        "o": [
          "They have fewer species",
          "Multiple feeding pathways mean the loss of one species has less overall impact",
          "They produce more energy",
          "They have no decomposers"
        ],
        "a": 1,
        "e": "In a food web, consumers have multiple food sources. If one prey species disappears, consumers can switch to alternatives. This redundancy makes the ecosystem more resilient. In a simple food chain, loss of any link can collapse the chain.",
        "h": "Food webs = multiple pathways = greater stability (loss of one species has less impact).",
        "yr": "SS1"
      },
      {
        "q": "At which trophic level is the MOST energy available in an ecosystem?",
        "o": ["Tertiary consumers", "Secondary consumers", "Primary consumers", "Producers"],
        "a": 3,
        "e": "Producers (1st trophic level) contain the most energy in an ecosystem because they have captured energy directly from the sun. At each subsequent level, ~90% of energy is lost as heat, so progressively less energy is available.",
        "h": "Most energy at T1 (producers); least energy at highest trophic level.",
        "yr": "SS1"
      },
      {
        "q": "Detritivores differ from decomposers in that detritivores:",
        "o": [
          "Are the same as decomposers",
          "Physically break down and ingest dead organic matter (e.g., earthworms, woodlice)",
          "Only photosynthesize",
          "Are always unicellular"
        ],
        "a": 1,
        "e": "Detritivores (e.g., earthworms, woodlice, millipedes) physically consume and break down dead organic matter. Decomposers (bacteria, fungi) break it down chemically by secreting enzymes externally. Both recycle nutrients but by different mechanisms.",
        "h": "Detritivores = physically eat dead matter (earthworms); decomposers = chemical breakdown (fungi, bacteria).",
        "yr": "SS1"
      },
      {
        "q": "If 10,000 kJ of energy is stored by grass (producers), how much energy is available to primary consumers?",
        "o": ["10,000 kJ", "1,000 kJ", "100 kJ", "500 kJ"],
        "a": 1,
        "e": "Using the 10% rule: only 10% of the energy at one trophic level is transferred to the next. 10% of 10,000 kJ = 1,000 kJ available to primary consumers (herbivores). The other 9,000 kJ is lost mainly as heat during respiration.",
        "h": "10% rule: 10% of 10,000 kJ = 1,000 kJ to primary consumers.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following correctly describes a trophic level?",
        "o": [
          "The number of organisms in an ecosystem",
          "Each step in a food chain representing an organism's feeding position",
          "The total energy in an ecosystem",
          "The number of food chains in a food web"
        ],
        "a": 1,
        "e": "A trophic level is each step in a food chain that represents the feeding (nutritional) position of an organism. Producers are T1, herbivores T2, carnivores T3, etc. Each level is defined by how many energy-transfer steps it is from the producers.",
        "h": "Trophic level = each feeding step in a food chain.",
        "yr": "SS1"
      }
    ]
  }
  ,
  {
    "topic": "Energy Transformation in Nature",
    "topicCode": "SS1-BIO-14",
    "module": "Ecology",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  Energy constantly flows and transforms through ecosystems. The sun is the primary source of energy for almost all life. Through <span class=\"learn-keyword\">photosynthesis</span>, producers convert solar energy into chemical energy. This energy is then transferred through <span class=\"learn-keyword\">food chains</span> and measured as <span class=\"learn-keyword\">productivity</span>. Understanding energy transformation is essential for managing ecosystems.\n</div>\n\n<h3 class=\"learn-subheading\">1. Energy Flow Through Ecosystems</h3>\n<ul class=\"learn-list\">\n  <li><strong>Source:</strong> Sunlight (solar energy) is the primary energy source</li>\n  <li>Plants convert ~1–2% of incoming solar energy into chemical energy via photosynthesis</li>\n  <li>Energy is transferred along food chains as organisms eat one another</li>\n  <li>At each transfer, ~90% is lost as heat (respiration, movement, heat loss)</li>\n  <li>Energy flow is <strong>unidirectional</strong> — it does not cycle</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Pyramid of Energy</h3>\n<p class=\"learn-p\">A <span class=\"learn-keyword\">pyramid of energy</span> (kcal/m²/year) shows the total energy at each trophic level per unit area per year. It is <strong>always upright</strong> (widest at producer level, narrowest at top consumer) because energy is always lost at each transfer.</p>\n<p class=\"learn-p\">Unlike pyramids of numbers or biomass, the pyramid of energy is NEVER inverted.</p>\n\n<h3 class=\"learn-subheading\">3. Productivity</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Term</th><th>Definition</th></tr></thead>\n    <tbody>\n      <tr><td>Gross Primary Productivity (GPP)</td><td>Total rate of photosynthesis — total chemical energy fixed by producers</td></tr>\n      <tr><td>Net Primary Productivity (NPP)</td><td>GPP minus energy used by the plant in its own respiration: NPP = GPP − Respiration</td></tr>\n      <tr><td>Secondary Productivity</td><td>Rate of energy storage by consumers (animals)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">4. Biogeochemical Cycles</h3>\n<p class=\"learn-p\">Unlike energy, materials (carbon, nitrogen, water) <strong>cycle</strong> continuously through biotic and abiotic components:</p>\n<ul class=\"learn-list\">\n  <li><strong>Carbon cycle:</strong> CO₂ fixation by photosynthesis → respiration/decomposition releases CO₂ back</li>\n  <li><strong>Nitrogen cycle:</strong> N₂ fixation (by bacteria) → nitrification → assimilation → denitrification</li>\n  <li><strong>Water cycle:</strong> Evaporation/transpiration → condensation → precipitation → runoff/infiltration</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Key difference: ENERGY flows one way (sun → producers → consumers → heat). MATERIALS cycle (carbon, nitrogen, water cycle repeatedly). NPP = GPP − Respiration. The pyramid of energy is ALWAYS upright — it can never be inverted because energy is always lost at each trophic level.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Solar energy enters ecosystems via photosynthesis; only ~10% transfers between trophic levels. Energy flow is one-directional. Pyramids of energy are always upright. NPP = GPP − Respiration. Unlike energy, carbon, nitrogen, and water cycle through biotic and abiotic components continuously.\n</div>\n  ",
    "questions": [
      {
        "q": "The primary source of energy for almost all ecosystems is:",
        "o": ["Chemical energy from soil", "Sunlight (solar energy)", "Energy from decomposers", "Geothermal energy"],
        "a": 1,
        "e": "Sunlight is the primary energy source for most ecosystems. Producers capture solar energy via photosynthesis and convert it into chemical energy (glucose), which then flows through food chains.",
        "h": "Primary energy source for ecosystems = sunlight (captured by photosynthesis).",
        "yr": "SS1"
      },
      {
        "q": "Net Primary Productivity (NPP) is calculated as:",
        "o": [
          "GPP + Respiration",
          "GPP − Respiration (energy the plant uses for itself)",
          "GPP × 10%",
          "Respiration − GPP"
        ],
        "a": 1,
        "e": "NPP = GPP − Respiration. GPP is the total energy fixed by photosynthesis. NPP is what remains after the plant uses some energy for its own respiration — this is what is actually available to consumers.",
        "h": "NPP = GPP − Respiration (energy available to consumers).",
        "yr": "SS1"
      },
      {
        "q": "The pyramid of energy is always upright because:",
        "o": [
          "Producers are always larger than consumers",
          "Energy is always lost as heat at each trophic level, so less is available at higher levels",
          "Consumers always eat more than producers",
          "Energy is recycled at each level"
        ],
        "a": 1,
        "e": "The pyramid of energy is always upright because energy is lost (~90%) at each trophic level through respiration and heat production. The total energy at each successive level is always less than the level below, making it impossible to invert.",
        "h": "Pyramid of energy always upright: energy lost as heat at each level.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following correctly describes carbon cycling?",
        "o": [
          "CO₂ is permanently removed from the atmosphere by photosynthesis",
          "CO₂ is fixed by photosynthesis, released by respiration and decomposition, cycling continuously",
          "Carbon only moves through animals",
          "Carbon is not recycled in ecosystems"
        ],
        "a": 1,
        "e": "In the carbon cycle, CO₂ is removed from the atmosphere by photosynthesis (fixed into organic compounds), and returned to the atmosphere through respiration (by all organisms) and decomposition (by decomposers). The cycle is continuous.",
        "h": "Carbon cycle: photosynthesis fixes CO₂ → respiration/decomposition releases it back.",
        "yr": "SS1"
      },
      {
        "q": "Gross Primary Productivity (GPP) refers to:",
        "o": [
          "Energy used by animals in the ecosystem",
          "Total energy fixed by photosynthesis per unit area per unit time",
          "Net energy available to consumers",
          "Energy lost in respiration"
        ],
        "a": 1,
        "e": "GPP is the total rate of energy fixation by photosynthesis in producers per unit area per unit time. It includes energy used by the plant in respiration. NPP (= GPP − Respiration) is the energy actually available to consumers.",
        "h": "GPP = total energy fixed by photosynthesis (includes plant's own respiration).",
        "yr": "SS1"
      },
      {
        "q": "In the nitrogen cycle, nitrogen fixation is carried out by:",
        "o": ["Green plants", "Animals", "Nitrogen-fixing bacteria (e.g., Rhizobium)", "Decomposers only"],
        "a": 2,
        "e": "Nitrogen fixation converts atmospheric N₂ (inert gas) into ammonia (NH₃) or nitrates that plants can absorb. This is done mainly by nitrogen-fixing bacteria (Rhizobium in legume roots, free-living Azotobacter, and cyanobacteria).",
        "h": "Nitrogen fixation = bacteria (Rhizobium, Azotobacter) converting N₂ to NH₃/nitrates.",
        "yr": "SS1"
      },
      {
        "q": "What percentage of solar energy is typically captured by producers through photosynthesis?",
        "o": ["50%", "25%", "10%", "About 1–2%"],
        "a": 3,
        "e": "Green plants capture only about 1–2% of the total solar energy that falls on them. Much of the solar energy is reflected, transmitted through leaves, or absorbed as heat. This makes photosynthesis relatively inefficient.",
        "h": "Producers capture only ~1–2% of solar energy via photosynthesis.",
        "yr": "SS1"
      },
      {
        "q": "Transpiration in the water cycle refers to:",
        "o": [
          "Water evaporating from soil",
          "Water loss from plant leaves through stomata",
          "Precipitation falling as rain",
          "Water infiltrating the soil"
        ],
        "a": 1,
        "e": "Transpiration is the evaporation of water from plant leaves through stomata. Together with evaporation from water surfaces, it returns water vapour to the atmosphere, contributing to cloud formation and precipitation.",
        "h": "Transpiration = water lost from plant leaves through stomata → returns water to atmosphere.",
        "yr": "SS1"
      },
      {
        "q": "Secondary productivity in an ecosystem refers to:",
        "o": [
          "Energy fixed by photosynthesis",
          "Rate of energy storage by consumers (animals)",
          "Energy lost to decomposers",
          "Total solar energy available"
        ],
        "a": 1,
        "e": "Secondary productivity is the rate of energy storage (production of biomass) by consumers (heterotrophs/animals) in an ecosystem. It depends on primary productivity — it cannot exceed what is available from producers.",
        "h": "Secondary productivity = rate of biomass/energy storage by consumers.",
        "yr": "SS1"
      },
      {
        "q": "Denitrification in the nitrogen cycle involves:",
        "o": [
          "Bacteria converting N₂ to ammonia",
          "Plants absorbing nitrates",
          "Bacteria converting nitrates back to N₂ gas, releasing it to the atmosphere",
          "Animals excreting urea"
        ],
        "a": 2,
        "e": "Denitrification is carried out by denitrifying bacteria (e.g., Pseudomonas) in anaerobic soil conditions. They reduce nitrates (NO₃⁻) back to nitrogen gas (N₂), which is released to the atmosphere, completing the nitrogen cycle.",
        "h": "Denitrification = bacteria convert nitrates → N₂ gas (back to atmosphere).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is TRUE about energy in ecosystems?",
        "o": [
          "Energy cycles like carbon and nitrogen",
          "Energy is created by consumers",
          "Energy flows in one direction and is progressively lost as heat",
          "All solar energy is captured by photosynthesis"
        ],
        "a": 2,
        "e": "Energy flows in one direction (sun → producers → consumers → heat). Unlike matter (carbon, nitrogen, water), energy does not cycle. At each trophic level, most energy (~90%) is lost as heat through respiration.",
        "h": "Energy: one-directional flow, progressively lost as heat (does NOT cycle).",
        "yr": "SS1"
      },
      {
        "q": "In the water cycle, which process returns water vapour to the atmosphere from land surfaces?",
        "o": ["Condensation", "Precipitation", "Evapotranspiration (evaporation + transpiration)", "Infiltration"],
        "a": 2,
        "e": "Evapotranspiration combines evaporation (water evaporating from soil, water bodies) and transpiration (water lost from plant leaves). Both processes convert liquid water to water vapour and return it to the atmosphere.",
        "h": "Evapotranspiration = evaporation + transpiration = returns water to atmosphere.",
        "yr": "SS1"
      },
      {
        "q": "If a producer has a GPP of 500 kcal/m²/year and uses 200 kcal/m²/year in respiration, the NPP is:",
        "o": ["700 kcal", "500 kcal", "300 kcal", "200 kcal"],
        "a": 2,
        "e": "NPP = GPP − Respiration = 500 − 200 = 300 kcal/m²/year. This 300 kcal is the net energy available to primary consumers in this ecosystem.",
        "h": "NPP = GPP − Respiration = 500 − 200 = 300 kcal.",
        "yr": "SS1"
      },
      {
        "q": "Why cannot energy cycle like carbon or nitrogen?",
        "o": [
          "Energy is too small to cycle",
          "Energy is continuously degraded to heat (entropy increases) and cannot be reused in biological systems",
          "Energy is stored permanently in producers",
          "Only animals lose energy"
        ],
        "a": 1,
        "e": "According to the second law of thermodynamics, energy transformations always produce heat, increasing entropy. Heat energy cannot be recaptured and reused for biological work. New energy input (from the sun) is therefore constantly required.",
        "h": "Energy is lost as heat (entropy); unlike matter, it cannot be reused → one-way flow.",
        "yr": "SS1"
      },
      {
        "q": "Which organisms are responsible for nitrification in the nitrogen cycle?",
        "o": [
          "Green plants",
          "Nitrifying bacteria (e.g., Nitrosomonas, Nitrobacter)",
          "Animals",
          "Denitrifying bacteria"
        ],
        "a": 1,
        "e": "Nitrification is the conversion of ammonia (NH₃) to nitrites (NO₂⁻) by Nitrosomonas, and then to nitrates (NO₃⁻) by Nitrobacter. Nitrates are the form most readily absorbed by plant roots.",
        "h": "Nitrification: Nitrosomonas converts NH₃ → NO₂⁻; Nitrobacter converts NO₂⁻ → NO₃⁻.",
        "yr": "SS1"
      },
      {
        "q": "A pyramid of numbers may be inverted (bottom narrower than top) when:",
        "o": [
          "There are many producers",
          "A single large producer (e.g., one tree) supports many consumers",
          "Energy loss is very small",
          "Decomposers are absent"
        ],
        "a": 1,
        "e": "A pyramid of numbers can be inverted when one large organism (e.g., one oak tree) supports many smaller herbivores (e.g., thousands of insects), which support even more parasites. The pyramid of energy, however, can NEVER be inverted.",
        "h": "Pyramid of numbers can invert (one tree → many insects); pyramid of energy NEVER inverts.",
        "yr": "SS1"
      },
      {
        "q": "How does burning of fossil fuels affect the carbon cycle?",
        "o": [
          "It removes CO₂ from the atmosphere",
          "It releases CO₂ stored for millions of years back into the atmosphere, increasing atmospheric CO₂",
          "It has no effect on the carbon cycle",
          "It converts CO₂ to oxygen"
        ],
        "a": 1,
        "e": "Fossil fuels (coal, oil, gas) contain carbon that was fixed by organisms millions of years ago. Burning them rapidly releases this stored CO₂ back into the atmosphere, disrupting the balance of the carbon cycle and contributing to climate change.",
        "h": "Burning fossil fuels releases ancient stored CO₂ → disrupts carbon cycle → climate change.",
        "yr": "SS1"
      },
      {
        "q": "Which process in the nitrogen cycle converts ammonia to nitrates (usable by plants)?",
        "o": ["Nitrogen fixation", "Denitrification", "Nitrification", "Assimilation"],
        "a": 2,
        "e": "Nitrification converts ammonia (NH₃) to nitrites (NO₂⁻) and then to nitrates (NO₃⁻) via bacteria. Nitrates are the primary form in which plants absorb nitrogen from the soil for making amino acids and proteins.",
        "h": "Nitrification: NH₃ → NO₂⁻ → NO₃⁻ (bacteria; makes N usable by plants).",
        "yr": "SS1"
      },
      {
        "q": "Which ecosystem tends to have the HIGHEST primary productivity?",
        "o": ["Arctic tundra", "Hot desert", "Tropical rainforest", "Open ocean"],
        "a": 2,
        "e": "Tropical rainforests have the highest primary productivity because they receive abundant sunlight, high temperatures, and high rainfall — ideal conditions for rapid, year-round photosynthesis. Deserts and tundra have low productivity due to water or temperature limitations.",
        "h": "Tropical rainforest = highest primary productivity (sunlight + warmth + rainfall).",
        "yr": "SS1"
      },
      {
        "q": "The role of decomposers in nutrient cycling is to:",
        "o": [
          "Fix atmospheric nitrogen",
          "Perform photosynthesis",
          "Break down dead organic matter and release inorganic nutrients back into the soil and atmosphere",
          "Consume living animals"
        ],
        "a": 2,
        "e": "Decomposers (bacteria and fungi) break down dead plants and animals, releasing the nutrients locked in organic compounds (carbon, nitrogen, phosphorus, etc.) back into the soil and atmosphere, where they can be reused by producers.",
        "h": "Decomposers break down dead matter → release nutrients → allow nutrient cycling.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Relevance of Biology to Agriculture",
    "topicCode": "SS1-BIO-15",
    "module": "Applied Biology",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Agriculture</span> is the science and practice of cultivating plants and rearing animals for food, fibre, and other products. Biology underpins almost every aspect of modern agriculture — from genetics and soil science to pest management and animal husbandry.\n</div>\n\n<h3 class=\"learn-subheading\">1. Genetics and Crop Improvement</h3>\n<ul class=\"learn-list\">\n  <li><strong>Plant breeding:</strong> Selection of desirable traits (high yield, disease resistance, drought tolerance) — uses knowledge of heredity and genetics</li>\n  <li><strong>Hybridization:</strong> Crossing two varieties with different desirable traits to produce a better hybrid (e.g., hybrid maize with higher yield)</li>\n  <li><strong>Genetic engineering:</strong> Introduction of specific genes to improve crops (e.g., Bt cotton resistant to pests)</li>\n  <li><strong>Mutation breeding:</strong> Inducing beneficial mutations using radiation or chemicals</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Soil Science (Edaphology)</h3>\n<ul class=\"learn-list\">\n  <li>Knowledge of soil structure, pH, and microbial activity is essential for crop production</li>\n  <li><strong>Nitrogen-fixing bacteria</strong> (Rhizobium in legume roots) improve soil fertility naturally</li>\n  <li><strong>Decomposers</strong> (bacteria, fungi) convert dead matter to humus, enriching soil</li>\n  <li>Crop rotation maintains soil fertility and breaks pest cycles</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Pest and Disease Control</h3>\n<ul class=\"learn-list\">\n  <li><strong>Biological control:</strong> Using natural predators or parasites to control pests (e.g., using ladybirds to control aphids)</li>\n  <li><strong>Chemical control:</strong> Pesticides, fungicides, herbicides — developed using knowledge of biochemistry</li>\n  <li>Understanding pathogen life cycles (bacteria, fungi, viruses) is essential for disease management</li>\n</ul>\n\n<h3 class=\"learn-subheading\">4. Animal Husbandry</h3>\n<ul class=\"learn-list\">\n  <li>Knowledge of animal physiology, nutrition, reproduction, and genetics guides livestock management</li>\n  <li>Selective breeding improves milk yield, meat quality, egg production</li>\n  <li>Vaccination programs protect livestock from diseases (application of immunology)</li>\n  <li>Understanding animal nutrition ensures proper feeding programs</li>\n</ul>\n\n<h3 class=\"learn-subheading\">5. Other Applications</h3>\n<ul class=\"learn-list\">\n  <li><strong>Photosynthesis research:</strong> Helps maximize crop yield by optimizing growing conditions</li>\n  <li><strong>Fermentation:</strong> Production of silage, compost, and biogas from agricultural waste</li>\n  <li><strong>Aquaculture:</strong> Application of fish biology for fish farming</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Key links between biology and agriculture: Genetics → crop improvement; Microbiology → soil fertility (nitrogen-fixing bacteria, decomposers); Ecology → biological pest control; Physiology → animal husbandry. Remember that crop rotation uses legumes (which harbor nitrogen-fixing bacteria) to restore soil nitrogen.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Biology contributes to agriculture through genetics (crop improvement, hybridization), soil microbiology (nitrogen-fixing bacteria, decomposers), pest/disease control (biological and chemical), animal husbandry (breeding, vaccination, nutrition), and applied research on photosynthesis and fermentation.\n</div>\n  ",
    "questions": [
      {
        "q": "How does knowledge of genetics benefit agriculture?",
        "o": [
          "It helps farmers understand the weather",
          "It enables selective breeding and hybridization to improve crop yield and disease resistance",
          "It explains how soil forms",
          "It is not relevant to agriculture"
        ],
        "a": 1,
        "e": "Genetics allows plant breeders to select and cross organisms with desirable traits (high yield, pest resistance, drought tolerance) to produce superior varieties. This forms the basis of both traditional selective breeding and modern genetic engineering.",
        "h": "Genetics → selective breeding → improved crop yield, disease resistance.",
        "yr": "SS1"
      },
      {
        "q": "Rhizobium bacteria in legume root nodules benefit agriculture by:",
        "o": [
          "Killing soil pests",
          "Fixing atmospheric nitrogen into compounds usable by plants, improving soil fertility",
          "Breaking down pesticides",
          "Producing antibiotics"
        ],
        "a": 1,
        "e": "Rhizobium bacteria fix atmospheric N₂ into ammonia, which is then converted to nitrates — the form plants use to make proteins. This natural nitrogen fixation reduces the need for chemical nitrogen fertilizers and improves soil fertility.",
        "h": "Rhizobium = nitrogen fixation → improves soil fertility for crops.",
        "yr": "SS1"
      },
      {
        "q": "Biological pest control involves:",
        "o": [
          "Spraying chemical pesticides",
          "Using natural predators, parasites, or pathogens to control pest populations",
          "Burning infested crops",
          "Using genetic engineering only"
        ],
        "a": 1,
        "e": "Biological control uses natural enemies of pests to regulate pest populations. Examples: introducing ladybirds to control aphids; using Bacillus thuringiensis (Bt) bacteria to control caterpillars; releasing parasitic wasps against whiteflies.",
        "h": "Biological pest control = natural predators/parasites/pathogens to control pests.",
        "yr": "SS1"
      },
      {
        "q": "Crop rotation in agriculture benefits soil because:",
        "o": [
          "It increases soil acidity",
          "It eliminates all insects from the soil",
          "It maintains soil fertility and breaks pest cycles, especially by including legumes that fix nitrogen",
          "It reduces the need for sunlight"
        ],
        "a": 2,
        "e": "Crop rotation involves growing different crops in sequence on the same land. Including legumes (which host nitrogen-fixing Rhizobium) restores soil nitrogen. Different crops have different pest/disease cycles, so rotation breaks these cycles.",
        "h": "Crop rotation: maintains soil fertility (legumes fix N₂), breaks pest/disease cycles.",
        "yr": "SS1"
      },
      {
        "q": "Hybridization in crop improvement involves:",
        "o": [
          "Planting the same variety repeatedly",
          "Cross-breeding two varieties with different desirable traits to produce superior offspring",
          "Applying fertilizers",
          "Removing weeds"
        ],
        "a": 1,
        "e": "Hybridization is the crossing of two genetically different varieties or species to produce offspring (hybrids) that combine the best traits of both parents. Hybrid maize, for example, may combine the high yield of one variety with the disease resistance of another.",
        "h": "Hybridization = cross two varieties → hybrid with combined desirable traits.",
        "yr": "SS1"
      },
      {
        "q": "Selective breeding in animal farming is used to:",
        "o": [
          "Control disease outbreaks only",
          "Improve desirable traits like milk yield, meat quality, and egg production over generations",
          "Increase the number of parasites",
          "Reduce animal movement"
        ],
        "a": 1,
        "e": "Selective breeding involves choosing animals with the most desirable traits for reproduction. Over many generations, this increases the frequency of beneficial traits in the population — e.g., dairy cows with high milk yield, chickens with high egg production.",
        "h": "Selective breeding = choose best animals for reproduction → improve traits over generations.",
        "yr": "SS1"
      },
      {
        "q": "The role of decomposers in soil fertility is to:",
        "o": [
          "Fix nitrogen from the atmosphere",
          "Break down dead organic matter into humus, releasing nutrients for plant uptake",
          "Directly supply crops with sunlight",
          "Kill soil pests"
        ],
        "a": 1,
        "e": "Decomposers (bacteria and fungi) break down dead plant and animal matter into humus — a rich, organic material. This releases essential nutrients (nitrogen, phosphorus, potassium) in forms plants can absorb, improving soil fertility.",
        "h": "Decomposers → break down organic matter → form humus → release nutrients for plants.",
        "yr": "SS1"
      },
      {
        "q": "Vaccination programs for livestock are an application of:",
        "o": ["Genetics", "Soil science", "Immunology (biological knowledge of immune responses)", "Ecology"],
        "a": 2,
        "e": "Vaccination stimulates the immune system to produce antibodies against specific pathogens. Livestock vaccination programs protect herds from diseases like foot-and-mouth disease, Newcastle disease (poultry), and rinderpest — an application of immunology.",
        "h": "Livestock vaccination = applied immunology (stimulates immune response against diseases).",
        "yr": "SS1"
      },
      {
        "q": "Genetic engineering in agriculture has produced crops that are:",
        "o": [
          "Less nutritious and more expensive",
          "Resistant to pests, diseases, or herbicides through introduction of specific genes",
          "Only able to grow in cold climates",
          "Unable to reproduce"
        ],
        "a": 1,
        "e": "Genetic engineering introduces specific genes into crop plants to give them new traits. Examples: Bt crops (with Bacillus thuringiensis toxin gene) that resist insect pests; herbicide-resistant crops; Golden Rice (with added vitamin A genes).",
        "h": "Genetic engineering: specific gene insertion → pest/disease/herbicide resistance in crops.",
        "yr": "SS1"
      },
      {
        "q": "Which branch of biology most directly relates to fish farming (aquaculture)?",
        "o": ["Botany", "Zoology (fish biology/animal physiology)", "Geology", "Chemistry only"],
        "a": 1,
        "e": "Fish farming (aquaculture) applies knowledge of fish biology, including nutrition, reproduction, physiology, water quality requirements, and disease management. This falls under zoology — the branch of biology dealing with animals.",
        "h": "Aquaculture = applied fish biology (zoology) — nutrition, reproduction, disease management.",
        "yr": "SS1"
      },
      {
        "q": "Understanding photosynthesis helps agriculture by:",
        "o": [
          "Explaining soil erosion",
          "Helping farmers optimize growing conditions (light, CO₂, water, nutrients) for maximum crop yield",
          "Predicting weather patterns",
          "Controlling insect populations"
        ],
        "a": 1,
        "e": "Knowledge of photosynthesis helps farmers maximize crop production by providing optimal conditions: sufficient light, appropriate CO₂ levels, adequate water, and essential mineral nutrients. This knowledge is applied in greenhouse agriculture and crop management.",
        "h": "Photosynthesis knowledge → optimize light, CO₂, water for maximum crop yield.",
        "yr": "SS1"
      },
      {
        "q": "Biogas production from agricultural waste is an application of:",
        "o": ["Genetics", "Microbiology (anaerobic fermentation by bacteria)", "Ecology only", "Photosynthesis"],
        "a": 1,
        "e": "Biogas (mainly methane, CH₄) is produced by anaerobic bacteria breaking down organic waste (manure, crop residues) in the absence of oxygen — fermentation. This is an application of microbiology and provides a renewable energy source.",
        "h": "Biogas = anaerobic fermentation by bacteria (microbiology application).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a benefit of using leguminous crops in crop rotation?",
        "o": [
          "They produce pesticides naturally",
          "They host nitrogen-fixing bacteria that enrich soil with nitrogen",
          "They repel all insects",
          "They produce more carbon dioxide"
        ],
        "a": 1,
        "e": "Leguminous crops (beans, groundnuts, cowpeas, soybeans) host Rhizobium bacteria in root nodules. These bacteria fix atmospheric nitrogen, enriching the soil. When the legume crop is ploughed back or decomposed, the nitrogen-rich organic matter improves soil fertility.",
        "h": "Legumes in rotation → Rhizobium fixes N₂ → enriches soil for subsequent crops.",
        "yr": "SS1"
      },
      {
        "q": "The use of biological knowledge to improve soil fertility is most closely related to which biological discipline?",
        "o": ["Genetics only", "Microbiology and ecology", "Anatomy", "Embryology"],
        "a": 1,
        "e": "Soil fertility improvement uses microbiology (nitrogen-fixing bacteria, decomposers, mycorrhizal fungi) and ecology (understanding nutrient cycling, food webs in soil, crop rotation effects on soil communities).",
        "h": "Soil fertility = microbiology (bacteria, fungi) + ecology (nutrient cycling).",
        "yr": "SS1"
      },
      {
        "q": "Chemical pesticides are effective in pest control but may cause problems such as:",
        "o": [
          "Increasing crop yield directly",
          "Environmental pollution, killing non-target organisms, and development of pest resistance",
          "Improving soil pH",
          "Fixing more nitrogen"
        ],
        "a": 1,
        "e": "Chemical pesticides can pollute soil and water, kill beneficial non-target organisms (bees, birds, earthworms), bioaccumulate in food chains, and lead to resistance in pest populations through natural selection over time.",
        "h": "Chemical pesticides: pollution, kill non-targets, lead to pest resistance.",
        "yr": "SS1"
      },
      {
        "q": "Silage is made by:",
        "o": [
          "Drying grass under sunlight",
          "Anaerobic fermentation of green fodder by bacteria, preserving it as animal feed",
          "Burning crop residues",
          "Soaking seeds in water"
        ],
        "a": 1,
        "e": "Silage is made by compressing fresh green fodder (grass, maize) in airtight conditions, allowing anaerobic bacteria to ferment it. The lactic acid produced preserves the feed. It is a nutritious livestock feed used when fresh grass is scarce.",
        "h": "Silage = anaerobic bacterial fermentation of green fodder for livestock feed.",
        "yr": "SS1"
      },
      {
        "q": "Knowledge of plant physiology helps in agriculture by explaining:",
        "o": [
          "How animals reproduce",
          "How plants absorb nutrients, transport water, and respond to environmental signals",
          "Geological processes",
          "How bacteria mutate"
        ],
        "a": 1,
        "e": "Plant physiology — the study of how plants function — is directly applicable to agriculture. It explains nutrient absorption, water transport (xylem), photosynthesis, hormonal responses (auxins affect growth), and responses to environmental stresses like drought.",
        "h": "Plant physiology: nutrient absorption, water transport, photosynthesis → guides crop management.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following best explains the advantage of using mycorrhizal fungi in agriculture?",
        "o": [
          "They produce pesticides",
          "They fix atmospheric nitrogen",
          "They form associations with plant roots that dramatically increase the plant's ability to absorb water and minerals",
          "They destroy weeds"
        ],
        "a": 2,
        "e": "Mycorrhizal fungi form symbiotic associations with plant roots. The fungal hyphae greatly extend the absorptive surface area, helping plants absorb water and minerals (especially phosphorus) that they otherwise could not access. This improves plant growth and yield.",
        "h": "Mycorrhizal fungi + plant roots = mutualism → increases water and mineral absorption.",
        "yr": "SS1"
      },
      {
        "q": "The concept of 'balanced diet' in animal husbandry is based on which biological principle?",
        "o": [
          "Genetics of the animals",
          "Nutritional requirements of animals — providing all essential nutrients in proper proportions",
          "Ecological balance",
          "Habitat requirements"
        ],
        "a": 1,
        "e": "Balanced feeding programs in animal husbandry are based on knowledge of animal nutrition — the specific requirements for carbohydrates, proteins, fats, vitamins, minerals, and water for optimal growth, health, and production.",
        "h": "Animal feeding programs = applied animal nutrition (biology of nutritional requirements).",
        "yr": "SS1"
      },
      {
        "q": "Integrated pest management (IPM) combines:",
        "o": [
          "Only chemical pesticides",
          "Only biological control",
          "Biological, chemical, cultural, and physical methods to manage pests sustainably",
          "Genetic engineering only"
        ],
        "a": 2,
        "e": "Integrated Pest Management (IPM) is a sustainable approach that combines multiple pest control strategies: biological (natural predators), chemical (targeted pesticides at threshold levels), cultural (crop rotation), and physical (traps, barriers) to minimize environmental impact.",
        "h": "IPM = combination of biological + chemical + cultural + physical pest management.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Micro-Organisms Around Us",
    "topicCode": "SS1-BIO-16",
    "module": "Microbiology",
    "term": "Second Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Micro-organisms</span> (microbes) are living organisms that are too small to be seen with the naked eye and require a microscope to be observed. They are the most numerous, diverse, and widespread forms of life on Earth, found in virtually every habitat including soil, water, air, and living organisms.\n</div>\n\n<h3 class=\"learn-subheading\">1. Types of Micro-organisms</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Type</th><th>Kingdom</th><th>Cell Type</th><th>Examples</th></tr></thead>\n    <tbody>\n      <tr><td>Bacteria</td><td>Monera</td><td>Prokaryotic</td><td>E. coli, Salmonella, Rhizobium, Lactobacillus</td></tr>\n      <tr><td>Fungi (microscopic)</td><td>Fungi</td><td>Eukaryotic</td><td>Yeast, Penicillium, Aspergillus</td></tr>\n      <tr><td>Protozoa</td><td>Protista</td><td>Eukaryotic</td><td>Amoeba, Paramecium, Plasmodium, Trypanosoma</td></tr>\n      <tr><td>Algae (microscopic)</td><td>Protista/Plantae</td><td>Eukaryotic</td><td>Chlamydomonas, Spirogyra, Chlorella</td></tr>\n      <tr><td>Viruses</td><td>Borderline</td><td>Non-cellular</td><td>HIV, influenza, polio, hepatitis B</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Characteristics of Micro-organisms</h3>\n<ul class=\"learn-list\">\n  <li>Microscopic in size (measured in micrometres, µm)</li>\n  <li>Reproduce rapidly (bacteria can double every 20 minutes)</li>\n  <li>Found in diverse habitats — soil, water, air, body surfaces, digestive tracts</li>\n  <li>Can be autotrophic (photosynthetic algae, some bacteria) or heterotrophic</li>\n  <li>Survive harsh conditions through spore formation (bacteria, fungi)</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Importance of Micro-organisms</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Role</th><th>Examples</th></tr></thead>\n    <tbody>\n      <tr><td>Decomposition/nutrient cycling</td><td>Bacteria and fungi decompose dead matter, releasing nutrients</td></tr>\n      <tr><td>Nitrogen fixation</td><td>Rhizobium, Azotobacter fix N₂ → soil fertility</td></tr>\n      <tr><td>Food production</td><td>Yeast (bread, beer); Lactobacillus (yogurt, cheese)</td></tr>\n      <tr><td>Medicine</td><td>Penicillium produces penicillin; bacteria produce insulin (via genetic engineering)</td></tr>\n      <tr><td>Disease causation</td><td>Bacteria (tuberculosis, cholera); viruses (HIV, malaria parasite is a protozoan)</td></tr>\n      <tr><td>Biotechnology</td><td>Bacteria used to produce enzymes, hormones, biofuels</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Not all microorganisms are harmful! Many are beneficial: decomposers recycle nutrients; nitrogen-fixing bacteria improve soil fertility; yeast is used in baking and brewing; Lactobacillus makes yogurt; Penicillium produces the antibiotic penicillin. Viruses are the only non-cellular microorganisms.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Micro-organisms include bacteria (prokaryotes), fungi (yeasts, moulds), protozoa, algae, and viruses. They are found everywhere, reproduce rapidly, and are of enormous importance. Beneficial roles include decomposition, nitrogen fixation, food production, and medicine. Harmful roles include causing diseases. Viruses are non-cellular and on the borderline of life.\n</div>\n  ",
    "questions": [
      {
        "q": "Which of the following is the ONLY non-cellular micro-organism?",
        "o": ["Bacteria", "Yeast", "Virus", "Amoeba"],
        "a": 2,
        "e": "Viruses are non-cellular — they have no cell membrane, cytoplasm, or organelles. They consist only of genetic material (DNA or RNA) enclosed in a protein coat (capsid). All other microorganisms (bacteria, fungi, protozoa, algae) are cellular.",
        "h": "Virus = non-cellular (no cell structure); all other microbes are cellular.",
        "yr": "SS1"
      },
      {
        "q": "Which micro-organism is used to make yogurt and cheese?",
        "o": ["Penicillium", "Aspergillus", "Lactobacillus (lactic acid bacteria)", "E. coli"],
        "a": 2,
        "e": "Lactobacillus and other lactic acid bacteria ferment lactose (milk sugar) to lactic acid, which curdles milk proteins and gives yogurt and cheese their characteristic texture and sour taste. This is a beneficial use of bacteria in food production.",
        "h": "Lactobacillus → ferments milk → yogurt and cheese production.",
        "yr": "SS1"
      },
      {
        "q": "The antibiotic penicillin is produced by:",
        "o": ["Bacteria", "Penicillium (a fungus)", "Yeast", "Virus"],
        "a": 1,
        "e": "Penicillin is produced by the fungus Penicillium (e.g., Penicillium notatum, P. chrysogenum). Alexander Fleming discovered in 1928 that Penicillium mould killed bacteria. Penicillin kills bacteria by preventing cell wall synthesis.",
        "h": "Penicillin = produced by Penicillium fungus (discovered by Fleming, 1928).",
        "yr": "SS1"
      },
      {
        "q": "Bacteria are classified in which kingdom?",
        "o": ["Protista", "Fungi", "Monera", "Animalia"],
        "a": 2,
        "e": "Bacteria belong to Kingdom Monera. They are prokaryotes — their cells lack a true membrane-bound nucleus and membrane-bound organelles. They are the most numerous and diverse organisms on Earth.",
        "h": "Bacteria = Kingdom Monera (prokaryotes).",
        "yr": "SS1"
      },
      {
        "q": "Protozoa belong to which kingdom?",
        "o": ["Monera", "Fungi", "Protista", "Plantae"],
        "a": 2,
        "e": "Protozoa (e.g., Amoeba, Paramecium, Plasmodium) are single-celled eukaryotes belonging to Kingdom Protista. They are heterotrophic and found in aquatic and moist environments.",
        "h": "Protozoa (Amoeba, Paramecium, Plasmodium) = Kingdom Protista.",
        "yr": "SS1"
      },
      {
        "q": "Which microorganism causes malaria in humans?",
        "o": ["Bacteria (Mycobacterium)", "Virus", "Plasmodium (a protozoan)", "Fungus"],
        "a": 2,
        "e": "Malaria is caused by Plasmodium — a protozoan parasite in Kingdom Protista. It is transmitted to humans through the bite of an infected female Anopheles mosquito. Species include P. falciparum (most deadly), P. vivax, P. malariae.",
        "h": "Malaria = Plasmodium (protozoan) transmitted by female Anopheles mosquito.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a beneficial role of micro-organisms?",
        "o": [
          "Causing cholera",
          "Causing AIDS",
          "Decomposing dead matter and recycling nutrients in the ecosystem",
          "Causing tuberculosis"
        ],
        "a": 2,
        "e": "Decomposition by bacteria and fungi is one of the most beneficial roles of microorganisms. They break down dead organic matter, releasing essential nutrients (nitrogen, carbon, phosphorus) back into the environment for reuse by producers.",
        "h": "Decomposition by microbes = essential nutrient cycling (beneficial role).",
        "yr": "SS1"
      },
      {
        "q": "Bacteria can survive unfavorable conditions by:",
        "o": [
          "Growing larger",
          "Forming spores (endospores) that are highly resistant to heat, desiccation, and chemicals",
          "Photosynthesizing",
          "Producing antibiotics"
        ],
        "a": 1,
        "e": "Some bacteria (e.g., Bacillus, Clostridium) form endospores — tough, dormant structures containing the cell's DNA and essential components. Endospores can survive extreme heat, desiccation, radiation, and disinfectants for long periods.",
        "h": "Bacteria survive harsh conditions by forming endospores (highly resistant dormant structures).",
        "yr": "SS1"
      },
      {
        "q": "Yeast is classified in which kingdom, and what is its main use in food production?",
        "o": [
          "Monera; used in making antibiotics",
          "Fungi; used in bread-making and brewing (fermentation)",
          "Protista; used in yogurt production",
          "Animalia; used in cheese production"
        ],
        "a": 1,
        "e": "Yeast (e.g., Saccharomyces cerevisiae) belongs to Kingdom Fungi. It is used in bread-making (CO₂ from fermentation causes dough to rise) and in brewing beer and wine (ethanol from fermentation). This is anaerobic fermentation.",
        "h": "Yeast = Fungi; used in bread-making (CO₂) and brewing (ethanol).",
        "yr": "SS1"
      },
      {
        "q": "Micro-organisms are measured in:",
        "o": ["Centimetres (cm)", "Millimetres (mm)", "Micrometres (µm)", "Nanometres only"],
        "a": 2,
        "e": "Micro-organisms are extremely small — most bacteria are 1–10 µm (micrometres) in diameter. One micrometre = 1/1,000,000 of a metre. Viruses are even smaller and are measured in nanometres (nm).",
        "h": "Bacteria measured in micrometres (µm); viruses in nanometres (nm).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following statements about viruses is correct?",
        "o": [
          "Viruses are living cells with a nucleus",
          "Viruses consist of genetic material (DNA or RNA) in a protein coat; they can only reproduce inside host cells",
          "Viruses are classified in Kingdom Monera",
          "Viruses can reproduce independently without a host"
        ],
        "a": 1,
        "e": "Viruses consist of nucleic acid (DNA or RNA) enclosed in a protein coat (capsid). They are non-cellular and can only reproduce by hijacking the machinery of a host cell. They cannot reproduce independently, distinguishing them from cellular organisms.",
        "h": "Viruses = DNA/RNA + protein coat; reproduce ONLY inside host cells.",
        "yr": "SS1"
      },
      {
        "q": "Bacteria that cause disease are called:",
        "o": ["Saprophytes", "Pathogens", "Autotrophs", "Decomposers"],
        "a": 1,
        "e": "Pathogens are disease-causing organisms (or agents). Pathogenic bacteria include Mycobacterium tuberculosis (tuberculosis), Vibrio cholerae (cholera), Salmonella typhi (typhoid). Not all bacteria are pathogens — many are beneficial.",
        "h": "Disease-causing microorganisms = pathogens.",
        "yr": "SS1"
      },
      {
        "q": "Microscopic algae (e.g., Chlamydomonas) are important in aquatic ecosystems because they:",
        "o": [
          "Cause waterborne diseases",
          "Are the main producers in aquatic food chains (phytoplankton)",
          "Fix nitrogen from water",
          "Decompose dead fish"
        ],
        "a": 1,
        "e": "Microscopic algae (phytoplankton) are the primary producers in most aquatic ecosystems. Through photosynthesis, they form the base of aquatic food chains and produce a significant portion of Earth's oxygen.",
        "h": "Microscopic algae = phytoplankton = primary producers in aquatic ecosystems.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following uses bacteria for the industrial production of insulin?",
        "o": ["Traditional fermentation", "Genetic engineering (inserting human insulin gene into E. coli)", "Selective breeding", "Chemical synthesis only"],
        "a": 1,
        "e": "The human insulin gene is inserted into E. coli bacteria using genetic engineering. The bacteria then produce human insulin in large quantities, which is purified for use by diabetic patients. This is a major application of biotechnology.",
        "h": "Insulin production: human insulin gene inserted into E. coli → bacteria produce insulin.",
        "yr": "SS1"
      },
      {
        "q": "Which organism causes African sleeping sickness?",
        "o": ["Plasmodium", "Trypanosoma (a protozoan)", "Salmonella", "Influenza virus"],
        "a": 1,
        "e": "African sleeping sickness is caused by Trypanosoma brucei — a protozoan parasite (Kingdom Protista). It is transmitted by the tsetse fly and affects the nervous system, causing sleep disturbances and, if untreated, death.",
        "h": "Sleeping sickness = Trypanosoma (protozoan), transmitted by tsetse fly.",
        "yr": "SS1"
      },
      {
        "q": "Micro-organisms are found in which of the following environments?",
        "o": [
          "Only in water",
          "Only in the human body",
          "Virtually everywhere — soil, water, air, living organisms, extreme environments",
          "Only in rotting food"
        ],
        "a": 2,
        "e": "Micro-organisms inhabit virtually every environment on Earth — including soil, water, air, the bodies of plants and animals, hot springs, deep oceans, and even polar ice. Extremophile bacteria are found in conditions hostile to most life.",
        "h": "Microbes = found virtually everywhere (soil, water, air, bodies, extreme environments).",
        "yr": "SS1"
      },
      {
        "q": "Which process do bacteria use to reproduce?",
        "o": ["Budding", "Binary fission", "Spore formation only", "Sexual reproduction"],
        "a": 1,
        "e": "Bacteria reproduce primarily by binary fission — the cell enlarges and its circular DNA replicates, then the cell divides into two identical daughter cells. Under optimal conditions, bacteria can divide every 20 minutes, leading to rapid population growth.",
        "h": "Bacteria reproduce by binary fission (can double every ~20 minutes).",
        "yr": "SS1"
      },
      {
        "q": "Methanogens are bacteria that produce methane gas. They are used in:",
        "o": [
          "Bread-making",
          "Biogas digesters to produce methane fuel from organic waste",
          "Antibiotic production",
          "Yogurt fermentation"
        ],
        "a": 1,
        "e": "Methanogenic bacteria (methanogens) produce methane gas by breaking down organic matter anaerobically. Biogas digesters harness this process — organic waste (manure, crop residues) is fermented by methanogens to produce methane gas, which can be used as fuel.",
        "h": "Methanogens produce methane → used in biogas digesters for fuel from organic waste.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following would you use to observe micro-organisms?",
        "o": ["Naked eye", "Magnifying glass only", "Light or electron microscope", "Telescope"],
        "a": 2,
        "e": "Micro-organisms are too small to see with the naked eye. They are studied using light microscopes (bacteria: ×400–1000) or electron microscopes (viruses and ultra-fine structures). The electron microscope has far greater resolution than the light microscope.",
        "h": "Microorganisms observed using light or electron microscope (too small for naked eye).",
        "yr": "SS1"
      },
      {
        "q": "Aspergillus, a common mould, belongs to which kingdom?",
        "o": ["Monera", "Protista", "Fungi", "Plantae"],
        "a": 2,
        "e": "Aspergillus is a genus of filamentous fungi (Kingdom Fungi). Some species are used industrially (e.g., Aspergillus niger produces citric acid), while others are pathogenic (e.g., Aspergillus fumigatus causes aspergillosis in immunocompromised patients).",
        "h": "Aspergillus = Kingdom Fungi (filamentous mould).",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Micro-Organisms in Action",
    "topicCode": "SS1-BIO-17",
    "module": "Microbiology",
    "term": "Third Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  Micro-organisms play critical roles in nature and human life through processes such as <span class=\"learn-keyword\">fermentation</span>, <span class=\"learn-keyword\">decay</span>, <span class=\"learn-keyword\">food spoilage</span>, and the causation of <span class=\"learn-keyword\">diseases</span>. Understanding these roles enables us to harness beneficial microbes and protect ourselves from harmful ones.\n</div>\n\n<h3 class=\"learn-subheading\">1. Fermentation</h3>\n<p class=\"learn-p\">Fermentation is the anaerobic breakdown of organic compounds (mainly carbohydrates) by micro-organisms to produce useful products:</p>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Micro-organism</th><th>Substrate</th><th>Products</th><th>Application</th></tr></thead>\n    <tbody>\n      <tr><td>Yeast (Saccharomyces)</td><td>Sugars</td><td>Ethanol + CO₂</td><td>Bread, beer, wine</td></tr>\n      <tr><td>Lactobacillus</td><td>Lactose (milk sugar)</td><td>Lactic acid</td><td>Yogurt, cheese, silage</td></tr>\n      <tr><td>Acetobacter</td><td>Ethanol</td><td>Acetic acid</td><td>Vinegar production</td></tr>\n      <tr><td>Methanogenic bacteria</td><td>Organic waste</td><td>Methane (CH₄)</td><td>Biogas fuel</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Decay and Decomposition</h3>\n<ul class=\"learn-list\">\n  <li>Bacteria and fungi (decomposers) break down dead organic matter in a process called <strong>decay</strong></li>\n  <li>They secrete enzymes that digest organic compounds externally, then absorb the products</li>\n  <li>Decay releases CO₂, water, ammonia, and mineral salts — essential for nutrient cycling</li>\n  <li>Conditions favouring decay: warmth, moisture, oxygen (for aerobic decomposers), neutral pH</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Food Spoilage</h3>\n<p class=\"learn-p\">Food spoilage occurs when micro-organisms grow in food, making it unfit for consumption:</p>\n<ul class=\"learn-list\">\n  <li><strong>Bacteria:</strong> Produce toxins (e.g., Clostridium botulinum produces botulin toxin in canned food)</li>\n  <li><strong>Moulds (fungi):</strong> Produce mycotoxins (e.g., Aspergillus flavus produces aflatoxin on groundnuts)</li>\n  <li>Food preservation methods prevent spoilage: refrigeration, drying, salting, canning, pasteurisation, irradiation, pickling</li>\n</ul>\n\n<h3 class=\"learn-subheading\">4. Micro-organisms as Disease Agents</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Pathogen</th><th>Type</th><th>Disease</th></tr></thead>\n    <tbody>\n      <tr><td>Mycobacterium tuberculosis</td><td>Bacterium</td><td>Tuberculosis (TB)</td></tr>\n      <tr><td>Vibrio cholerae</td><td>Bacterium</td><td>Cholera</td></tr>\n      <tr><td>Salmonella typhi</td><td>Bacterium</td><td>Typhoid fever</td></tr>\n      <tr><td>Plasmodium falciparum</td><td>Protozoan</td><td>Malaria</td></tr>\n      <tr><td>HIV</td><td>Virus</td><td>AIDS</td></tr>\n      <tr><td>Poliovirus</td><td>Virus</td><td>Poliomyelitis</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Know key examples: Yeast → ethanol + CO₂ (bread/brewing); Lactobacillus → lactic acid (yogurt); Aflatoxin from Aspergillus flavus on groundnuts is a carcinogen. Conditions that slow decay/spoilage: low temperature, low moisture, low pH, absence of oxygen, high salt/sugar concentration.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Microbes act through fermentation (yeast: ethanol; Lactobacillus: lactic acid), decay (decomposers break down dead matter, cycling nutrients), food spoilage (bacteria and moulds make food unsafe), and disease (bacteria, protozoa, viruses cause infections). Food preservation methods inhibit microbial growth.\n</div>\n  ",
    "questions": [
      {
        "q": "Fermentation is best described as:",
        "o": [
          "Aerobic respiration by micro-organisms",
          "Anaerobic breakdown of organic compounds by micro-organisms to produce useful products",
          "Decomposition of dead matter only",
          "The process of pasteurization"
        ],
        "a": 1,
        "e": "Fermentation is the anaerobic (without oxygen) breakdown of organic compounds, mainly carbohydrates, by micro-organisms (yeast, bacteria) to produce products like ethanol, CO₂, lactic acid, or methane.",
        "h": "Fermentation = anaerobic breakdown of organics by microbes → useful products.",
        "yr": "SS1"
      },
      {
        "q": "Which micro-organism is responsible for the fermentation used in bread-making?",
        "o": ["Lactobacillus", "Penicillium", "Yeast (Saccharomyces cerevisiae)", "Aspergillus"],
        "a": 2,
        "e": "Yeast (Saccharomyces cerevisiae) ferments sugars in dough to produce CO₂ (which makes dough rise) and ethanol (which evaporates during baking). This anaerobic fermentation is essential in bread-making.",
        "h": "Bread-making = yeast fermentation → CO₂ (dough rises) + ethanol.",
        "yr": "SS1"
      },
      {
        "q": "Aflatoxin is a dangerous mycotoxin produced by:",
        "o": ["Salmonella bacteria", "Aspergillus flavus (mould on groundnuts)", "Yeast", "Lactobacillus"],
        "a": 1,
        "e": "Aflatoxin is produced by Aspergillus flavus and A. parasiticus moulds that grow on groundnuts (peanuts), maize, and other stored crops in warm, humid conditions. Aflatoxin is a potent carcinogen (causes liver cancer) and food contaminant.",
        "h": "Aflatoxin = produced by Aspergillus flavus on groundnuts/maize = carcinogen.",
        "yr": "SS1"
      },
      {
        "q": "Which conditions favour rapid decay (decomposition) of organic matter?",
        "o": [
          "Cold, dry, acidic conditions",
          "Warm, moist, neutral pH with oxygen (for aerobic decomposers)",
          "Freezing temperatures and high salt",
          "Very high temperature and no water"
        ],
        "a": 1,
        "e": "Decomposers (bacteria and fungi) work most efficiently in warm, moist environments with a near-neutral pH and sufficient oxygen (for aerobic types). Cold, dryness, acidity, and lack of oxygen all slow decomposition — these are the principles behind food preservation.",
        "h": "Decay fastest in: warm + moist + neutral pH + oxygen.",
        "yr": "SS1"
      },
      {
        "q": "Pasteurization is a food preservation method that involves:",
        "o": [
          "Freezing food to −20°C",
          "Heating food to a specific temperature for a set time to kill pathogens, then rapidly cooling",
          "Adding salt to food",
          "Drying food under sunlight"
        ],
        "a": 1,
        "e": "Pasteurization (developed by Louis Pasteur) involves heating food (typically milk) to 72°C for 15 seconds (HTST) or 63°C for 30 minutes (LTLT) to kill pathogenic bacteria, then rapidly cooling. It reduces microbial load without significantly altering taste or nutrition.",
        "h": "Pasteurization = heat to kill pathogens (72°C/15sec for milk), then rapidly cool.",
        "yr": "SS1"
      },
      {
        "q": "Clostridium botulinum is dangerous in improperly canned food because:",
        "o": [
          "It produces lactic acid",
          "It produces botulin toxin in anaerobic conditions, causing potentially fatal food poisoning",
          "It destroys the can's metal",
          "It prevents fermentation"
        ],
        "a": 1,
        "e": "C. botulinum is an anaerobic bacterium that produces botulin neurotoxin in oxygen-free environments like improperly sealed cans. Botulin is one of the most lethal toxins known, causing muscle paralysis and potentially fatal botulism.",
        "h": "Clostridium botulinum: anaerobic, produces botulin toxin in canned food → botulism.",
        "yr": "SS1"
      },
      {
        "q": "Lactic acid fermentation by Lactobacillus is used to produce:",
        "o": ["Beer and wine", "Bread", "Yogurt and cheese", "Vinegar"],
        "a": 2,
        "e": "Lactobacillus bacteria ferment lactose (milk sugar) to lactic acid. The lactic acid lowers pH, causing milk proteins (caseins) to curdle and giving yogurt and cheese their characteristic texture and sour flavor.",
        "h": "Lactobacillus + lactose → lactic acid → yogurt and cheese.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following food preservation methods uses high temperature to completely sterilize food?",
        "o": ["Refrigeration", "Pickling", "Canning (sterilization by heat)", "Salting"],
        "a": 2,
        "e": "Canning involves heating food to very high temperatures (121°C for 15–20 min in an autoclave) to kill all micro-organisms including spores. The food is then sealed in airtight containers to prevent recontamination.",
        "h": "Canning = heat sterilization + airtight seal → kills all microbes including spores.",
        "yr": "SS1"
      },
      {
        "q": "How does decay of dead organic matter benefit the ecosystem?",
        "o": [
          "It produces oxygen",
          "It releases nutrients (nitrogen, carbon, phosphorus) back into the environment for reuse by producers",
          "It increases temperature",
          "It spreads diseases"
        ],
        "a": 1,
        "e": "Decay (decomposition) releases inorganic nutrients (nitrogen, carbon, phosphorus, potassium) from dead organic matter back into the soil, water, and atmosphere. These nutrients are then available for uptake by producers, completing nutrient cycles.",
        "h": "Decay → releases nutrients (N, C, P) for reuse by producers → essential for nutrient cycling.",
        "yr": "SS1"
      },
      {
        "q": "Vinegar is produced by the fermentation action of:",
        "o": ["Yeast converting sugar to ethanol", "Acetobacter converting ethanol to acetic acid", "Lactobacillus converting lactose to lactic acid", "Methanogenic bacteria"],
        "a": 1,
        "e": "Vinegar production involves two steps: (1) yeast converts sugar to ethanol (anaerobic fermentation), then (2) Acetobacter bacteria aerobically oxidize ethanol to acetic acid (ethanoic acid) — the compound that gives vinegar its sour taste.",
        "h": "Vinegar: Acetobacter converts ethanol → acetic acid (aerobic process).",
        "yr": "SS1"
      },
      {
        "q": "Which method of food preservation works by creating an environment too salty for micro-organisms to survive?",
        "o": ["Refrigeration", "Pickling", "Salting", "Irradiation"],
        "a": 2,
        "e": "Salting creates a high osmotic concentration around food. Water moves out of microbial cells by osmosis, dehydrating and killing them or preventing their growth. Salted fish and meat can be preserved for extended periods.",
        "h": "Salting = high osmotic concentration → water leaves microbial cells → growth inhibited.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following diseases is caused by a bacterium?",
        "o": ["Malaria", "AIDS", "Influenza", "Cholera"],
        "a": 3,
        "e": "Cholera is caused by the bacterium Vibrio cholerae. It spreads through contaminated water and causes severe watery diarrhea and dehydration. Malaria is caused by a protozoan, AIDS by HIV (virus), and influenza by influenza virus.",
        "h": "Cholera = Vibrio cholerae (bacterium); malaria = Plasmodium (protozoan); AIDS = HIV (virus).",
        "yr": "SS1"
      },
      {
        "q": "How does refrigeration preserve food?",
        "o": [
          "It kills all micro-organisms instantly",
          "It lowers temperature, slowing the metabolic rate and growth rate of micro-organisms",
          "It removes all moisture from food",
          "It increases salt concentration"
        ],
        "a": 1,
        "e": "Refrigeration slows but does not stop microbial growth. At low temperatures (4°C), the metabolic rate and reproduction of most micro-organisms is significantly slowed, extending food shelf life. Freezing (below 0°C) halts growth further but does not kill all microbes.",
        "h": "Refrigeration = low temperature → slows microbial metabolism and growth.",
        "yr": "SS1"
      },
      {
        "q": "Biogas produced by methanogenic bacteria consists mainly of:",
        "o": ["Hydrogen gas", "Carbon dioxide only", "Methane (CH₄)", "Oxygen"],
        "a": 2,
        "e": "Biogas produced by anaerobic digestion (by methanogenic archaea/bacteria) is composed mainly of methane (CH₄, 50–70%) and CO₂ (30–50%), with trace amounts of other gases. Methane makes biogas combustible and useful as a fuel.",
        "h": "Biogas = mainly methane (CH₄, ~50–70%) + CO₂.",
        "yr": "SS1"
      },
      {
        "q": "Food spoilage by fungi often produces dangerous toxins called:",
        "o": ["Antibiotics", "Vaccines", "Mycotoxins (e.g., aflatoxin)", "Antitoxins"],
        "a": 2,
        "e": "Mycotoxins are toxic secondary metabolites produced by moulds (fungi) that contaminate food. The most dangerous is aflatoxin (from Aspergillus flavus) which is carcinogenic, particularly causing liver cancer.",
        "h": "Fungal food spoilage toxins = mycotoxins (e.g., aflatoxin from Aspergillus).",
        "yr": "SS1"
      },
      {
        "q": "Decay is carried out by micro-organisms through the process of:",
        "o": [
          "Photosynthesis",
          "Secreting digestive enzymes onto dead matter (external digestion) and absorbing the products",
          "Ingesting whole food particles",
          "Active transport only"
        ],
        "a": 1,
        "e": "Decomposers (bacteria and fungi) carry out external digestion — they secrete enzymes onto dead organic matter, breaking complex molecules into simpler ones, then absorb the soluble products. This is saprophytic nutrition.",
        "h": "Decay = external enzyme secretion by microbes + absorption of products.",
        "yr": "SS1"
      },
      {
        "q": "Which process makes use of micro-organisms to break down organic waste and produce compost?",
        "o": ["Fermentation for ethanol", "Composting (aerobic decomposition by bacteria and fungi)", "Nitrogen fixation", "Pasteurization"],
        "a": 1,
        "e": "Composting is the aerobic decomposition of organic waste (vegetable peels, leaves, manure) by bacteria and fungi under controlled conditions. The resulting compost is rich in humus and nutrients, improving soil fertility.",
        "h": "Composting = aerobic decomposition by bacteria/fungi → nutrient-rich humus.",
        "yr": "SS1"
      },
      {
        "q": "Mycobacterium tuberculosis causes which disease?",
        "o": ["Malaria", "Cholera", "Tuberculosis (TB)", "AIDS"],
        "a": 2,
        "e": "Mycobacterium tuberculosis is the bacterium that causes tuberculosis (TB). It primarily infects the lungs (pulmonary TB), causing persistent cough, weight loss, and night sweats. TB is spread through airborne droplets.",
        "h": "Mycobacterium tuberculosis → tuberculosis (TB); spread by airborne droplets.",
        "yr": "SS1"
      },
      {
        "q": "Drying (desiccation) as a food preservation method works because:",
        "o": [
          "It freezes all micro-organisms",
          "It kills micro-organisms with heat",
          "It removes water, preventing microbial growth (micro-organisms need water to survive and reproduce)",
          "It increases oxygen in food"
        ],
        "a": 2,
        "e": "Drying removes water from food. Since micro-organisms need water for metabolism and reproduction, dehydrating food to low moisture content prevents their growth and multiplication, extending shelf life. Examples: dried fish, sun-dried tomatoes, biltong.",
        "h": "Drying = removes water → micro-organisms cannot grow without water.",
        "yr": "SS1"
      },
      {
        "q": "The role of micro-organisms in the carbon cycle is to:",
        "o": [
          "Fix atmospheric CO₂ via photosynthesis",
          "Release CO₂ by decomposing dead organic matter and through respiration",
          "Store carbon permanently",
          "Only consume other organisms"
        ],
        "a": 1,
        "e": "Micro-organisms (bacteria and fungi) decompose dead organic matter, releasing CO₂ back into the atmosphere through their respiration and enzymatic breakdown of organic compounds. This is essential for completing the carbon cycle.",
        "h": "Microbes in carbon cycle: decompose organic matter → release CO₂ → completes cycle.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Towards Better Health",
    "topicCode": "SS1-BIO-18",
    "module": "Health Biology",
    "term": "Third Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  Good health is defined by the World Health Organization (WHO) as 'a state of complete physical, mental, and social well-being, not merely the absence of disease.' Achieving and maintaining good health requires proper <span class=\"learn-keyword\">hygiene</span>, <span class=\"learn-keyword\">sanitation</span>, <span class=\"learn-keyword\">vaccination</span>, and effective <span class=\"learn-keyword\">disease prevention</span> strategies.\n</div>\n\n<h3 class=\"learn-subheading\">1. Hygiene</h3>\n<p class=\"learn-p\">Hygiene is the practice of maintaining cleanliness to prevent disease. Types include:</p>\n<ul class=\"learn-list\">\n  <li><strong>Personal hygiene:</strong> Regular bathing, hand washing, clean clothes, dental care</li>\n  <li><strong>Food hygiene:</strong> Proper storage, cooking, and handling of food to prevent contamination</li>\n  <li><strong>Environmental hygiene:</strong> Keeping surroundings clean to reduce breeding sites for pathogens and vectors</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Sanitation</h3>\n<p class=\"learn-p\">Sanitation involves the provision and maintenance of facilities for safe disposal of human waste and contamination control:</p>\n<ul class=\"learn-list\">\n  <li>Safe water supply and water treatment (chlorination, boiling)</li>\n  <li>Proper sewage disposal (pit latrines, sewage treatment plants)</li>\n  <li>Refuse disposal (waste management, landfills)</li>\n  <li>Control of disease vectors (mosquitoes, rats, flies) through drainage, insecticides, and nets</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Vaccination (Immunisation)</h3>\n<p class=\"learn-p\">Vaccination stimulates the body's immune system to produce <strong>antibodies</strong> against specific pathogens without causing the disease. Types:</p>\n<ul class=\"learn-list\">\n  <li><strong>Active immunity:</strong> Vaccination or natural infection stimulates the body to make its own antibodies — long-lasting</li>\n  <li><strong>Passive immunity:</strong> Ready-made antibodies given directly (e.g., anti-tetanus serum) — immediate but short-lived</li>\n</ul>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Vaccine</th><th>Disease prevented</th></tr></thead>\n    <tbody>\n      <tr><td>BCG</td><td>Tuberculosis</td></tr>\n      <tr><td>OPV (Oral Polio Vaccine)</td><td>Poliomyelitis</td></tr>\n      <tr><td>DPT</td><td>Diphtheria, Pertussis (whooping cough), Tetanus</td></tr>\n      <tr><td>MMR</td><td>Measles, Mumps, Rubella</td></tr>\n      <tr><td>Hepatitis B vaccine</td><td>Hepatitis B</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">4. Disease Prevention Strategies</h3>\n<ul class=\"learn-list\">\n  <li><strong>Primary prevention:</strong> Stop disease before it occurs (vaccination, hygiene, vector control)</li>\n  <li><strong>Secondary prevention:</strong> Early detection and treatment (screening, diagnosis)</li>\n  <li><strong>Tertiary prevention:</strong> Reduce impact of established disease (rehabilitation, management)</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Key distinction: Active immunity = body makes own antibodies (long-lasting). Passive immunity = antibodies given from outside (immediate but short-lived). Vaccination is active immunity. Anti-tetanus serum (given after a wound) is passive immunity.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Better health is achieved through: personal, food, and environmental hygiene; sanitation (safe water, sewage disposal, waste management, vector control); vaccination (active immunity — long-lasting; passive immunity — immediate, short-lived); and disease prevention at primary, secondary, and tertiary levels.\n</div>\n  ",
    "questions": [
      {
        "q": "The WHO defines health as:",
        "o": [
          "The absence of disease only",
          "A state of complete physical, mental, and social well-being — not merely the absence of disease",
          "Being physically fit",
          "Living past 70 years of age"
        ],
        "a": 1,
        "e": "The World Health Organization (1948) defines health as 'a state of complete physical, mental, and social well-being, not merely the absence of disease or infirmity.' This holistic definition recognizes mental and social dimensions alongside physical health.",
        "h": "WHO: health = complete physical + mental + social well-being (not just absence of disease).",
        "yr": "SS1"
      },
      {
        "q": "Vaccination provides immunity by:",
        "o": [
          "Directly killing all pathogens in the body",
          "Providing ready-made antibodies from another organism",
          "Stimulating the immune system to produce antibodies against a specific pathogen",
          "Replacing damaged white blood cells"
        ],
        "a": 2,
        "e": "Vaccination introduces a weakened/killed pathogen, toxoid, or pathogen antigen into the body. This stimulates the immune system to produce specific antibodies and memory cells, providing protection if the actual pathogen is encountered later.",
        "h": "Vaccination = stimulates immune system to produce antibodies (active immunity).",
        "yr": "SS1"
      },
      {
        "q": "The BCG vaccine is used to prevent:",
        "o": ["Malaria", "Poliomyelitis", "Tuberculosis (TB)", "Cholera"],
        "a": 2,
        "e": "BCG (Bacillus Calmette-Guérin) is a vaccine against tuberculosis (TB) caused by Mycobacterium tuberculosis. It is typically given to newborns and young children. BCG also provides partial protection against leprosy.",
        "h": "BCG vaccine = prevents tuberculosis (TB).",
        "yr": "SS1"
      },
      {
        "q": "Active immunity differs from passive immunity in that active immunity:",
        "o": [
          "Is provided by ready-made antibodies given from outside",
          "Is immediate but short-lasting",
          "Is produced by the person's own immune system and is long-lasting",
          "Only occurs during infection, not from vaccination"
        ],
        "a": 2,
        "e": "Active immunity is produced when the body's own immune system responds to an antigen (from vaccination or infection) by producing antibodies and memory cells. It is long-lasting. Passive immunity uses externally provided antibodies and is immediate but temporary.",
        "h": "Active immunity = body makes own antibodies → long-lasting. Passive = given antibodies → immediate, short-lived.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is an example of passive immunity?",
        "o": [
          "Getting the polio vaccine",
          "Recovering from measles",
          "Receiving anti-tetanus serum (ready-made antibodies) after an injury",
          "Being vaccinated against hepatitis B"
        ],
        "a": 2,
        "e": "Anti-tetanus serum contains ready-made antibodies against tetanus toxin. These are injected directly into the patient, providing immediate protection. This is passive immunity — the body did not make its own antibodies.",
        "h": "Anti-tetanus serum = passive immunity (ready-made antibodies, immediate but short-lived).",
        "yr": "SS1"
      },
      {
        "q": "Proper hand washing before eating helps prevent disease because:",
        "o": [
          "It improves digestion",
          "It removes pathogens from hands before they can enter the body through food",
          "It strengthens the immune system",
          "It provides vitamins"
        ],
        "a": 1,
        "e": "Hand washing removes pathogens (bacteria, viruses, protozoa) that may be on hands from contact with contaminated surfaces, faeces, or sick people. Without hand washing, these pathogens can be ingested with food, causing gastrointestinal and other diseases.",
        "h": "Hand washing = removes pathogens → prevents faecal-oral disease transmission.",
        "yr": "SS1"
      },
      {
        "q": "Sanitation in public health involves:",
        "o": [
          "Personal cleanliness only",
          "Provision of safe water, sewage disposal, refuse management, and vector control",
          "Treatment of sick individuals only",
          "Taking vitamins regularly"
        ],
        "a": 1,
        "e": "Sanitation encompasses the provision of safe water supply, proper sewage and refuse disposal, environmental cleanliness, and control of disease vectors (mosquitoes, rats, flies). It prevents the spread of waterborne and vector-borne diseases.",
        "h": "Sanitation = safe water + sewage disposal + refuse management + vector control.",
        "yr": "SS1"
      },
      {
        "q": "The DPT vaccine provides protection against which three diseases?",
        "o": [
          "Diphtheria, Poliomyelitis, Tetanus",
          "Diphtheria, Pertussis (whooping cough), Tetanus",
          "Dengue, Pneumonia, Typhoid",
          "Diphtheria, Plague, Tuberculosis"
        ],
        "a": 1,
        "e": "DPT (or DTaP) vaccine protects against Diphtheria (bacterial throat infection), Pertussis/whooping cough (Bordetella pertussis), and Tetanus (Clostridium tetani toxin). It is given as a series starting in infancy.",
        "h": "DPT = Diphtheria + Pertussis (whooping cough) + Tetanus.",
        "yr": "SS1"
      },
      {
        "q": "Chlorination of drinking water is a sanitation measure that:",
        "o": [
          "Removes heavy metals",
          "Adds essential minerals",
          "Kills pathogenic micro-organisms (bacteria, viruses) in water",
          "Improves water taste only"
        ],
        "a": 2,
        "e": "Chlorination adds chlorine (or chlorine compounds) to water, which kills pathogenic bacteria, viruses, and protozoa. It is a cost-effective method of water treatment that prevents waterborne diseases like cholera, typhoid, and dysentery.",
        "h": "Chlorination = kills pathogens in water → prevents waterborne diseases.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following best describes primary prevention of disease?",
        "o": [
          "Treating patients after they fall ill",
          "Rehabilitating patients with chronic disease",
          "Preventing disease before it occurs through vaccination, hygiene, and vector control",
          "Screening populations for early signs of disease"
        ],
        "a": 2,
        "e": "Primary prevention aims to stop disease before it occurs. Strategies include vaccination (preventing infection), hygiene education, vector control (eliminating mosquito breeding sites), clean water supply, and sanitation.",
        "h": "Primary prevention = stop disease BEFORE it occurs (vaccination, hygiene, vector control).",
        "yr": "SS1"
      },
      {
        "q": "Food hygiene practices that prevent food-borne diseases include:",
        "o": [
          "Eating raw meat to preserve nutrients",
          "Storing food at room temperature for extended periods",
          "Proper cooking, refrigeration, and handling of food to prevent microbial contamination",
          "Mixing cooked and raw foods together"
        ],
        "a": 2,
        "e": "Food hygiene involves: cooking food to safe temperatures (kills pathogens), refrigerating perishables, using clean utensils, separating raw and cooked food, washing hands before food preparation, and properly disposing of food waste.",
        "h": "Food hygiene: proper cooking + refrigeration + clean handling = prevents food-borne illness.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following prevents malaria at the primary prevention level?",
        "o": [
          "Taking malaria treatment after infection",
          "Blood transfusion",
          "Using insecticide-treated bed nets and controlling mosquito breeding sites",
          "Only taking vitamins"
        ],
        "a": 2,
        "e": "Primary prevention of malaria involves stopping transmission: using insecticide-treated bed nets (ITNs), indoor residual spraying (IRS), eliminating stagnant water (mosquito breeding sites), using insect repellents, and chemoprophylaxis for high-risk individuals.",
        "h": "Malaria primary prevention = bed nets + mosquito control (stop transmission before infection).",
        "yr": "SS1"
      },
      {
        "q": "Which vaccine prevents poliomyelitis?",
        "o": ["BCG", "MMR", "OPV (Oral Polio Vaccine)", "DPT"],
        "a": 2,
        "e": "OPV (Oral Polio Vaccine) and IPV (Inactivated Polio Vaccine) are used to prevent poliomyelitis — a viral disease caused by poliovirus that can cause paralysis. The oral form (Sabin vaccine) is commonly used in national immunization campaigns.",
        "h": "OPV (Oral Polio Vaccine) = prevents poliomyelitis.",
        "yr": "SS1"
      },
      {
        "q": "Environmental hygiene involves:",
        "o": [
          "Only brushing teeth",
          "Keeping the environment clean — proper waste disposal, drainage, and vector control",
          "Only personal bathing",
          "Taking multivitamins"
        ],
        "a": 1,
        "e": "Environmental hygiene includes proper refuse and sewage disposal, draining stagnant water (reduces mosquito breeding), maintaining clean surroundings, controlling pests (rats, cockroaches), and proper food storage — all reducing exposure to pathogens.",
        "h": "Environmental hygiene = clean surroundings, waste disposal, drainage, pest control.",
        "yr": "SS1"
      },
      {
        "q": "The MMR vaccine provides protection against:",
        "o": [
          "Malaria, Meningitis, Rabies",
          "Measles, Mumps, Rubella",
          "Malaria, Mumps, Respiratory syncytial virus",
          "Meningitis, Mumps, Rotavirus"
        ],
        "a": 1,
        "e": "MMR vaccine protects against Measles, Mumps, and Rubella — three viral diseases. It is typically given in two doses during early childhood. Measles and mumps can cause serious complications including encephalitis and deafness.",
        "h": "MMR = Measles + Mumps + Rubella (three viral diseases).",
        "yr": "SS1"
      },
      {
        "q": "Secondary prevention of disease involves:",
        "o": [
          "Preventing disease before it occurs",
          "Rehabilitation after disease",
          "Early detection and treatment to prevent disease progression",
          "Vector control"
        ],
        "a": 2,
        "e": "Secondary prevention involves early detection (screening, diagnosis) and prompt treatment to stop disease progression and prevent complications. Examples: cervical cancer screening, early TB diagnosis and treatment, blood pressure monitoring.",
        "h": "Secondary prevention = early detection + prompt treatment (prevents progression).",
        "yr": "SS1"
      },
      {
        "q": "Herd immunity is achieved when:",
        "o": [
          "Every individual is vaccinated",
          "A sufficient proportion of a population is immune (through vaccination or prior infection) to protect even unvaccinated individuals",
          "All animals in a herd are treated",
          "Disease symptoms disappear spontaneously"
        ],
        "a": 1,
        "e": "Herd immunity occurs when a large enough percentage of a population is immune to an infectious disease (through vaccination or past infection) that the disease can no longer spread easily, protecting even those who are not immune.",
        "h": "Herd immunity = enough immune individuals → disease can't spread → protects whole community.",
        "yr": "SS1"
      },
      {
        "q": "Boiling water before drinking is an effective prevention against waterborne diseases because:",
        "o": [
          "It adds minerals to water",
          "It cools the water rapidly",
          "It kills pathogenic micro-organisms (bacteria, viruses, protozoa) present in the water",
          "It removes heavy metals only"
        ],
        "a": 2,
        "e": "Boiling water at 100°C (at sea level) for at least one minute kills almost all pathogenic bacteria, viruses, and protozoa (including Giardia, Cryptosporidium cysts). It is one of the simplest and most effective water purification methods.",
        "h": "Boiling kills pathogenic microbes in water → prevents cholera, typhoid, amoebic dysentery.",
        "yr": "SS1"
      },
      {
        "q": "Antibodies are produced by which type of white blood cell?",
        "o": ["Red blood cells", "Platelets", "B lymphocytes (B cells)", "Phagocytes"],
        "a": 2,
        "e": "B lymphocytes (B cells) are the white blood cells that produce antibodies (immunoglobulins) in response to a specific antigen. When activated by an antigen, B cells differentiate into plasma cells that secrete large quantities of specific antibodies.",
        "h": "Antibodies produced by B lymphocytes (B cells) → specific to each antigen.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a vector of malaria?",
        "o": ["Housefly", "Tsetse fly", "Female Anopheles mosquito", "Culex mosquito"],
        "a": 2,
        "e": "The female Anopheles mosquito is the vector for malaria — it carries Plasmodium and transmits it when it bites a human to feed on blood. The male Anopheles does not bite humans. Culex mosquitoes transmit filariasis and West Nile virus, not malaria.",
        "h": "Malaria vector = female Anopheles mosquito (transmits Plasmodium).",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Habitat",
    "topicCode": "SS1-BIO-19",
    "module": "Ecology",
    "term": "Third Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  A <span class=\"learn-keyword\">habitat</span> is the natural environment in which an organism lives and obtains all its requirements — food, water, shelter, and space for reproduction. Different habitats are characterised by distinct physical conditions that determine which organisms can survive there.\n</div>\n\n<h3 class=\"learn-subheading\">1. Meaning of Habitat</h3>\n<p class=\"learn-p\">A habitat provides an organism with:</p>\n<ul class=\"learn-list\">\n  <li>Suitable abiotic conditions (temperature, light, water, soil type)</li>\n  <li>Food sources (prey, plants, or nutrients)</li>\n  <li>Shelter and sites for reproduction</li>\n  <li>Other organisms of the same species for reproduction</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Types of Habitats</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Major Type</th><th>Subtypes</th><th>Examples</th></tr></thead>\n    <tbody>\n      <tr><td>Aquatic</td><td>Freshwater, Marine, Estuarine, Brackish</td><td>River, lake, ocean, estuary</td></tr>\n      <tr><td>Terrestrial</td><td>Forest, Grassland, Desert, Tundra</td><td>Tropical rainforest, savanna, Sahara, Arctic</td></tr>\n      <tr><td>Arboreal</td><td>Tree-dwelling</td><td>Monkeys, chameleons, tree snakes</td></tr>\n      <tr><td>Subterranean</td><td>Underground/Soil</td><td>Earthworms, moles, soil bacteria</td></tr>\n      <tr><td>Aerial</td><td>Air/sky</td><td>Birds, bats, insects in flight</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">3. Characteristics of Habitats</h3>\n<p class=\"learn-p\">Each habitat has unique abiotic characteristics that determine which organisms can live there:</p>\n<ul class=\"learn-list\">\n  <li><strong>Aquatic habitats:</strong> Water availability, dissolved oxygen, salinity, water current, depth, temperature, pH</li>\n  <li><strong>Terrestrial habitats:</strong> Temperature, rainfall, soil type, humidity, wind, topography</li>\n  <li><strong>Adaptation:</strong> Organisms have structural, physiological, and behavioural adaptations to their habitat</li>\n</ul>\n\n<h3 class=\"learn-subheading\">4. Habitat and Ecological Niche</h3>\n<p class=\"learn-p\">Habitat = WHERE an organism lives. Niche = HOW an organism lives (its role, diet, behaviour, interactions). Two species can share the same habitat but occupy different niches. No two species can occupy identical niches indefinitely (competitive exclusion principle).</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Remember: Habitat = WHERE an organism lives (the address). Niche = HOW it lives (the occupation/role). A pond is a habitat; a frog in a pond eating insects is occupying its niche. Major habitat divisions: aquatic (water) and terrestrial (land).\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> A habitat is where an organism lives and finds its requirements. Major types: aquatic (freshwater, marine, estuarine), terrestrial (forest, grassland, desert, tundra), arboreal (trees), subterranean (soil/underground), and aerial. Organisms are adapted to their habitats. Habitat = where (address); niche = role (occupation).\n</div>\n  ",
    "questions": [
      {
        "q": "A habitat is best defined as:",
        "o": [
          "The role of an organism in an ecosystem",
          "All organisms living in a region",
          "The natural environment where an organism lives and finds all its requirements",
          "The physical climate of a large region"
        ],
        "a": 2,
        "e": "A habitat is the specific place where an organism naturally lives and finds all its requirements — food, water, shelter, mates, and suitable abiotic conditions. Examples: a pond, a forest floor, the ocean, a tree canopy.",
        "h": "Habitat = where an organism lives and finds all its requirements.",
        "yr": "SS1"
      },
      {
        "q": "The major categories of habitats are:",
        "o": [
          "Urban and rural",
          "Hot and cold",
          "Aquatic and terrestrial (with subdivisions)",
          "Only tropical and polar"
        ],
        "a": 2,
        "e": "Habitats are broadly divided into aquatic (water-based: freshwater, marine, estuarine) and terrestrial (land-based: forest, grassland, desert, tundra). Additional types include arboreal (trees), subterranean (underground), and aerial.",
        "h": "Major habitat types: aquatic (water) and terrestrial (land) — with many subdivisions.",
        "yr": "SS1"
      },
      {
        "q": "How does a habitat differ from an ecological niche?",
        "o": [
          "They are exactly the same",
          "Habitat = where an organism lives; niche = the organism's role, diet, and behaviour in the ecosystem",
          "Niche = physical location; habitat = organism's role",
          "Both only refer to physical location"
        ],
        "a": 1,
        "e": "Habitat is WHERE an organism lives (its address). Niche is HOW it lives — including what it eats, how it feeds, when it is active, and all its interactions. A frog and a water snake may share the same pond habitat but occupy different niches.",
        "h": "Habitat = WHERE (address); Niche = HOW (role, diet, interactions).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is an example of an arboreal habitat?",
        "o": ["Soil", "Ocean", "Tree canopy where monkeys live", "Cave"],
        "a": 2,
        "e": "Arboreal habitats are tree-dwelling habitats. Organisms like monkeys, chameleons, tree frogs, and many birds live in tree canopies. They are adapted with grasping limbs, prehensile tails, or other features for life in trees.",
        "h": "Arboreal = tree-dwelling habitat (monkeys, chameleons, tree frogs).",
        "yr": "SS1"
      },
      {
        "q": "Earthworms are found in soil, making soil their:",
        "o": ["Niche", "Biome", "Habitat", "Ecosystem"],
        "a": 2,
        "e": "Soil is the habitat of earthworms — it is where they live, find food, shelter, and reproduce. Within the soil habitat, the earthworm occupies a specific niche as a detritivore (breaking down organic matter and improving soil aeration).",
        "h": "Soil = earthworm's habitat (where it lives); detritivore = its niche (what it does).",
        "yr": "SS1"
      },
      {
        "q": "Which abiotic factor is most important for determining whether a habitat is classified as aquatic or terrestrial?",
        "o": ["Temperature", "Presence of water as the medium of life", "Soil type", "Wind speed"],
        "a": 1,
        "e": "The fundamental distinction between aquatic and terrestrial habitats is whether water is the medium in which organisms live (aquatic) or whether organisms live on land and obtain water from their environment (terrestrial).",
        "h": "Aquatic = water as medium; terrestrial = land (organisms access water from environment).",
        "yr": "SS1"
      },
      {
        "q": "Which type of habitat is characterized by being found underground?",
        "o": ["Arboreal", "Aerial", "Subterranean", "Aquatic"],
        "a": 2,
        "e": "Subterranean habitats are underground environments — caves, soil, and rock crevices. Organisms like earthworms, moles, burrowing beetles, cave fish, and many soil bacteria inhabit subterranean habitats.",
        "h": "Subterranean = underground habitat (earthworms, moles, soil bacteria).",
        "yr": "SS1"
      },
      {
        "q": "Organisms are adapted to their habitats. A cactus storing water in its thick stem is an adaptation to:",
        "o": ["Aquatic habitat", "Forest habitat", "Desert (arid terrestrial) habitat", "Arboreal habitat"],
        "a": 2,
        "e": "Cacti are adapted to desert habitats — where water is scarce. Adaptations include: thick, fleshy stems to store water; reduced leaves (spines) to minimise water loss; deep or wide roots; CAM photosynthesis to reduce daytime water loss.",
        "h": "Cactus adaptations (water storage, reduced leaves) = desert habitat adaptations.",
        "yr": "SS1"
      },
      {
        "q": "The competitive exclusion principle states that:",
        "o": [
          "Two species in the same habitat always cooperate",
          "No two species can occupy exactly the same niche indefinitely — one will outcompete the other",
          "Species in different habitats never compete",
          "All species are equal in competition"
        ],
        "a": 1,
        "e": "The competitive exclusion principle (Gause's principle) states that two species competing for identical resources in the same niche cannot coexist — one will be more competitive and eventually exclude the other or drive it to occupy a different niche.",
        "h": "Competitive exclusion: no two species can occupy identical niches indefinitely.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following correctly matches an organism with its habitat type?",
        "o": [
          "Camel — aquatic habitat",
          "Tilapia — terrestrial habitat",
          "Bats in flight — aerial habitat",
          "Seaweed — desert habitat"
        ],
        "a": 2,
        "e": "Bats spend much of their time flying — using the aerial habitat. Camels are terrestrial (desert); tilapia are aquatic (freshwater); seaweed is aquatic (marine). Each is adapted to their respective habitat.",
        "h": "Bats = aerial habitat; camel = terrestrial (desert); tilapia = aquatic.",
        "yr": "SS1"
      },
      {
        "q": "A microhabitat is:",
        "o": [
          "A very large biome",
          "A small, specific habitat within a larger habitat (e.g., under a log, in leaf litter)",
          "An oceanic habitat",
          "A type of ecological niche"
        ],
        "a": 1,
        "e": "A microhabitat is a small, localized habitat within a larger one — for example, the underside of a log, a rock pool, or leaf litter. It has distinctive abiotic conditions and supports its own community of organisms.",
        "h": "Microhabitat = small habitat within a larger one (e.g., under a log, in leaf litter).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a key abiotic factor of freshwater habitats?",
        "o": ["Salinity (high salt)", "Dissolved oxygen content", "High sand content", "Absence of light"],
        "a": 1,
        "e": "Dissolved oxygen is a critical abiotic factor in freshwater habitats — fish and other aquatic organisms depend on it for aerobic respiration. Fast-flowing water typically has more dissolved oxygen than stagnant water.",
        "h": "Dissolved oxygen = critical abiotic factor in freshwater habitats.",
        "yr": "SS1"
      },
      {
        "q": "Bioluminescent deep-sea fish live in total darkness. What adaptation enables them to survive in this extreme habitat?",
        "o": [
          "Photosynthesis",
          "Production of light through chemical reactions in their bodies (bioluminescence)",
          "Storing sunlight during the day",
          "Very large ears"
        ],
        "a": 1,
        "e": "Deep-sea fish adapted to total darkness have developed bioluminescence — the ability to produce their own light through chemical reactions. This helps them attract prey, find mates, and communicate in the complete absence of sunlight.",
        "h": "Deep-sea fish: bioluminescence = adaptation to zero-light habitat.",
        "yr": "SS1"
      },
      {
        "q": "The ocean is an example of which type of habitat?",
        "o": ["Freshwater aquatic", "Terrestrial", "Marine aquatic", "Arboreal"],
        "a": 2,
        "e": "The ocean is a marine aquatic habitat — characterized by high salinity (~3.5%), vast size, light gradients with depth, and diverse organisms from phytoplankton to whales. It differs from freshwater habitats in salinity and ionic composition.",
        "h": "Ocean = marine aquatic habitat (high salinity, large, diverse).",
        "yr": "SS1"
      },
      {
        "q": "What does it mean for an organism to be adapted to its habitat?",
        "o": [
          "It has been removed from its habitat",
          "It has structural, physiological, or behavioural features that enable it to survive and reproduce in that habitat",
          "It can live in all habitats equally",
          "It is found only in captivity"
        ],
        "a": 1,
        "e": "Adaptation refers to features (structural, physiological, behavioural) that improve an organism's fitness in its specific habitat. These features evolve over many generations through natural selection. For example, webbed feet in ducks for swimming.",
        "h": "Adaptation = features evolved to improve survival in a specific habitat.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is NOT an abiotic characteristic of terrestrial habitats?",
        "o": ["Temperature", "Soil composition", "Wind speed", "Predator-prey relationships"],
        "a": 3,
        "e": "Predator-prey relationships are biotic (living) interactions, not abiotic factors. Abiotic characteristics of terrestrial habitats include temperature, rainfall, soil composition, wind, humidity, topography, and light intensity.",
        "h": "Predator-prey = biotic (living) factor, NOT abiotic.",
        "yr": "SS1"
      },
      {
        "q": "Why is the tropical rainforest considered a habitat of high biodiversity?",
        "o": [
          "It has harsh, extreme conditions",
          "It is the coldest habitat on Earth",
          "Constant warmth, high rainfall, and year-round photosynthesis provide abundant resources supporting enormous species diversity",
          "It has very few trees"
        ],
        "a": 2,
        "e": "Tropical rainforests have stable, warm temperatures (~25–30°C), very high rainfall (>200 cm/year), and abundant sunlight. These conditions support year-round plant growth, creating a complex multi-layered structure with niches for thousands of species.",
        "h": "Rainforest biodiversity: constant warmth + high rainfall → abundant resources → many niches.",
        "yr": "SS1"
      },
      {
        "q": "What is the relationship between habitat and biodiversity?",
        "o": [
          "The simpler the habitat, the higher the biodiversity",
          "Habitats with more diverse abiotic conditions and resources generally support greater biodiversity",
          "Biodiversity is the same in all habitats",
          "Ocean habitats always have less biodiversity than land"
        ],
        "a": 1,
        "e": "Habitats with complex, diverse physical structures and abundant resources (like tropical rainforests, coral reefs) provide more niches, supporting higher species diversity (biodiversity). Simple, extreme habitats (deserts, tundra) support fewer species.",
        "h": "Complex habitats with diverse resources = more niches = higher biodiversity.",
        "yr": "SS1"
      },
      {
        "q": "An organism's realized niche is:",
        "o": [
          "The ideal niche with no competition",
          "The actual niche an organism occupies after accounting for competition and other interactions",
          "Only its feeding habits",
          "Only its geographic location"
        ],
        "a": 1,
        "e": "The fundamental niche is the full range of conditions an organism could theoretically occupy. The realized niche is the actual, smaller portion it occupies due to competition, predation, and other biotic interactions limiting its use of resources.",
        "h": "Realized niche = actual niche occupied (smaller than fundamental niche due to competition).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following organisms best exemplifies adaptation to an aquatic habitat?",
        "o": [
          "Camel with hump for fat storage",
          "Cactus with spines instead of leaves",
          "Fish with streamlined body, gills, and fins",
          "Desert rat with long hind legs for jumping"
        ],
        "a": 2,
        "e": "Fish have multiple adaptations to aquatic habitats: streamlined body shape (reduces water resistance), gills (extract dissolved oxygen from water), fins (for steering and propulsion), and lateral line system (detects water pressure changes).",
        "h": "Fish: streamlined body + gills + fins = adaptations to aquatic habitat.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Aquatic Habitat",
    "topicCode": "SS1-BIO-20",
    "module": "Ecology",
    "term": "Third Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Aquatic habitats</span> are environments where water is the primary medium for life. They cover about 71% of Earth's surface and support enormous biodiversity. The two main categories are <span class=\"learn-keyword\">freshwater</span> (rivers, lakes, ponds) and <span class=\"learn-keyword\">marine</span> (oceans, seas), with <span class=\"learn-keyword\">estuarine</span> environments at the interface.\n</div>\n\n<h3 class=\"learn-subheading\">1. Freshwater Habitat</h3>\n<ul class=\"learn-list\">\n  <li>Low salinity (less than 0.5 ppt)</li>\n  <li>Includes lotic (flowing: rivers, streams) and lentic (still: lakes, ponds) environments</li>\n  <li><strong>Abiotic factors:</strong> Temperature, dissolved oxygen, pH, turbidity, current speed, light penetration</li>\n  <li><strong>Organisms:</strong> Tilapia, catfish, frogs, water hyacinth, algae, insect larvae, Amoeba</li>\n  <li>Organisms face osmotic challenges — freshwater organisms tend to gain water by osmosis</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Estuarine Habitat</h3>\n<p class=\"learn-p\">An estuary is where a river meets the sea — characterized by <strong>fluctuating salinity</strong> (brackish water), tidal flows, nutrient-rich waters, and variable temperature.</p>\n<ul class=\"learn-list\">\n  <li>Very productive ecosystem due to high nutrient input from both land and sea</li>\n  <li>Important nursery ground for many fish species</li>\n  <li>Organisms must be euryhaline (tolerant of wide salinity range): mudskippers, oysters, mangroves, crabs</li>\n  <li>Mangrove forests stabilize estuarine banks and provide habitat</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Marine Habitat</h3>\n<ul class=\"learn-list\">\n  <li>High salinity (~3.5% or 35 ppt)</li>\n  <li>Zones: intertidal (between tide marks), neritic (shallow coastal), pelagic (open water), benthic (sea floor), abyssal (deep sea)</li>\n  <li><strong>Organisms:</strong> Plankton, fish, whales, sharks, coral, sea anemones, jellyfish, starfish</li>\n  <li>Marine organisms have adaptations to resist dehydration caused by osmosis in high-salinity water</li>\n</ul>\n\n<h3 class=\"learn-subheading\">4. Adaptations to Aquatic Life</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Organism</th><th>Adaptation</th><th>Function</th></tr></thead>\n    <tbody>\n      <tr><td>Fish</td><td>Streamlined body, gills, fins, lateral line</td><td>Movement, breathing, sensing</td></tr>\n      <tr><td>Aquatic plants</td><td>Submerged leaves finely dissected; air spaces (aerenchyma)</td><td>Reduce resistance; buoyancy; gas exchange</td></tr>\n      <tr><td>Freshwater protozoa</td><td>Contractile vacuole</td><td>Expel excess water gained by osmosis</td></tr>\n      <tr><td>Marine fish</td><td>Drink large amounts of water; produce little concentrated urine</td><td>Counteract osmotic water loss</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Estuary = where river meets sea = fluctuating salinity. Freshwater organisms gain water by osmosis (need to expel it — e.g., contractile vacuole in Amoeba). Marine organisms lose water by osmosis (need to drink water). Estuarine organisms must tolerate both extremes (euryhaline).\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Aquatic habitats include freshwater (low salinity, rivers/ponds), estuarine (fluctuating salinity, where river meets sea), and marine (high salinity, oceans). Key adaptations: streamlined bodies, gills, fins (fish); contractile vacuoles (freshwater protozoa); aerenchyma (aquatic plants). Estuaries are highly productive and important as fish nurseries.\n</div>\n  ",
    "questions": [
      {
        "q": "An estuary is formed where:",
        "o": [
          "Two rivers meet",
          "A river meets the sea, creating a zone of fluctuating salinity",
          "The ocean becomes very deep",
          "A lake forms from rainfall"
        ],
        "a": 1,
        "e": "An estuary is the transition zone where a freshwater river meets the saltwater sea. Salinity fluctuates with tides and freshwater flow. Estuaries are highly productive ecosystems and serve as important nurseries for fish.",
        "h": "Estuary = where river meets sea = fluctuating (brackish) salinity.",
        "yr": "SS1"
      },
      {
        "q": "Freshwater organisms like Amoeba use a contractile vacuole to:",
        "o": [
          "Store food",
          "Absorb salt from water",
          "Expel excess water that enters the cell by osmosis",
          "Reproduce asexually"
        ],
        "a": 2,
        "e": "In freshwater, the surrounding water has a lower solute concentration than the organism's cytoplasm. Water continuously enters by osmosis. The contractile vacuole collects this excess water and pumps it out of the cell, preventing the cell from bursting.",
        "h": "Contractile vacuole = expels excess water gained by osmosis in freshwater organisms.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is a characteristic of estuarine habitats?",
        "o": [
          "Constant high salinity like the open ocean",
          "Constant low salinity like a river",
          "Fluctuating salinity (brackish water), tidal flows, and high nutrient content",
          "No biological organisms due to extreme conditions"
        ],
        "a": 2,
        "e": "Estuaries are characterized by variable salinity (mixing of fresh and salt water), tidal influences (periodic flooding and exposure), high turbidity, rich nutrient input from rivers, and high biological productivity.",
        "h": "Estuary characteristics: fluctuating salinity, tidal flows, high nutrients, high productivity.",
        "yr": "SS1"
      },
      {
        "q": "The approximate salinity of ocean water is:",
        "o": ["0.1%", "1.0%", "3.5% (35 ppt)", "10%"],
        "a": 2,
        "e": "The average salinity of ocean water is ~3.5% (35 parts per thousand, ppt). This is mainly sodium chloride (NaCl) with smaller amounts of other salts. Marine organisms are adapted to this high salt concentration.",
        "h": "Ocean salinity ≈ 3.5% (35 ppt) — mainly NaCl.",
        "yr": "SS1"
      },
      {
        "q": "Lotic freshwater habitats refer to:",
        "o": [
          "Still, standing water (lakes, ponds)",
          "Underground caves with water",
          "Flowing water habitats (rivers, streams)",
          "Coastal marine zones"
        ],
        "a": 2,
        "e": "Lotic means flowing — lotic freshwater habitats include rivers, streams, and springs where water moves continuously. Lentic habitats are still water (lakes, ponds). Lotic habitats typically have more dissolved oxygen due to turbulence.",
        "h": "Lotic = flowing freshwater (rivers, streams); lentic = still water (lakes, ponds).",
        "yr": "SS1"
      },
      {
        "q": "Why are estuaries described as among the most productive ecosystems on Earth?",
        "o": [
          "They have very deep water",
          "They receive nutrients from both rivers (land runoff) and the sea, and have high sunlight in shallow waters",
          "They are too salty for most organisms",
          "They lack decomposers"
        ],
        "a": 1,
        "e": "Estuaries are highly productive because they receive continuous nutrient inputs from rivers (nitrogen, phosphorus from land) and marine tidal flow, in shallow, sunlit waters that support prolific photosynthesis by phytoplankton and aquatic plants.",
        "h": "Estuaries productive: river + marine nutrients + shallow sunlit water = high photosynthesis.",
        "yr": "SS1"
      },
      {
        "q": "Fish gills are adapted for:",
        "o": [
          "Producing energy by photosynthesis",
          "Extracting dissolved oxygen from water for aerobic respiration",
          "Sensing water temperature",
          "Filtering food particles"
        ],
        "a": 1,
        "e": "Gills are highly vascularized structures with large surface area that extract dissolved oxygen (O₂) from water passing over them by diffusion. CO₂ waste is simultaneously expelled. They are the primary respiratory organs of most fish.",
        "h": "Gills = extract dissolved O₂ from water (by diffusion) for fish respiration.",
        "yr": "SS1"
      },
      {
        "q": "Mangrove forests are typically found in:",
        "o": [
          "Deep ocean environments",
          "Freshwater lakes",
          "Estuaries and coastal areas — in brackish, muddy, tidal zones",
          "Desert regions near rivers"
        ],
        "a": 2,
        "e": "Mangroves are salt-tolerant trees and shrubs that grow in intertidal zones of estuaries and tropical coastlines. They stabilize sediments with their prop roots, provide habitat for many species, and act as a nursery for fish.",
        "h": "Mangroves = estuarine/coastal tidal zones (salt-tolerant, stabilize sediments).",
        "yr": "SS1"
      },
      {
        "q": "Organisms that can tolerate a wide range of salinity are called:",
        "o": ["Stenohaline", "Euryhaline", "Eurythermal", "Stenothermal"],
        "a": 1,
        "e": "Euryhaline organisms can survive a wide range of salinities — important for estuarine animals that experience fluctuating salinity. Examples: salmon, blue crabs, mudskippers. Stenohaline organisms can only tolerate a narrow salinity range.",
        "h": "Euryhaline = tolerates wide salinity range (important for estuarine organisms).",
        "yr": "SS1"
      },
      {
        "q": "Aquatic plants like water lilies have large flat leaves that float. This adaptation helps with:",
        "o": [
          "Storing excess water",
          "Maximizing sunlight capture for photosynthesis at the water surface",
          "Preventing water entry into the leaf",
          "Resisting water currents"
        ],
        "a": 1,
        "e": "The large, flat, floating leaves of water lilies spread out horizontally on the water surface to maximize light capture for photosynthesis. Stomata are on the upper surface (exposed to air). Waxy coating prevents waterlogging.",
        "h": "Water lily floating leaves = maximize sunlight capture for photosynthesis.",
        "yr": "SS1"
      },
      {
        "q": "Marine fish lose water through osmosis because:",
        "o": [
          "Ocean water is less concentrated than their body fluids",
          "Ocean water is more concentrated (higher salinity) than their body fluids, causing water to leave by osmosis",
          "They breathe air",
          "Their gills are too large"
        ],
        "a": 1,
        "e": "Marine fish live in seawater (~3.5% salt) which is more concentrated than their body fluids. Water therefore moves out of their cells by osmosis. To compensate, marine fish drink large volumes of seawater and excrete concentrated urine.",
        "h": "Marine fish: seawater more concentrated → water leaves by osmosis → drink seawater to compensate.",
        "yr": "SS1"
      },
      {
        "q": "Phytoplankton are important in aquatic ecosystems because they:",
        "o": [
          "Decompose dead organisms",
          "Are the primary producers, forming the base of aquatic food chains through photosynthesis",
          "Fix nitrogen in water",
          "Provide oxygen by respiration"
        ],
        "a": 1,
        "e": "Phytoplankton (microscopic photosynthetic organisms including algae and cyanobacteria) are the primary producers in most aquatic ecosystems. They form the base of aquatic food chains and produce approximately 50% of Earth's oxygen.",
        "h": "Phytoplankton = primary producers in aquatic ecosystems (base of food chains).",
        "yr": "SS1"
      },
      {
        "q": "The benthic zone of a lake or ocean refers to:",
        "o": [
          "The surface layer with most light",
          "The middle water column",
          "The bottom (bed/floor) of the water body",
          "The zone between high and low tide"
        ],
        "a": 2,
        "e": "The benthic zone is the bottommost layer of an aquatic environment — the bed of rivers, lakes, or ocean. Benthic organisms (benthos) include bottom-dwelling organisms like worms, molluscs, crabs, and bacteria that live on or in the sediment.",
        "h": "Benthic zone = bottom of lake/ocean (home to benthic organisms).",
        "yr": "SS1"
      },
      {
        "q": "Why does fast-flowing river water typically contain more dissolved oxygen than stagnant pond water?",
        "o": [
          "Rivers are closer to the sun",
          "Turbulence in flowing water increases contact with the atmosphere, allowing more O₂ to dissolve",
          "Rivers have more fish",
          "Pond water contains more CO₂"
        ],
        "a": 1,
        "e": "Turbulence in fast-flowing water increases the surface area of water in contact with air, facilitating the dissolution of oxygen. Still water has less mixing and oxygen may become depleted, especially in warmer or deeper zones.",
        "h": "Fast-flowing water: turbulence → more O₂ dissolution than stagnant water.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is an adaptation of freshwater fish to their habitat?",
        "o": [
          "Drinking large volumes of water",
          "Producing large quantities of dilute urine to remove excess water gained by osmosis",
          "High tolerance of salt",
          "Producing no urine"
        ],
        "a": 1,
        "e": "Freshwater fish live in water more dilute than their body fluids. Water enters their bodies by osmosis. To maintain ionic balance, freshwater fish produce large amounts of very dilute urine, expelling excess water from the body.",
        "h": "Freshwater fish: gain water by osmosis → produce large amounts of dilute urine.",
        "yr": "SS1"
      },
      {
        "q": "The intertidal zone is found:",
        "o": [
          "In the deep ocean",
          "Between the high and low tide marks on a shore",
          "Only in freshwater rivers",
          "In the middle of the ocean"
        ],
        "a": 1,
        "e": "The intertidal (littoral) zone is the coastal area exposed to air at low tide and submerged at high tide. Organisms here (barnacles, limpets, crabs, mussels, seaweeds) must be adapted to periodic desiccation, wave action, and temperature fluctuations.",
        "h": "Intertidal zone = between high and low tide marks (alternately wet and dry).",
        "yr": "SS1"
      },
      {
        "q": "Aerenchyma tissue in aquatic plants serves to:",
        "o": [
          "Store starch",
          "Provide buoyancy and facilitate gas transport to submerged parts",
          "Absorb sunlight",
          "Resist grazing by herbivores"
        ],
        "a": 1,
        "e": "Aerenchyma is plant tissue with large intercellular air spaces. In aquatic plants, it provides buoyancy (keeping plant parts at the surface) and facilitates the transport of oxygen to submerged roots and stems where gas exchange is difficult.",
        "h": "Aerenchyma = air spaces in aquatic plants → buoyancy + O₂ transport to submerged parts.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following correctly describes a characteristic of marine habitats?",
        "o": [
          "Low salinity (less than 0.5 ppt)",
          "No light penetration at any depth",
          "High salinity (~35 ppt), vast size, and diverse zones from surface to abyssal deep",
          "Absence of decomposers"
        ],
        "a": 2,
        "e": "Marine habitats are characterized by high salinity (~35 ppt), enormous size (covers 71% of Earth's surface), and distinct zones: photic/euphotic zone (light-penetrated surface), mesopelagic (twilight), bathypelagic, and abyssal zones (deep, dark, cold).",
        "h": "Marine habitat: high salinity (~35 ppt), vast, diverse zones (surface to abyssal).",
        "yr": "SS1"
      },
      {
        "q": "An estuary is important for fish populations primarily because:",
        "o": [
          "It has the highest salinity of all aquatic habitats",
          "It serves as a nursery for juvenile fish, offering shelter and abundant food",
          "It has no predators",
          "It is the deepest aquatic habitat"
        ],
        "a": 1,
        "e": "Estuaries provide sheltered, nutrient-rich, shallow waters that are ideal nursery grounds for the larvae and juveniles of many commercially important fish and invertebrate species. The complex habitat structure (mangroves, seagrasses) offers protection from predators.",
        "h": "Estuaries = nursery grounds for juvenile fish (shelter + abundant food).",
        "yr": "SS1"
      },
      {
        "q": "Dissolved oxygen in water is reduced by:",
        "o": [
          "Fast water flow",
          "Low temperature",
          "High temperature and high organic pollution (decomposers consuming O₂)",
          "Low salinity"
        ],
        "a": 2,
        "e": "Dissolved oxygen decreases with: (1) higher water temperature (gases less soluble in warm water), and (2) decomposition of organic pollutants (bacteria use O₂ during aerobic decomposition). Oxygen-depleted water kills fish — this is the cause of fish kills in polluted rivers.",
        "h": "Dissolved O₂ reduced by: high temperature + organic pollution (decomposers use O₂).",
        "yr": "SS1"
      }
    ]
  }
  ,
  {
    "topic": "Terrestrial Habitat",
    "topicCode": "SS1-BIO-21",
    "module": "Ecology",
    "term": "Third Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  A <span class=\"learn-keyword\">terrestrial habitat</span> is a land-based environment where organisms live. Terrestrial habitats are the most diverse on Earth — ranging from tropical rainforests and savannas to deserts and tundra — each shaped by climate, soil, and topography.\n</div>\n\n<h3 class=\"learn-subheading\">1. Types of Terrestrial Habitats</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Habitat Type</th><th>Climate</th><th>Key Plants</th><th>Key Animals</th></tr></thead>\n    <tbody>\n      <tr><td>Tropical Rainforest</td><td>Hot, very high rainfall (>200 cm/yr), no dry season</td><td>Tall canopy trees, epiphytes, ferns</td><td>Monkeys, parrots, leopards, frogs</td></tr>\n      <tr><td>Savanna (Guinea savanna)</td><td>Warm, seasonal rainfall, long dry season</td><td>Grasses, scattered trees (acacia, baobab)</td><td>Elephants, lions, zebra, grass-cutters</td></tr>\n      <tr><td>Desert</td><td>Extremely low rainfall (<25 cm/yr), extreme temperatures</td><td>Cacti, succulents, thorn scrub</td><td>Camels, scorpions, desert foxes</td></tr>\n      <tr><td>Temperate Forest</td><td>Moderate rainfall, four seasons</td><td>Deciduous trees (oak, beech, maple)</td><td>Deer, badgers, foxes, owls</td></tr>\n      <tr><td>Tundra</td><td>Very cold, low rainfall, permafrost</td><td>Mosses, lichens, low shrubs</td><td>Polar bears, arctic foxes, caribou</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Soil — The Foundation of Terrestrial Habitats</h3>\n<ul class=\"learn-list\">\n  <li>Soil provides anchorage, water, and mineral nutrients for plants</li>\n  <li>Soil components: mineral particles (sand, silt, clay), humus (organic matter), water, air, and organisms</li>\n  <li>Loam (mixture of sand, silt, clay) is ideal for plant growth</li>\n  <li><strong>Humus:</strong> Decomposed organic matter — improves soil fertility, water-holding capacity, and aeration</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Adaptations to Terrestrial Life</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Organism</th><th>Challenge</th><th>Adaptation</th></tr></thead>\n    <tbody>\n      <tr><td>Desert plants (cacti)</td><td>Water scarcity</td><td>Thick stem stores water; spines reduce transpiration; deep roots</td></tr>\n      <tr><td>Deciduous trees</td><td>Cold winter (water frozen)</td><td>Shed leaves in autumn to reduce water loss and damage</td></tr>\n      <tr><td>Camels</td><td>Desert heat and water scarcity</td><td>Hump stores fat; efficient kidneys; wide feet for sand</td></tr>\n      <tr><td>Burrowing animals</td><td>Extreme surface temperatures</td><td>Burrow underground for insulation</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">4. Biotic and Abiotic Interactions</h3>\n<ul class=\"learn-list\">\n  <li>Terrestrial food chains typically start with grasses or trees (producers)</li>\n  <li>Decomposers (earthworms, bacteria, fungi) in soil are critical for nutrient recycling</li>\n  <li>Key abiotic factors: temperature, rainfall, soil type, humidity, wind, light</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Key challenge for terrestrial organisms = WATER CONSERVATION (unlike aquatic organisms). Desert adaptations all relate to reducing water loss and surviving heat. Loam soil is the best agricultural soil because it has a balanced mix of particles and good water-holding capacity.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Terrestrial habitats range from tropical rainforests to deserts and tundra. Soil provides nutrients, anchorage, and water. Key challenge for land organisms is water conservation. Adaptations: cacti store water; deciduous trees shed leaves; camels have efficient kidneys. Soil components: mineral particles + humus + water + air + organisms.\n</div>\n  ",
    "questions": [
      {
        "q": "The main challenge faced by organisms living in terrestrial habitats (unlike aquatic organisms) is:",
        "o": [
          "Excess of light",
          "Too much oxygen",
          "Preventing excessive water loss (desiccation)",
          "Lack of gravity"
        ],
        "a": 2,
        "e": "Unlike aquatic organisms surrounded by water, terrestrial organisms constantly risk drying out (desiccation). Most adaptations of land organisms — waxy cuticles, scales, waterproof skin, efficient kidneys — relate to conserving water.",
        "h": "Main challenge for terrestrial organisms = water conservation (preventing desiccation).",
        "yr": "SS1"
      },
      {
        "q": "Which terrestrial habitat receives the most rainfall and supports the greatest biodiversity?",
        "o": ["Desert", "Tundra", "Tropical rainforest", "Temperate grassland"],
        "a": 2,
        "e": "The tropical rainforest receives over 200 cm of rainfall per year with no distinct dry season. Constant warmth and moisture support year-round plant growth and an enormous diversity of species in complex multi-layered canopy structures.",
        "h": "Tropical rainforest = highest rainfall + most biodiversity of terrestrial habitats.",
        "yr": "SS1"
      },
      {
        "q": "Loam soil is considered ideal for plant growth because:",
        "o": [
          "It is entirely composed of clay",
          "It is entirely composed of sand",
          "It has a balanced mixture of sand, silt, and clay giving good drainage, aeration, and water retention",
          "It has no organic matter"
        ],
        "a": 2,
        "e": "Loam is a balanced mixture of sand (drainage/aeration), silt (nutrients), and clay (water retention) plus humus. This combination gives loam good drainage (prevents waterlogging), adequate water retention, good aeration, and nutrient availability — ideal for most crops.",
        "h": "Loam = balanced sand + silt + clay + humus = ideal agricultural soil.",
        "yr": "SS1"
      },
      {
        "q": "Deciduous trees shed their leaves in autumn as an adaptation to:",
        "o": [
          "Increase photosynthesis in winter",
          "Reduce water loss during cold winter when water is frozen and unavailable",
          "Attract animals for pollination",
          "Store food in the leaves"
        ],
        "a": 1,
        "e": "In winter, water in soil is frozen and unavailable. Leaves lose water through transpiration — continuing photosynthesis would lose precious water. Deciduous trees shed leaves to reduce water loss, then regrow them in spring when water is available again.",
        "h": "Deciduous trees shed leaves in winter = reduce water loss when frozen water unavailable.",
        "yr": "SS1"
      },
      {
        "q": "Humus in soil is:",
        "o": [
          "Inorganic mineral particles",
          "Water molecules held in soil",
          "Decomposed organic matter that improves soil fertility, water retention, and aeration",
          "Air pockets in soil"
        ],
        "a": 2,
        "e": "Humus is partially decomposed organic matter (from dead plants and animals). It improves soil fertility (releases nutrients), water-holding capacity (sponge-like structure), aeration (improves soil structure), and provides habitat for soil organisms.",
        "h": "Humus = decomposed organic matter; improves soil fertility, water retention, aeration.",
        "yr": "SS1"
      },
      {
        "q": "A camel's hump stores:",
        "o": ["Water", "Salt", "Fat (which can be metabolized to produce water and energy)", "Blood"],
        "a": 2,
        "e": "A camel's hump stores fat — not water. When food is scarce, fat is metabolized to provide energy and water (metabolic water). Other adaptations: efficient kidneys producing concentrated urine, nostrils that recapture water from exhaled air, and long legs keeping body away from hot ground.",
        "h": "Camel hump = FAT storage (not water); metabolized for energy and metabolic water.",
        "yr": "SS1"
      },
      {
        "q": "What is permafrost and in which terrestrial habitat is it found?",
        "o": [
          "Permanently frozen subsoil found in tropical rainforests",
          "Permanently frozen subsoil found in tundra regions",
          "A type of soil found in deserts",
          "A type of clay found in savannas"
        ],
        "a": 1,
        "e": "Permafrost is permanently frozen subsoil (ground that remains frozen year-round). It is found in tundra regions (Arctic and Antarctic). It severely limits plant root depth and drainage, and thawing of permafrost due to climate change is a major ecological concern.",
        "h": "Permafrost = permanently frozen subsoil = found in tundra regions.",
        "yr": "SS1"
      },
      {
        "q": "The soil component most responsible for retaining water in soil is:",
        "o": ["Sand particles (large)", "Gravel", "Clay particles (small, with large surface area)", "Air pockets"],
        "a": 2,
        "e": "Clay particles are very small with a large surface area and negative surface charges that attract and hold water molecules. Clay soils have high water-holding capacity but poor drainage. Sand particles are large, drain quickly, and hold little water.",
        "h": "Clay = small particles, large surface area, high water retention.",
        "yr": "SS1"
      },
      {
        "q": "The savanna (Guinea savanna) in West Africa is characterized by:",
        "o": [
          "Dense forest with no dry season",
          "Year-round rainfall and no fires",
          "Seasonal rainfall, long dry season, grasses with scattered trees, and seasonal fires",
          "Permanent snow and ice"
        ],
        "a": 2,
        "e": "The Guinea savanna is characterized by distinct wet and dry seasons, dominated by grasses with scattered fire-resistant trees (acacia, baobab). Seasonal fires during the dry season are a key ecological feature that maintains the grassland ecosystem.",
        "h": "Savanna = seasonal rainfall, long dry season, grasses + scattered trees, seasonal fires.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is an abiotic factor specific to terrestrial habitats?",
        "o": [
          "Salinity of water",
          "Water current",
          "Soil composition and pH",
          "Dissolved oxygen in water"
        ],
        "a": 2,
        "e": "Soil composition and pH are abiotic factors specific to terrestrial habitats — they determine which plants can grow and what nutrients are available. Salinity, water current, and dissolved oxygen are abiotic factors of aquatic habitats.",
        "h": "Soil composition/pH = abiotic factor specific to terrestrial habitats.",
        "yr": "SS1"
      },
      {
        "q": "Desert animals such as the jerboa (desert rat) are often nocturnal. This behaviour is an adaptation because:",
        "o": [
          "They prefer total darkness",
          "They avoid the intense daytime heat and water loss, becoming active during cooler nights",
          "Food is only available at night",
          "They are afraid of sunlight"
        ],
        "a": 1,
        "e": "Nocturnal behaviour in desert animals allows them to avoid the extreme daytime heat (which accelerates water loss through sweating/respiration). They shelter underground during the day and emerge at night when temperatures are cooler.",
        "h": "Desert animals nocturnal = avoid daytime heat and water loss.",
        "yr": "SS1"
      },
      {
        "q": "Earthworms are important to terrestrial habitats because they:",
        "o": [
          "Fix atmospheric nitrogen",
          "Photosynthesize in soil",
          "Aerate and mix soil, improving drainage and fertility as decomposers",
          "Produce seeds"
        ],
        "a": 2,
        "e": "Earthworms burrow through soil, physically mixing layers and creating channels that improve aeration and drainage. As they feed on organic matter, they produce casts rich in nutrients. Darwin called them 'nature's ploughs.'",
        "h": "Earthworms: aerate/mix soil + decompose organic matter = improve soil fertility.",
        "yr": "SS1"
      },
      {
        "q": "Which soil type has the poorest water-holding capacity and drains most quickly?",
        "o": ["Clay soil", "Loam soil", "Sandy soil", "Peaty soil"],
        "a": 2,
        "e": "Sandy soil has large particles with large spaces between them. Water drains rapidly through these spaces and does not adhere strongly to sand particles. Sandy soils dry out quickly and are poor in nutrients but have excellent aeration.",
        "h": "Sandy soil = large particles, drains quickly, poor water retention.",
        "yr": "SS1"
      },
      {
        "q": "The waxy cuticle on plant leaves is an adaptation that:",
        "o": [
          "Increases photosynthesis",
          "Reduces water loss by transpiration in terrestrial conditions",
          "Absorbs water from air",
          "Attracts pollinators"
        ],
        "a": 1,
        "e": "The waxy cuticle is a waterproof layer covering the epidermis of plant leaves and stems. It significantly reduces water loss by transpiration, which is a critical adaptation for terrestrial plants, especially in dry environments.",
        "h": "Waxy cuticle = waterproof layer, reduces transpiration water loss in terrestrial plants.",
        "yr": "SS1"
      },
      {
        "q": "The four main components of soil are:",
        "o": [
          "Only sand, clay, and water",
          "Mineral particles, humus (organic matter), water (soil solution), and air",
          "Only minerals and water",
          "Sand, salt, water, and bacteria only"
        ],
        "a": 1,
        "e": "Soil consists of four main components: mineral particles (sand, silt, clay), organic matter/humus, water (soil solution containing dissolved nutrients), and air (in pore spaces). Living organisms (bacteria, fungi, earthworms) are also sometimes listed as the fifth component.",
        "h": "Soil = mineral particles + humus + water + air (+ living organisms).",
        "yr": "SS1"
      },
      {
        "q": "What is the dominant vegetation in the tundra biome?",
        "o": [
          "Tall trees like oaks",
          "Cacti and succulents",
          "Mosses, lichens, and low-growing shrubs",
          "Dense tropical rainforest trees"
        ],
        "a": 2,
        "e": "The tundra has extremely cold temperatures with permafrost just below the surface. This limits plant root depth and the growing season. Only low-growing, cold-resistant plants like mosses, lichens, sedges, and dwarf shrubs can survive.",
        "h": "Tundra vegetation = mosses, lichens, low shrubs (no tall trees due to cold + permafrost).",
        "yr": "SS1"
      },
      {
        "q": "Overgrazing on savanna grasslands can lead to:",
        "o": [
          "Increased biodiversity",
          "Desertification — loss of vegetation cover, soil erosion, and spread of desert conditions",
          "Improved soil fertility",
          "Formation of forests"
        ],
        "a": 1,
        "e": "Overgrazing removes vegetation cover, exposing soil to wind and rain erosion. Soil becomes compacted and loses humus. Over time, this can lead to desertification — the spread of desert-like conditions into formerly vegetated land.",
        "h": "Overgrazing → soil erosion → loss of vegetation → desertification.",
        "yr": "SS1"
      },
      {
        "q": "Fire-resistant bark and underground roots are adaptations of savanna plants to:",
        "o": [
          "Flooding",
          "Cold temperatures",
          "Seasonal fires that regularly sweep through the savanna",
          "Extreme drought only"
        ],
        "a": 2,
        "e": "Savanna plants have evolved fire resistance — thick, corky bark protects trees from surface fires; many grasses and trees have extensive underground root systems that survive fire and rapidly resprout after burning.",
        "h": "Fire-resistant bark + underground roots = savanna plant adaptations to seasonal fires.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following correctly describes the relationship between climate and terrestrial biome type?",
        "o": [
          "Cold, dry climate → tropical rainforest",
          "Hot, very wet climate → desert",
          "Hot, seasonally wet/dry climate → savanna; very hot, very dry → desert; cold → tundra",
          "All climates support the same type of vegetation"
        ],
        "a": 2,
        "e": "Climate (temperature and precipitation) is the primary determinant of biome type. Hot + very wet = tropical rainforest; hot + seasonal rainfall = savanna; hot + very dry = desert; cold + dry = tundra. Temperature and rainfall patterns determine which plants and animals can survive.",
        "h": "Climate determines biome: hot+wet=rainforest; hot+seasonal=savanna; hot+dry=desert; cold=tundra.",
        "yr": "SS1"
      },
      {
        "q": "The process of soil formation from weathered rock and organic matter over time is called:",
        "o": ["Photosynthesis", "Decomposition", "Pedogenesis (soil formation)", "Transpiration"],
        "a": 2,
        "e": "Pedogenesis is the process of soil formation over long periods. It involves physical and chemical weathering of parent rock (breaking into mineral particles), accumulation of organic matter from plants and animals, and activity of soil organisms.",
        "h": "Pedogenesis = soil formation from weathered rock + organic matter (very slow process).",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Grassland Habitat",
    "topicCode": "SS1-BIO-22",
    "module": "Ecology",
    "term": "Third Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Grassland habitats</span> are terrestrial ecosystems dominated by grasses and herbs, with few or no trees. They cover about 25% of Earth's land surface and are found on every continent except Antarctica. In Nigeria and West Africa, the Guinea Savanna is the main grassland type.\n</div>\n\n<h3 class=\"learn-subheading\">1. Types of Grasslands</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Type</th><th>Location/Climate</th><th>Key Features</th></tr></thead>\n    <tbody>\n      <tr><td>Savanna</td><td>Tropical, seasonal rainfall, Africa/S.America/Asia</td><td>Grass dominant; scattered trees; seasonal fires</td></tr>\n      <tr><td>Prairie</td><td>Temperate, North America</td><td>Tall grasses; deep, fertile soil; no trees</td></tr>\n      <tr><td>Steppe</td><td>Temperate, Eurasia</td><td>Short, dry grasses; cold winters</td></tr>\n      <tr><td>Pampas</td><td>Temperate, South America</td><td>Tall grasses; fertile soil</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Characteristics of Grassland Habitats</h3>\n<ul class=\"learn-list\">\n  <li>Dominated by grasses (Poaceae family) and herbaceous plants</li>\n  <li>Rainfall: 25–75 cm/year (more than desert, less than forest)</li>\n  <li>Seasonal drought and periodic fires maintain the grassland (prevent tree growth)</li>\n  <li>Rich, deep soils with abundant humus from decomposing grass litter</li>\n  <li>High biodiversity, especially of large herbivores, birds, and insects</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Adaptations of Grassland Organisms</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Organism</th><th>Adaptation</th><th>Benefit</th></tr></thead>\n    <tbody>\n      <tr><td>Grasses</td><td>Meristem at base of leaves; deep fibrous roots</td><td>Regrow after grazing or fire; resist drought</td></tr>\n      <tr><td>Large herbivores (zebra, cattle)</td><td>Flat, grinding molar teeth; long digestive tract with cellulose-digesting bacteria</td><td>Efficiently process tough grass fibers</td></tr>\n      <tr><td>Ground-nesting birds</td><td>Camouflaged feathers</td><td>Protection from predators on open ground</td></tr>\n      <tr><td>Burrowing animals (aardvark, prairie dog)</td><td>Strong limbs for digging</td><td>Shelter from predators and temperature extremes</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">4. Ecological Importance of Grasslands</h3>\n<ul class=\"learn-list\">\n  <li>Carbon storage — grassland soils store large amounts of carbon</li>\n  <li>Watershed protection — prevent soil erosion and regulate water flow</li>\n  <li>Biodiversity hotspots — support diverse food webs</li>\n  <li>Agriculture — converted to croplands and pastures (wheat, maize, sorghum cultivation)</li>\n  <li>Grassland degradation (overgrazing, farming) leads to soil erosion and desertification</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Key grassland features: grass meristem at BASE of leaf (not tip) — this is why grass can be grazed or mown and still grow back. Fires and grazing MAINTAIN grasslands by preventing tree colonisation. Grassland soils are among the most fertile on Earth due to deep humus accumulation.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Grasslands (savanna, prairie, steppe) are dominated by grasses with 25–75 cm/year rainfall. Seasonal drought and fire maintain them. Grass meristems at leaf bases allow regrowth after grazing/fire. Herbivores have grinding teeth and cellulose-digesting gut bacteria. Grasslands have fertile soils, store carbon, and are important for agriculture. Overgrazing threatens them with desertification.\n</div>\n  ",
    "questions": [
      {
        "q": "Grassland habitats are dominated by which plant family?",
        "o": ["Leguminosae (legume family)", "Poaceae (grass family)", "Euphorbiaceae", "Asteraceae (daisy family)"],
        "a": 1,
        "e": "Grasslands are dominated by plants of the family Poaceae (grasses), which include many important species: Andropogon, Panicum, Imperata (savanna), wheat, maize, and sorghum (agricultural grasslands).",
        "h": "Grasslands dominated by Poaceae (grass family).",
        "yr": "SS1"
      },
      {
        "q": "Why do grasses recover quickly after being grazed or burned?",
        "o": [
          "Their roots are above the soil",
          "Their leaves produce photosynthates rapidly",
          "Their meristem (growth region) is located at the BASE of the leaf, below the zone of damage",
          "They store food in their flowers"
        ],
        "a": 2,
        "e": "Unlike most plants (where the meristem is at the leaf tip), grass meristems are intercalary — located at the base of each leaf blade, near the soil level. This means grazing or fire removes only the leaf tip, leaving the growth zone intact to produce new growth.",
        "h": "Grass meristem at BASE of leaf → regrows after grazing or fire.",
        "yr": "SS1"
      },
      {
        "q": "The seasonal fires in savanna grasslands serve an ecological function by:",
        "o": [
          "Killing all grasses permanently",
          "Preventing tree and woody shrub colonization, maintaining the open grassland",
          "Reducing soil fertility",
          "Eliminating all animal life"
        ],
        "a": 1,
        "e": "Savanna fires remove accumulated dry grass, kill tree seedlings that might colonize the grassland, recycle nutrients from burnt material into the soil, and promote fresh grass growth. Without fire and grazing, savannas would gradually become forests.",
        "h": "Savanna fires prevent tree colonisation → maintain open grassland ecosystem.",
        "yr": "SS1"
      },
      {
        "q": "What annual rainfall range typically characterises grassland habitats?",
        "o": ["Less than 25 cm (desert level)", "25–75 cm", "Over 200 cm (rainforest level)", "More than 100 cm (forest level)"],
        "a": 1,
        "e": "Grasslands typically receive 25–75 cm of rainfall per year — enough to support grasses and herbs but not enough for dense forest. Below 25 cm, deserts form. Above 75–100 cm, trees begin to dominate and forests develop.",
        "h": "Grassland rainfall: 25–75 cm/year (between desert and forest).",
        "yr": "SS1"
      },
      {
        "q": "Herbivores in grasslands have flat, ridged molar teeth. This is an adaptation for:",
        "o": [
          "Tearing flesh",
          "Grinding tough grass fibres",
          "Biting bark from trees",
          "Sucking nectar from flowers"
        ],
        "a": 1,
        "e": "Grass is tough, abrasive, and rich in cellulose (which is hard to digest). Grassland herbivores (cattle, zebra, horses) have flat, ridged molars adapted for grinding tough grass fibers into fine particles to maximize digestive surface area.",
        "h": "Flat ridged molars = adaptation for grinding tough grass fibres.",
        "yr": "SS1"
      },
      {
        "q": "Cellulose in grass is digested by grassland herbivores through the help of:",
        "o": [
          "Their own stomach acid",
          "Symbiotic cellulose-digesting bacteria in their gut (rumen)",
          "Bile produced by the liver",
          "Photosynthesis"
        ],
        "a": 1,
        "e": "Mammals cannot produce cellulase (the enzyme that breaks down cellulose). Ruminants (cattle, sheep, goats) and other herbivores harbor symbiotic bacteria and protozoa in their rumen or cecum that produce cellulase and ferment cellulose.",
        "h": "Cellulose digestion in herbivores = symbiotic bacteria in gut (rumen) produce cellulase.",
        "yr": "SS1"
      },
      {
        "q": "Grassland soils are among the most fertile because:",
        "o": [
          "They are made entirely of clay",
          "They have no micro-organisms",
          "Deep fibrous grass roots add large amounts of organic matter, building thick, humus-rich topsoil",
          "They receive very high rainfall"
        ],
        "a": 2,
        "e": "Grasses have deep, dense, fibrous root systems that penetrate soil deeply. When roots die and decompose, they add enormous quantities of organic matter to form thick, humus-rich topsoil. Prairie and pampas soils are among the world's most productive agricultural lands.",
        "h": "Grassland soils fertile: deep fibrous roots decay → thick humus-rich topsoil.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following correctly identifies a grassland type and its location?",
        "o": [
          "Prairie — South America",
          "Pampas — North America",
          "Steppe — Eurasia (Russia, Central Asia)",
          "Savanna — Arctic regions"
        ],
        "a": 2,
        "e": "Steppe grasslands are found in Eurasia — from Eastern Europe through Russia to Central Asia. They have cold winters, hot dry summers, and short grasses. Prairie = North America; Pampas = South America; Savanna = tropical Africa/South America.",
        "h": "Steppe = Eurasia; Prairie = North America; Pampas = South America; Savanna = tropical.",
        "yr": "SS1"
      },
      {
        "q": "What threat does overgrazing pose to grassland habitats?",
        "o": [
          "It increases soil fertility",
          "It increases grass species diversity",
          "It removes vegetation cover, leading to soil erosion and desertification",
          "It increases rainfall"
        ],
        "a": 2,
        "e": "Overgrazing removes protective vegetation cover, exposes bare soil to wind and rain erosion, compacts soil (reducing water infiltration), depletes soil nutrients, and can eventually lead to desertification — the conversion of grassland to desert.",
        "h": "Overgrazing → bare soil → erosion → soil degradation → desertification.",
        "yr": "SS1"
      },
      {
        "q": "The deep fibrous root system of grasses serves which ecological function?",
        "o": [
          "Producing seeds underground",
          "Photosynthesizing in the soil",
          "Anchoring plants, absorbing water/minerals, preventing soil erosion, and adding organic matter to soil",
          "Storing water for drinking by animals"
        ],
        "a": 2,
        "e": "Grass fibrous root systems: (1) anchor the plant against wind; (2) absorb water and minerals from deep in soil; (3) bind soil particles together, preventing erosion; and (4) when roots die, they add organic matter (humus) to improve soil fertility.",
        "h": "Grass fibrous roots: anchor + absorb water + prevent erosion + add humus.",
        "yr": "SS1"
      },
      {
        "q": "The Guinea savanna zone of Nigeria is characterized by:",
        "o": [
          "Dense evergreen forest with no dry season",
          "Grasses, scattered trees, seasonal rainfall, and a long dry season",
          "Desert vegetation with cacti",
          "Permanent snow and permafrost"
        ],
        "a": 1,
        "e": "The Guinea savanna in central Nigeria is characterized by tall grasses, scattered fire-resistant trees (shea, locust bean, dawadawa), a wet season (April–October) and a long dry season with Harmattan winds. Seasonal fires are common.",
        "h": "Guinea savanna = tall grasses + scattered trees + wet and dry seasons + seasonal fires.",
        "yr": "SS1"
      },
      {
        "q": "Animals that burrow in grasslands (e.g., moles, aardvarks, prairie dogs) benefit from burrows by:",
        "o": [
          "Accessing more sunlight",
          "Finding more grass to eat",
          "Gaining protection from predators and insulation from surface temperature extremes",
          "Storing water in the burrow"
        ],
        "a": 2,
        "e": "Burrows provide shelter from predators (hard for larger carnivores to follow), insulation from extreme surface temperatures (cool in summer, warm in winter), and a safe site for rearing young. This is a behavioural/structural adaptation to open grassland life.",
        "h": "Burrows = protection from predators + insulation from temperature extremes.",
        "yr": "SS1"
      },
      {
        "q": "Camouflage in grassland animals (e.g., lions, grasshoppers) helps with:",
        "o": [
          "Photosynthesis",
          "Blending with the grassland background for hunting or avoiding predation",
          "Attracting mates only",
          "Digesting cellulose"
        ],
        "a": 1,
        "e": "Camouflage — coloration matching the environment — is an important adaptation in open grasslands where there is little cover. Predators like lions use it to approach prey undetected; prey species like grasshoppers and ground-nesting birds use it to avoid detection.",
        "h": "Camouflage = blend with grassland background → predators hunt, prey evades detection.",
        "yr": "SS1"
      },
      {
        "q": "Grasslands store large amounts of carbon in:",
        "o": [
          "Their leaves",
          "The atmosphere above them",
          "Their deep, humus-rich soils",
          "Animal biomass only"
        ],
        "a": 2,
        "e": "Grassland soils are one of Earth's major carbon sinks. Organic carbon from decomposing roots and plant matter accumulates as humus in deep soils. Globally, grassland soils contain more carbon than grassland vegetation — making soil carbon a critical component of the global carbon budget.",
        "h": "Grasslands store carbon in deep humus-rich soils (major carbon sink).",
        "yr": "SS1"
      },
      {
        "q": "Which grassland type is found in North America and is known for its very fertile soils and tall grasses?",
        "o": ["Savanna", "Steppe", "Prairie", "Pampas"],
        "a": 2,
        "e": "The North American prairie is one of the world's most fertile grassland ecosystems. Historically dominated by tall-grass prairie (1–3 m tall grasses), much of it has been converted to wheat and maize farmland due to its exceptionally deep, fertile soil.",
        "h": "Prairie = North American grassland (fertile soil, tall grasses; mostly converted to farmland).",
        "yr": "SS1"
      },
      {
        "q": "The term 'edaphic climax' in grassland ecology refers to:",
        "o": [
          "The final stage of grassland development into forest",
          "A stable vegetation community maintained by soil conditions rather than climate",
          "The effect of grazing on grass species",
          "The flowering stage of grasses"
        ],
        "a": 1,
        "e": "An edaphic climax is a stable plant community maintained by soil conditions (e.g., poor, waterlogged, or shallow soil) that prevents the natural climax vegetation (usually forest) from developing, even if the climate would otherwise support trees.",
        "h": "Edaphic climax = stable community maintained by soil factors (prevents expected climax vegetation).",
        "yr": "SS1"
      },
      {
        "q": "Why are grasslands important for water conservation in their catchment areas?",
        "o": [
          "They block all rainfall",
          "Grass roots improve soil structure, increasing water infiltration and reducing surface runoff and erosion",
          "They have no effect on water",
          "They completely dry out watersheds"
        ],
        "a": 1,
        "e": "Grass roots bind soil particles and create pore channels that allow water to infiltrate slowly into the soil (recharging groundwater) rather than running off the surface. This reduces flooding, erosion, and sedimentation of rivers.",
        "h": "Grass roots improve infiltration → reduce runoff → protect watersheds and reduce erosion.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following best describes the difference between savanna and prairie?",
        "o": [
          "They are identical",
          "Savanna is tropical with scattered trees and seasonal fires; prairie is temperate with no/few trees and very fertile soils",
          "Prairie is tropical; savanna is in North America",
          "Savanna has more rainfall than prairie"
        ],
        "a": 1,
        "e": "Savanna: tropical, Africa/South America, with scattered fire-resistant trees, seasonal fires, and distinct wet/dry seasons. Prairie: temperate North America, very fertile deep soil, historically tall grassland with no trees, colder winters.",
        "h": "Savanna = tropical + scattered trees + fires; Prairie = temperate + no trees + very fertile soil.",
        "yr": "SS1"
      },
      {
        "q": "The Harmattan wind affects the Nigerian savanna by:",
        "o": [
          "Bringing heavy rainfall",
          "Cooling and moistening the air",
          "Bringing dry, dusty, cold air from the Sahara, causing desiccation and dry conditions",
          "Increasing humidity"
        ],
        "a": 2,
        "e": "The Harmattan is a dry, dusty northeast trade wind that blows from the Sahara Desert across West Africa (November–March). It causes extreme dryness, low humidity, haze, and cold nights — the dry season conditions that characterise the Nigerian savanna.",
        "h": "Harmattan = dry, dusty Saharan wind → desiccation and dry season in Nigerian savanna.",
        "yr": "SS1"
      },
      {
        "q": "How does biodiversity in grasslands compare to tropical rainforests?",
        "o": [
          "Grasslands always have more biodiversity than rainforests",
          "Grasslands have lower overall species diversity than rainforests but support large numbers of specialist grass-dependent species",
          "Both have exactly the same biodiversity",
          "Grasslands have no biodiversity"
        ],
        "a": 1,
        "e": "Tropical rainforests have the highest overall biodiversity. Grasslands have lower total species diversity but support abundant specialist species — large herbivore herds, ground-nesting birds, burrowing mammals, grasshoppers — adapted specifically to the open grassland structure.",
        "h": "Grasslands: lower total biodiversity than rainforests, but abundant specialist grass-dependent species.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Reproduction in Unicellular Organisms and Invertebrates I",
    "topicCode": "SS1-BIO-23",
    "module": "Reproduction",
    "term": "Third Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Reproduction</span> is the biological process by which organisms produce new individuals of the same species, ensuring the <span class=\"learn-keyword\">continuity of life</span>. There are two fundamental types: <span class=\"learn-keyword\">asexual reproduction</span> (involving only one parent, no fusion of gametes) and <span class=\"learn-keyword\">sexual reproduction</span> (involving fusion of male and female gametes). In this topic, we focus on reproduction in unicellular organisms and simpler invertebrates.\n</div>\n\n<h3 class=\"learn-subheading\">1. Types of Reproduction</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Feature</th><th>Asexual Reproduction</th><th>Sexual Reproduction</th></tr></thead>\n    <tbody>\n      <tr><td>Parents involved</td><td>One parent</td><td>Usually two parents</td></tr>\n      <tr><td>Gametes</td><td>No gametes</td><td>Fusion of gametes (fertilization)</td></tr>\n      <tr><td>Genetic variation</td><td>Offspring genetically identical (clones)</td><td>Offspring genetically varied</td></tr>\n      <tr><td>Speed</td><td>Rapid</td><td>Usually slower</td></tr>\n      <tr><td>Examples</td><td>Binary fission, budding, spore formation</td><td>Conjugation, fertilization</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Reproduction in Unicellular Organisms</h3>\n<p class=\"learn-p\"><strong>Amoeba (Binary Fission):</strong></p>\n<ul class=\"learn-list\">\n  <li>Nucleus divides (mitosis) → cytoplasm divides → two identical daughter cells</li>\n  <li>Asexual; very rapid; daughter cells are clones of parent</li>\n  <li>Also forms cysts (dormant spores) under unfavorable conditions</li>\n</ul>\n<p class=\"learn-p\"><strong>Paramecium:</strong></p>\n<ul class=\"learn-list\">\n  <li><strong>Asexual:</strong> Binary fission (transverse) — divides across the middle</li>\n  <li><strong>Sexual:</strong> Conjugation — two Paramecia pair up, exchange micronuclei (genetic material), then separate and divide</li>\n</ul>\n<p class=\"learn-p\"><strong>Yeast (Budding):</strong></p>\n<ul class=\"learn-list\">\n  <li>A small bud grows from the parent cell</li>\n  <li>Nucleus divides; one nucleus enters bud</li>\n  <li>Bud grows and eventually separates to form daughter cell</li>\n  <li>Asexual; rapid population growth under favorable conditions</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Reproduction in Simple Invertebrates</h3>\n<p class=\"learn-p\"><strong>Hydra (Budding):</strong></p>\n<ul class=\"learn-list\">\n  <li>A small bud grows from the parent's body wall by repeated mitosis</li>\n  <li>Develops tentacles and mouth, then detaches as a new individual</li>\n  <li>Also reproduces sexually (separate male and female gonads) during unfavorable conditions</li>\n</ul>\n<p class=\"learn-p\"><strong>Spirogyra (Conjugation):</strong></p>\n<ul class=\"learn-list\">\n  <li>A filamentous green alga that reproduces sexually by conjugation</li>\n  <li>Two filaments align; conjugation tubes form; cell contents (gametes) from one filament move into the other</li>\n  <li>Zygospores form, which are resistant and can survive unfavorable conditions</li>\n</ul>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Binary fission = asexual reproduction in bacteria and Amoeba (one parent → two identical daughters). Budding = asexual reproduction in yeast and Hydra (bud grows off parent). Conjugation = sexual reproduction in Paramecium and Spirogyra (genetic material exchanged). Sexual reproduction produces genetic variation; asexual produces clones.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Asexual reproduction: binary fission (Amoeba, bacteria), budding (yeast, Hydra). Sexual reproduction: conjugation (Paramecium, Spirogyra). Asexual = one parent, clones, fast. Sexual = two parents, genetic variation, usually slower. Most organisms switch to sexual reproduction under unfavorable conditions to generate variation for adaptability.\n</div>\n  ",
    "questions": [
      {
        "q": "Asexual reproduction is defined as reproduction that:",
        "o": [
          "Always involves two parents",
          "Involves fusion of gametes",
          "Involves only one parent and produces genetically identical offspring",
          "Always occurs in sexual organisms"
        ],
        "a": 2,
        "e": "Asexual reproduction involves a single parent producing offspring through non-sexual means (binary fission, budding, spore formation). Offspring are genetically identical to the parent (clones) because no mixing of genetic material from two parents occurs.",
        "h": "Asexual reproduction: one parent, no gametes, offspring are clones (genetically identical).",
        "yr": "SS1"
      },
      {
        "q": "Which method of asexual reproduction does Amoeba use?",
        "o": ["Budding", "Conjugation", "Binary fission", "Spore formation"],
        "a": 2,
        "e": "Amoeba reproduces asexually by binary fission: the nucleus divides by mitosis, then the cytoplasm divides, producing two genetically identical daughter cells. This can occur every few hours under favorable conditions.",
        "h": "Amoeba = binary fission (nucleus divides → cytoplasm divides → two daughter cells).",
        "yr": "SS1"
      },
      {
        "q": "Budding in yeast involves:",
        "o": [
          "The cell splitting into two equal halves",
          "A small outgrowth (bud) developing from the parent cell and eventually separating as a new individual",
          "Two cells exchanging genetic material",
          "Formation of spores"
        ],
        "a": 1,
        "e": "In yeast budding, a small bud forms as an outgrowth of the parent cell. The parent's nucleus divides by mitosis and one nucleus moves into the bud. The bud enlarges and eventually detaches (or remains as a chain) to form a new yeast cell.",
        "h": "Yeast budding: bud grows from parent → nucleus divides → bud detaches as new cell.",
        "yr": "SS1"
      },
      {
        "q": "Conjugation in Paramecium is a form of:",
        "o": ["Asexual reproduction", "Binary fission", "Sexual reproduction (exchange of genetic material between two individuals)", "Budding"],
        "a": 2,
        "e": "Conjugation in Paramecium is a form of sexual reproduction where two individuals pair up, their micronuclei undergo meiosis, and they exchange micronuclei (genetic material) through a conjugation bridge. They then separate and each divides asexually.",
        "h": "Paramecium conjugation = sexual reproduction (exchange of genetic material between two individuals).",
        "yr": "SS1"
      },
      {
        "q": "The main advantage of sexual reproduction over asexual reproduction is:",
        "o": [
          "It is faster",
          "It requires only one parent",
          "It produces genetic variation in offspring, enabling adaptation to changing environments",
          "Offspring are always healthier"
        ],
        "a": 2,
        "e": "Sexual reproduction combines genetic material from two parents, producing genetically varied offspring. This variation is the raw material for natural selection and evolution, enabling populations to adapt to changing environments. Asexual reproduction produces clones with no variation.",
        "h": "Sexual reproduction → genetic variation → enables adaptation to changing environments.",
        "yr": "SS1"
      },
      {
        "q": "How does Hydra reproduce asexually?",
        "o": ["Binary fission", "Spore formation", "Budding — a bud grows from the parent body and detaches", "Conjugation"],
        "a": 2,
        "e": "Hydra (a freshwater cnidarian) reproduces asexually by budding. A bud forms by repeated mitosis of cells on the parent's body wall. The bud develops a mouth and tentacles, and eventually detaches as a complete new Hydra.",
        "h": "Hydra = budding (bud grows from body wall, develops tentacles, detaches).",
        "yr": "SS1"
      },
      {
        "q": "In Spirogyra (a filamentous alga), sexual reproduction occurs through:",
        "o": [
          "Binary fission",
          "Budding",
          "Conjugation — two filaments align and exchange gametes through conjugation tubes",
          "Spore dispersal only"
        ],
        "a": 2,
        "e": "In Spirogyra conjugation, two filaments align side by side. Conjugation tubes form between corresponding cells. The cell contents (gametes) of one filament move through the tubes into the other filament, where fusion occurs, forming zygospores.",
        "h": "Spirogyra conjugation: filaments align → tubes form → gametes fuse → zygospores.",
        "yr": "SS1"
      },
      {
        "q": "Zygospores formed during Spirogyra conjugation are resistant structures that:",
        "o": [
          "Immediately germinate into new Spirogyra",
          "Survive unfavorable conditions (drought, cold) and germinate when conditions improve",
          "Are the same as regular Spirogyra cells",
          "Only function in asexual reproduction"
        ],
        "a": 1,
        "e": "Zygospores are thick-walled, resistant structures formed by fusion of gametes in Spirogyra. They can survive unfavorable conditions (drought, cold, darkness) for extended periods. When conditions improve, they germinate and develop into new Spirogyra filaments.",
        "h": "Zygospores = resistant structures surviving harsh conditions; germinate when conditions improve.",
        "yr": "SS1"
      },
      {
        "q": "Binary fission in bacteria results in:",
        "o": [
          "Four daughter cells",
          "One daughter cell",
          "Two genetically identical daughter cells",
          "A spore and a vegetative cell"
        ],
        "a": 2,
        "e": "Binary fission in bacteria produces two genetically identical daughter cells from one parent cell. The circular DNA replicates, the cell elongates, and a septum (dividing wall) forms, splitting the cell into two. Under optimal conditions, bacteria can divide every 20 minutes.",
        "h": "Binary fission → 2 identical daughter cells (bacteria double every ~20 min).",
        "yr": "SS1"
      },
      {
        "q": "Why do many unicellular organisms switch to sexual reproduction under unfavorable environmental conditions?",
        "o": [
          "Because asexual reproduction requires too much energy",
          "To produce genetically varied offspring with better chances of surviving new challenges",
          "Because sexual reproduction is always faster",
          "Because they cannot perform binary fission in harsh conditions"
        ],
        "a": 1,
        "e": "Under unfavorable conditions, genetically varied offspring (from sexual reproduction) have a higher chance that some individuals will possess traits suited to the new conditions. Genetic variation is the key advantage — it generates diversity for natural selection.",
        "h": "Sexual reproduction under stress → genetic variation → better survival chances in new conditions.",
        "yr": "SS1"
      },
      {
        "q": "What is a clone in the context of asexual reproduction?",
        "o": [
          "An offspring with traits from two parents",
          "A genetically identical copy of the parent organism",
          "A sexually produced offspring",
          "An organism that reproduces sexually"
        ],
        "a": 1,
        "e": "A clone is a genetically identical copy of the parent organism. All offspring produced by asexual reproduction are clones — they have exactly the same DNA as the parent because no mixing of genetic material from two sources occurs.",
        "h": "Clone = genetically identical copy of parent (product of asexual reproduction).",
        "yr": "SS1"
      },
      {
        "q": "Which organism reproduces by transverse binary fission across the middle?",
        "o": ["Yeast", "Hydra", "Paramecium", "Spirogyra"],
        "a": 2,
        "e": "Paramecium reproduces asexually by transverse binary fission — dividing across its short axis (perpendicular to its length). This produces two smaller daughter paramecia that grow to full size. Compare this to Amoeba, where fission is less precisely oriented.",
        "h": "Paramecium = transverse binary fission (divides across the short axis).",
        "yr": "SS1"
      },
      {
        "q": "Vegetative propagation in plants is a form of:",
        "o": ["Sexual reproduction", "Asexual reproduction", "Fertilization", "Conjugation"],
        "a": 1,
        "e": "Vegetative propagation (e.g., runners in strawberries, tubers in yam, rhizomes in ginger) is asexual reproduction in plants — new individuals are produced from vegetative parts (stem, root, leaf) without the formation or fusion of gametes.",
        "h": "Vegetative propagation = asexual reproduction in plants (no gametes).",
        "yr": "SS1"
      },
      {
        "q": "Hydra can also reproduce sexually. This occurs when:",
        "o": [
          "Conditions are optimal and food is abundant",
          "Environmental conditions become unfavorable (cold, drought, starvation) — gonads develop and gametes are produced",
          "Budding fails",
          "The Hydra is isolated"
        ],
        "a": 1,
        "e": "Under harsh conditions (approaching winter, food scarcity), Hydra develops temporary gonads (testes and ovaries). Sperm are released and fertilize eggs, forming resistant fertilized eggs (zygotes) that can survive unfavorable periods.",
        "h": "Hydra sexual reproduction: unfavorable conditions trigger gonad development → gametes form.",
        "yr": "SS1"
      },
      {
        "q": "The micronucleus in Paramecium is exchanged during conjugation. What is the role of the macronucleus?",
        "o": [
          "It is exchanged during conjugation",
          "It controls everyday cell functions (metabolism, feeding, movement) while the micronucleus is for reproduction",
          "It only stores food",
          "It produces gametes"
        ],
        "a": 1,
        "e": "Paramecium has two types of nuclei: (1) the macronucleus — controls daily cell functions (metabolism, movement, feeding), and (2) the micronucleus — contains the complete genetic information used during conjugation (sexual reproduction).",
        "h": "Macronucleus = controls cell functions; Micronucleus = for sexual reproduction (conjugation).",
        "yr": "SS1"
      },
      {
        "q": "Which of the following is an advantage of asexual reproduction?",
        "o": [
          "It produces genetic variation",
          "Rapid population growth — many offspring quickly from a single parent without a mate",
          "Offspring are always healthier",
          "It always produces more variation"
        ],
        "a": 1,
        "e": "The main advantage of asexual reproduction is rapid population growth — a single organism can produce many offspring quickly without finding a mate. This is advantageous when conditions are good and the existing genotype is well-adapted to the environment.",
        "h": "Asexual advantage: rapid population growth from one parent (no mate needed).",
        "yr": "SS1"
      },
      {
        "q": "Reproduction is essential for the survival of a species because:",
        "o": [
          "Individual organisms live forever with reproduction",
          "It replaces individuals that die, maintaining and increasing population numbers and passing on genetic information",
          "It allows organisms to grow larger",
          "It has no direct benefit to survival"
        ],
        "a": 1,
        "e": "All individuals eventually die. Reproduction ensures the species survives by producing new individuals to replace those that die, maintaining population numbers. It also passes on genetic information (and in sexual reproduction, creates variation for evolution).",
        "h": "Reproduction = ensures species survival by replacing individuals that die + passing on genetic information.",
        "yr": "SS1"
      },
      {
        "q": "An Amoeba under harsh conditions (drought, extreme temperature) may form a:",
        "o": ["Bud", "Zygospore", "Cyst — a dormant, protective form encased in a thick wall", "Conjugation tube"],
        "a": 2,
        "e": "Under adverse conditions, Amoeba forms a cyst — a dormant form enclosed in a thick, resistant wall. The cyst can survive desiccation, extreme temperatures, and lack of food. When conditions improve, the cyst germinates and the Amoeba resumes normal activity.",
        "h": "Amoeba under harsh conditions → forms cyst (dormant, thick-walled, resistant form).",
        "yr": "SS1"
      },
      {
        "q": "Which statement about binary fission is FALSE?",
        "o": [
          "It is an asexual process",
          "It produces genetically identical daughter cells",
          "It involves fusion of two gametes",
          "It is the main reproductive method in bacteria"
        ],
        "a": 2,
        "e": "Binary fission does NOT involve the fusion of gametes — that would be sexual reproduction (fertilization). Binary fission is purely asexual: one cell divides into two identical daughter cells through DNA replication and cell splitting, with no gametes involved.",
        "h": "Binary fission does NOT involve gamete fusion (that's fertilization/sexual reproduction).",
        "yr": "SS1"
      },
      {
        "q": "After conjugation in Paramecium, the two individuals:",
        "o": [
          "Die immediately",
          "Form a permanent pair",
          "Separate and each undergoes several rounds of binary fission",
          "Merge permanently into one large organism"
        ],
        "a": 2,
        "e": "After exchanging micronuclei during conjugation, the two Paramecia separate. Each undergoes nuclear reorganization and then resumes asexual reproduction by binary fission, now with a new genetic combination that provides increased variation.",
        "h": "After conjugation: Paramecia separate → each resumes binary fission with new genetic combination.",
        "yr": "SS1"
      }
    ]
  },
  {
    "topic": "Reproduction in Unicellular Organisms and Invertebrates II",
    "topicCode": "SS1-BIO-24",
    "module": "Reproduction",
    "term": "Third Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  In this second part on reproduction, we examine <span class=\"learn-keyword\">spore formation</span> in fungi and ferns, <span class=\"learn-keyword\">fragmentation</span> and <span class=\"learn-keyword\">regeneration</span>, and reproduction in more complex invertebrates such as earthworms (hermaphroditic) and insects (metamorphosis). We also compare <span class=\"learn-keyword\">complete</span> and <span class=\"learn-keyword\">incomplete metamorphosis</span>.\n</div>\n\n<h3 class=\"learn-subheading\">1. Spore Formation (Sporulation)</h3>\n<ul class=\"learn-list\">\n  <li>Spores are small, light, resistant reproductive structures produced in large numbers</li>\n  <li><strong>Fungi (e.g., Rhizopus, Mucor):</strong> Produce asexual spores in sporangia — released and dispersed by wind; germinate in suitable conditions</li>\n  <li><strong>Ferns:</strong> Produce spores on the underside of fronds (in sori); spores germinate into gametophytes</li>\n  <li><strong>Bacteria:</strong> Form endospores under adverse conditions — not for reproduction, but survival</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Fragmentation and Regeneration</h3>\n<ul class=\"learn-list\">\n  <li><strong>Fragmentation:</strong> An organism breaks into pieces, each developing into a complete new individual</li>\n  <li>Examples: Spirogyra (filament breaks into fragments, each grows into new filament); Planaria (flatworm); starfish</li>\n  <li><strong>Regeneration:</strong> Regrowth of lost body parts — a survival ability, not primarily reproductive</li>\n  <li>Examples: earthworm (limited regeneration), lizard tail regrowth, starfish from a severed arm</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Earthworm Reproduction</h3>\n<ul class=\"learn-list\">\n  <li>Earthworms are <strong>hermaphrodites</strong> — each individual has both male and female reproductive organs</li>\n  <li>They still cross-fertilize (two individuals mate, exchanging sperm)</li>\n  <li>The clitellum secretes a mucus cocoon in which fertilized eggs develop</li>\n  <li>Young worms emerge fully formed (direct development — no larval stage)</li>\n</ul>\n\n<h3 class=\"learn-subheading\">4. Insect Metamorphosis</h3>\n<p class=\"learn-p\">Metamorphosis is the transformation of an insect through distinct developmental stages:</p>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Feature</th><th>Complete Metamorphosis (Holometabolism)</th><th>Incomplete Metamorphosis (Hemimetabolism)</th></tr></thead>\n    <tbody>\n      <tr><td>Stages</td><td>Egg → Larva → Pupa → Adult (4 stages)</td><td>Egg → Nymph → Adult (3 stages)</td></tr>\n      <tr><td>Larva/Nymph</td><td>Larva looks completely different from adult (e.g., caterpillar vs butterfly)</td><td>Nymph resembles small adult (no wings yet)</td></tr>\n      <tr><td>Pupa</td><td>Present (major transformation occurs)</td><td>Absent</td></tr>\n      <tr><td>Examples</td><td>Butterfly, moth, housefly, mosquito, beetle</td><td>Grasshopper, cockroach, locust, dragonfly</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Complete metamorphosis = 4 stages (Egg → Larva → Pupa → Adult). Incomplete = 3 stages (Egg → Nymph → Adult). In complete metamorphosis, larva and adult are morphologically very different and often occupy different ecological niches. Key examples: butterfly (complete); grasshopper (incomplete).\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Spore formation: fungi (in sporangia) and ferns. Fragmentation: Spirogyra, Planaria, starfish (each piece grows into new individual). Earthworms: hermaphrodites, cross-fertilize, clitellum forms cocoon. Complete metamorphosis: 4 stages (egg → larva → pupa → adult) e.g., butterfly. Incomplete metamorphosis: 3 stages (egg → nymph → adult) e.g., grasshopper.\n</div>\n  ",
    "questions": [
      {
        "q": "Spore formation as a method of asexual reproduction is characteristic of:",
        "o": ["Bacteria only", "Fungi (e.g., Rhizopus) and ferns", "Insects", "Earthworms"],
        "a": 1,
        "e": "Spore formation is characteristic of fungi (which produce asexual spores in sporangia) and ferns (which produce spores in sori on frond undersides). Spores are small, light, resistant structures that disperse and germinate to form new organisms.",
        "h": "Spore formation: fungi (in sporangia) and ferns (in sori on fronds).",
        "yr": "SS1"
      },
      {
        "q": "Complete metamorphosis in insects involves which four stages?",
        "o": [
          "Egg → Nymph → Pupa → Adult",
          "Egg → Larva → Pupa → Adult",
          "Nymph → Larva → Pupa → Adult",
          "Egg → Larva → Nymph → Adult"
        ],
        "a": 1,
        "e": "Complete metamorphosis (holometabolism) has four stages: Egg → Larva → Pupa → Adult. The larva (e.g., caterpillar) is morphologically very different from the adult (butterfly). Major reorganization occurs in the pupa (chrysalis) stage.",
        "h": "Complete metamorphosis: Egg → Larva → Pupa → Adult (4 stages).",
        "yr": "SS1"
      },
      {
        "q": "Incomplete metamorphosis differs from complete metamorphosis in that:",
        "o": [
          "It involves more stages",
          "It has no egg stage",
          "It has only three stages (Egg → Nymph → Adult) and no pupa stage",
          "It is found only in butterflies"
        ],
        "a": 2,
        "e": "Incomplete (hemimetabolous) metamorphosis has three stages: Egg → Nymph → Adult. There is no pupal stage. The nymph resembles a miniature adult but lacks fully developed wings and reproductive organs. Examples: grasshopper, cockroach, locust.",
        "h": "Incomplete metamorphosis: 3 stages (Egg → Nymph → Adult); no pupa; nymph resembles adult.",
        "yr": "SS1"
      },
      {
        "q": "Earthworms are hermaphrodites. This means that:",
        "o": [
          "They reproduce asexually only",
          "Each individual possesses both male and female reproductive organs",
          "They reproduce by binary fission",
          "They have no reproductive organs"
        ],
        "a": 1,
        "e": "Hermaphrodites have both male and female reproductive organs in the same individual. However, earthworms still cross-fertilize — two worms mate and exchange sperm, ensuring genetic variation. Self-fertilization is generally avoided.",
        "h": "Earthworm hermaphrodite = both male and female organs in one individual; still cross-fertilizes.",
        "yr": "SS1"
      },
      {
        "q": "The clitellum in earthworms serves to:",
        "o": [
          "Help the earthworm burrow",
          "Digest food",
          "Secrete a mucus cocoon in which fertilized eggs develop",
          "Absorb water from soil"
        ],
        "a": 2,
        "e": "The clitellum is a swollen, glandular band around the earthworm's body. During reproduction, it secretes a mucus ring that slides forward, collecting eggs and sperm. As it slips off, it closes to form a cocoon in which fertilized eggs develop.",
        "h": "Clitellum = secretes mucus cocoon for egg development in earthworms.",
        "yr": "SS1"
      },
      {
        "q": "Fragmentation as a method of reproduction means:",
        "o": [
          "An organism divides into two equal cells",
          "An organism breaks into pieces, each of which can develop into a complete new individual",
          "An organism forms spores",
          "Two organisms exchange genetic material"
        ],
        "a": 1,
        "e": "Fragmentation is a form of asexual reproduction where an organism breaks into fragments, and each fragment grows into a complete new individual. Examples: Spirogyra (algal filaments break), Planaria (flatworm pieces regenerate), and starfish (arm fragments).",
        "h": "Fragmentation: organism breaks into pieces → each grows into new individual.",
        "yr": "SS1"
      },
      {
        "q": "Which insect undergoes complete metamorphosis?",
        "o": ["Grasshopper", "Cockroach", "Locust", "Butterfly"],
        "a": 3,
        "e": "Butterflies undergo complete metamorphosis: Egg → Larva (caterpillar) → Pupa (chrysalis) → Adult. The caterpillar and butterfly are morphologically completely different — the caterpillar feeds and grows; the pupa transforms; the adult reproduces.",
        "h": "Butterfly = complete metamorphosis (egg → caterpillar → chrysalis → adult).",
        "yr": "SS1"
      },
      {
        "q": "A nymph in incomplete metamorphosis differs from a larva in complete metamorphosis because a nymph:",
        "o": [
          "Looks completely different from the adult",
          "Has a pupal stage",
          "Resembles a small, wingless version of the adult",
          "Cannot eat"
        ],
        "a": 2,
        "e": "A nymph (e.g., young grasshopper) resembles the adult in body form but is smaller and lacks fully developed wings and reproductive organs. A larva (e.g., caterpillar) looks completely different from the adult butterfly.",
        "h": "Nymph = resembles small adult (no wings yet); larva = completely different from adult.",
        "yr": "SS1"
      },
      {
        "q": "In fungi like Rhizopus (bread mould), spores are produced in structures called:",
        "o": ["Hyphae", "Mycelium", "Sporangia (spore cases)", "Cysts"],
        "a": 2,
        "e": "Rhizopus and other fungi produce asexual spores in sporangia — round, dark structures at the tips of upright hyphae called sporangiophores. Sporangia burst when mature, releasing thousands of spores into the air for dispersal.",
        "h": "Rhizopus spores produced in sporangia (spore cases) at tips of sporangiophores.",
        "yr": "SS1"
      },
      {
        "q": "The pupal stage in complete metamorphosis is important because:",
        "o": [
          "The pupa feeds intensively",
          "Major reorganization of larval tissues into adult structures occurs within the pupa",
          "The pupa mates and produces eggs",
          "The pupa disperses to new habitats"
        ],
        "a": 1,
        "e": "During the pupal stage, the larval body is almost completely broken down and reconstructed into the adult form. Larval tissues dissolve and reorganize into adult structures (wings, compound eyes, reproductive organs) through a process controlled by hormones.",
        "h": "Pupa stage = complete tissue reorganization from larval to adult form.",
        "yr": "SS1"
      },
      {
        "q": "Regeneration in starfish involves:",
        "o": [
          "Producing spores",
          "The ability to regrow a complete new organism from a severed arm (if it contains part of the central disc)",
          "Binary fission",
          "Budding only"
        ],
        "a": 1,
        "e": "Starfish have remarkable regenerative ability — a severed arm (containing part of the central disc) can regenerate into a complete new starfish. This can function as a form of asexual reproduction. Some species deliberately shed arms (autotomy).",
        "h": "Starfish: severed arm (with part of disc) regenerates into complete new individual.",
        "yr": "SS1"
      },
      {
        "q": "Which of the following insects undergoes incomplete metamorphosis?",
        "o": ["Housefly", "Mosquito", "Beetle", "Grasshopper"],
        "a": 3,
        "e": "Grasshopper undergoes incomplete metamorphosis: Egg → Nymph → Adult. Housefly, mosquito, and beetle all undergo complete metamorphosis (4 stages including pupa).",
        "h": "Grasshopper = incomplete metamorphosis; housefly, mosquito, beetle = complete metamorphosis.",
        "yr": "SS1"
      },
      {
        "q": "Why are fungal spores produced in very large numbers?",
        "o": [
          "To provide food for animals",
          "Because most spores fail to land in suitable conditions — large numbers compensate for high failure rate",
          "To create genetic variation",
          "They are produced in small numbers"
        ],
        "a": 1,
        "e": "Spores are dispersed randomly by wind, water, or animals. The vast majority land in unsuitable conditions and fail to germinate. Producing millions of spores compensates for this high 'wastage' rate, ensuring at least some land in suitable conditions.",
        "h": "Many spores produced because most fail to land in suitable conditions (high wastage).",
        "yr": "SS1"
      },
      {
        "q": "Planaria (flatworm) reproduces by fragmentation. If it is cut into three pieces, how many new organisms can potentially develop?",
        "o": ["One", "Two", "Three", "Six"],
        "a": 2,
        "e": "Planaria can regenerate from fragments — each piece has totipotent cells (neoblasts) capable of regenerating the entire body. If cut into three pieces, each piece can develop into a complete new Planaria, producing three new organisms.",
        "h": "Planaria fragmentation: each piece regenerates → 3 pieces = 3 new organisms.",
        "yr": "SS1"
      },
      {
        "q": "The life cycle of a mosquito demonstrates complete metamorphosis. The correct order is:",
        "o": [
          "Egg → Nymph → Adult",
          "Egg → Pupa → Larva → Adult",
          "Egg → Larva (wriggler) → Pupa (tumbler) → Adult",
          "Larva → Egg → Pupa → Adult"
        ],
        "a": 2,
        "e": "Mosquito complete metamorphosis: Egg (laid on water surface) → Larva (wriggler, aquatic) → Pupa (tumbler, aquatic, non-feeding) → Adult (winged, terrestrial). Larvae and pupae are aquatic; adults are terrestrial/aerial.",
        "h": "Mosquito: Egg → Larva (wriggler) → Pupa (tumbler) → Adult (all aquatic except adult).",
        "yr": "SS1"
      },
      {
        "q": "An advantage of producing spores with thick, resistant walls is that they:",
        "o": [
          "Germinate immediately upon release",
          "Can survive unfavorable conditions (drought, heat, cold) until conditions improve",
          "Provide food for the fungus",
          "Always develop into sexual organisms"
        ],
        "a": 1,
        "e": "Spores with thick, resistant walls can remain dormant for extended periods, surviving drought, extreme temperatures, UV radiation, and lack of nutrients. When conditions become favorable, the spores germinate and develop into new organisms.",
        "h": "Thick-walled spores: survive harsh conditions (dormancy) → germinate when conditions improve.",
        "yr": "SS1"
      },
      {
        "q": "Cross-fertilization in earthworms (rather than self-fertilization) is beneficial because:",
        "o": [
          "It is faster than self-fertilization",
          "It requires no energy",
          "It ensures genetic variation in offspring — beneficial for adaptation and survival",
          "It produces more eggs"
        ],
        "a": 2,
        "e": "Although earthworms are hermaphrodites, cross-fertilization (exchanging sperm with another individual) produces genetically varied offspring. Genetic variation is advantageous because it gives populations a better chance of adapting to environmental changes.",
        "h": "Earthworm cross-fertilization → genetic variation → better adaptability than self-fertilization.",
        "yr": "SS1"
      },
      {
        "q": "In ferns, where are spores produced?",
        "o": [
          "In the roots",
          "In the sporangia grouped in sori on the underside of fronds",
          "In flowers",
          "In seeds"
        ],
        "a": 1,
        "e": "Fern spores are produced in sporangia — tiny capsules grouped in clusters called sori (singular: sorus) on the underside of fronds (leaves). When mature, sporangia open and release spores into the air for dispersal.",
        "h": "Fern spores: in sporangia, grouped in sori on underside of fronds.",
        "yr": "SS1"
      },
      {
        "q": "What is the main difference between regeneration and fragmentation?",
        "o": [
          "They are the same process",
          "Regeneration is the regrowth of lost body parts; fragmentation is when pieces break off and each grows into a complete new organism",
          "Fragmentation only occurs in bacteria",
          "Regeneration always produces genetic variation"
        ],
        "a": 1,
        "e": "Regeneration is the regrowth of lost or damaged body parts (e.g., lizard regrowing its tail) — it is primarily a survival/repair mechanism, not reproduction. Fragmentation is when an organism breaks into pieces, each becoming a complete new organism — it IS a form of asexual reproduction.",
        "h": "Regeneration = regrow lost parts (repair); Fragmentation = pieces become new organisms (reproduction).",
        "yr": "SS1"
      },
      {
        "q": "Young earthworms emerge from cocoons as:",
        "o": [
          "Larvae that undergo metamorphosis",
          "Pupae",
          "Fully formed miniature worms (direct development — no larval stage)",
          "Spores"
        ],
        "a": 2,
        "e": "Earthworms undergo direct development — young worms emerge from cocoons as fully formed miniature versions of the adult. There is no larval stage or metamorphosis. This is typical of many terrestrial invertebrates that protect their eggs in specialized structures.",
        "h": "Earthworm: direct development (fully formed young emerge from cocoon — no larval stage).",
        "yr": "SS1"
      }
    ]
  }
];
